//src/components/ChatInterface.jsx
// DIS attachment-only completion + owner-wide authoritative catalog/progress + complete document chat routing + hardened File Vault early DI handoff + streaming backend TTS + billing-aware frontend messaging + paginated history navigation: 2026.09.13-v24-history-nav-pagination
// - ChatInterface gates the reusable DI dock from the authenticated backend catalog
// - newly sent documents render one backend-driven in-thread progress state
// - interrupted/reloaded DI processing reconnects to the authoritative /status record
// - File Vault handoff starts /commit from the backend-issued session immediately; React session synchronization no longer gates processing
// - pre-commit handoff recovery and committed/processing /status recovery remain separate so refresh survival is preserved
// - Document Intelligence attachments never render raw inline image previews in Chat; the top transcript strip is display-only while the dedicated DI View File remains authoritative
// - backend progress percentages remain authoritative while customer-facing stage copy is translated safely
// - legacy attachment View File behavior remains unchanged while DI top transcript strips omit the duplicate View File action; print output still uses a plain filename reference
// - legacy Chat attachment "View File" remains on its existing original-file path until that surface is migrated
// - Document Intelligence "View File" opens the ORIGINAL owner-authorized file through the shared File Access Gateway
// - gateway-backed PDF viewing uses Firebase Bearer headers; non-PDF/Office embeds receive a short-lived opaque AskLawly browser ticket
// - ChatInterface owns one stable turnId per logical Send and reuses it across persistence, DI ingestion, Stream, Nonstream fallback, and Lexa persistence
// - selected DI document carries documentId + mode + actionId + page scope identically to Stream and Nonstream
// - persistent document mode supports Auto / Always Use / Off; explicit action/page focus forces only that turn
// - user transcript preserves compact context for Lexa follow-up replies and Document Intelligence page/action/document scope
// - Ask This Document is composer-contained and becomes a compact About this document reference on the sent user turn
// - Lexa speaker sends a dominant-language hint to /speak, streams 24 kHz PCM through a buffered Web Audio queue, and uses cleaned browser speech only as fallback
import React, { useState, useEffect, useRef, useContext, useLayoutEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FaVolumeUp, FaCopy, FaEdit } from "react-icons/fa";
import {
  FiX,
  FiMessageCircle,
  FiFileText,
  FiShield,
  FiEye,
  FiChevronRight,
  FiChevronUp,
  FiChevronDown,
} from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import ChatInput from "./ChatInput";
import AnswerRenderer from "./answer/AnswerRenderer";
import FileViewerModal from "./FileViewerModal";
import DocumentIntelligence from "./DocumentIntelligence";
import { continueFastChatDocumentIntelligence } from "./AttachmentController";
import { useQuota } from "../context/QuotaContext";

// 🔧 TEMP: disable streaming path from the client
const DISABLE_STREAM = false; // set to false to re-enable streaming

// ✅ Single source of truth for the content column width on desktop/laptop
const CONTENT_MAX = 800; // px — match ChatInput visual width

const ACTIVE_CHAT_STORAGE_KEY = "active_chat";

// ✅ MUST match Sidebar.jsx
const SUPPRESS_RESTORE_KEY = "suppress_chat_restore_until";

// ✅ Extra breathing room so the dock never touches native bottom UI (mobile/tablet only)
const SAFE_BREATHING_PX = 12;

// ✅ UI guards (ONLY these two changes requested)
const EMPTY_STATE_SUPPRESS_MS = 12000; // 12s
const THINKING_FLASH_SUPPRESS_MS = 2000; // 2s

// ✅ Desktop-only: keep chat scrollbar visible + ensure it doesn't sit under the browser scrollbar
const DESKTOP_CHAT_SCROLLBAR_INSET_PX = 14; // fallback initial inset (dynamic calc will override)

// ✅ Top spacing tuning (controls the vertical gap below the app top bar)
const TOP_BAR_EXTRA_NARROW_PX = 0; // was 6 — tighter on mobile/tablet
const TOP_BAR_EXTRA_WIDE_PX = 12; // was 20 — tighter on desktop/laptop

// ✅ Chat history pagination/navigation
// Backend returns at most 40 stored message documents per page. The first page
// is the newest 40; the ↑ control walks backward through older pages.
const CHAT_HISTORY_PAGE_SIZE = 40;

// ↓ is frontend-only. Show it only after the user has moved a meaningful
// distance away from the newest message.
const CHAT_JUMP_TO_LATEST_DISTANCE_PX = 240;


// -----------------------------------------------------------------------------
// LEXA READ-ALOUD — STREAMING PCM
// -----------------------------------------------------------------------------
// Primary:
//   authenticated POST /speak
//   -> backend dominant-language/code-switch guard
//   -> Google Chirp 3 HD / Erinome
//   -> streamed raw PCM
//   -> Web Audio playback as bytes arrive
//
// Fallback:
//   browser speechSynthesis only when backend/provider/stream/playback fails.
//
// Frontend responsibility:
// - Determine only the DOMINANT language hint for the current Lexa answer.
// - Do not attempt final mixed-language segmentation here.
// - Browser fallback receives aggressively cleaned text so Markdown/punctuation
//   such as ###, **, bullets, raw URLs, "comma", "stop", etc. are not spoken.

const TTS_ROUTE = "/speak";
const TTS_DEFAULT_LANGUAGE = "en-US";

// Must match tts_handler.py.
const TTS_PCM_SAMPLE_RATE_HZ = 24000;
const TTS_PCM_CHANNELS = 1;
const TTS_PCM_SAMPLE_WIDTH_BYTES = 2;

// Backend prefetches the first Google audio chunk before HTTP 200.
// Immediate HTTP/network failures fall back immediately. An unresolved/cold
// request gets enough room to start without making the user wait indefinitely.
const TTS_FIRST_AUDIO_TIMEOUT_MS = 18000;

// Once streaming has begun, abort only if the byte stream itself stops making
// progress for an unreasonable period.
const TTS_STREAM_STALL_TIMEOUT_MS = 20000;

// Streaming playback smoothing.
//
// We deliberately keep only a small rolling reserve. This remains dramatically
// faster than waiting for a complete MP3, while reducing underruns/clicks caused
// by arbitrary HTTP chunk timing.
//
// Startup reserve:
//   220 ms before first Web Audio source is scheduled.
//
// Steady block size:
//   120 ms chunks. Network reads are arbitrary; Web Audio should receive stable
//   PCM-sized blocks instead of one AudioBufferSource per fetch chunk.
//
// Scheduling lead:
//   keep at least ~80 ms queued ahead of context.currentTime.
const TTS_PCM_START_BUFFER_MS = 220;
const TTS_PCM_PLAYBACK_BLOCK_MS = 120;
const TTS_PCM_MIN_SCHEDULE_LEAD_MS = 80;

const TTS_PCM_BYTES_PER_MS =
  (TTS_PCM_SAMPLE_RATE_HZ *
    TTS_PCM_CHANNELS *
    TTS_PCM_SAMPLE_WIDTH_BYTES) /
  1000;

const TTS_PCM_START_BUFFER_BYTES = Math.max(
  TTS_PCM_SAMPLE_WIDTH_BYTES,
  Math.floor(
    TTS_PCM_BYTES_PER_MS *
      TTS_PCM_START_BUFFER_MS
  )
);

const TTS_PCM_PLAYBACK_BLOCK_BYTES = Math.max(
  TTS_PCM_SAMPLE_WIDTH_BYTES,
  Math.floor(
    TTS_PCM_BYTES_PER_MS *
      TTS_PCM_PLAYBACK_BLOCK_MS
  )
);

const TTS_LANGUAGE_DEFAULTS = {
  af: "af-ZA",
  ar: "ar-XA",
  bg: "bg-BG",
  bn: "bn-IN",
  ca: "ca-ES",
  cs: "cs-CZ",
  da: "da-DK",
  de: "de-DE",
  el: "el-GR",
  en: "en-US",
  es: "es-US",
  et: "et-EE",
  fi: "fi-FI",
  fr: "fr-FR",
  gu: "gu-IN",
  he: "he-IL",
  hi: "hi-IN",
  hr: "hr-HR",
  hu: "hu-HU",
  id: "id-ID",
  it: "it-IT",
  ja: "ja-JP",
  kn: "kn-IN",
  ko: "ko-KR",
  lt: "lt-LT",
  lv: "lv-LV",
  ml: "ml-IN",
  mr: "mr-IN",
  ms: "ms-MY",
  nb: "nb-NO",
  nl: "nl-NL",
  no: "nb-NO",
  pa: "pa-IN",
  pl: "pl-PL",
  pt: "pt-BR",
  ro: "ro-RO",
  ru: "ru-RU",
  sk: "sk-SK",
  sl: "sl-SI",
  sr: "sr-RS",
  sv: "sv-SE",
  sw: "sw-KE",
  ta: "ta-IN",
  te: "te-IN",
  th: "th-TH",
  tr: "tr-TR",
  uk: "uk-UA",
  ur: "ur-IN",
  vi: "vi-VN",
  yue: "yue-HK",
  zh: "cmn-CN",
  cmn: "cmn-CN",
};

const TTS_LOCALE_ALIASES = {
  "bn-BD": "bn-IN",
  "ar-001": "ar-XA",
  "zh-CN": "cmn-CN",
  "zh-SG": "cmn-CN",
  "zh-Hans": "cmn-CN",
  "no-NO": "nb-NO",
};

// Browser SpeechSynthesisVoice has no standardized gender property. These names
// are only a best-effort emergency preference. Production Google TTS remains
// strictly Erinome/female.
const FEMALE_BROWSER_VOICE_RE =
  /\b(?:female|samantha|ava|zoe|allison|victoria|karen|moira|tessa|veena|fiona|zira|susan|hazel|helena|aria|jenny|sonia|natasha|monica|paulina|amelie|audrey)\b/i;

function normalizeTtsLocale(value) {
  const raw = String(value || "").trim().replace(/_/g, "-");
  if (!raw) return "";

  const parts = raw.split("-").filter(Boolean);
  if (!parts.length) return "";

  const base = parts[0].toLowerCase();

  if (parts.length === 1) {
    return TTS_LANGUAGE_DEFAULTS[base] || "";
  }

  const normalized = [base];

  for (const part of parts.slice(1)) {
    if (/^[A-Za-z]{4}$/.test(part)) {
      normalized.push(
        part[0].toUpperCase() +
          part.slice(1).toLowerCase()
      );
    } else if (
      /^[A-Za-z]{2,3}$/.test(part) ||
      /^\d{3}$/.test(part)
    ) {
      normalized.push(part.toUpperCase());
    } else {
      normalized.push(part);
    }
  }

  const locale = normalized.join("-");
  return TTS_LOCALE_ALIASES[locale] || locale;
}

function countTtsMatches(value, pattern) {
  try {
    return (String(value || "").match(pattern) || []).length;
  } catch {
    return 0;
  }
}

function scoreLatinSpeechLanguage(
  text,
  words,
  accentPattern = null
) {
  const source = ` ${String(text || "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")} `;

  let score = 0;

  for (const word of words || []) {
    const escaped = String(word).replace(
      /[.*+?^${}()|[\]\\]/g,
      "\\$&"
    );

    if (
      new RegExp(
        `(?:^|\\s)${escaped}(?:\\s|$)`,
        "i"
      ).test(source)
    ) {
      score += 1;
    }
  }

  if (
    accentPattern &&
    accentPattern.test(String(text || ""))
  ) {
    score += 2;
  }

  return score;
}

function inferTtsDominantLocale(
  text,
  explicitLanguage = "",
  uiLanguage = ""
) {
  const explicit =
    normalizeTtsLocale(explicitLanguage);

  // Message metadata, when present, is the strongest frontend hint. The backend
  // still performs the final code-switch guard and can correct mixed segments.
  if (explicit) return explicit;

  const source = String(text || "");

  const uiLocale =
    normalizeTtsLocale(uiLanguage);
  const uiBase =
    String(uiLocale || "")
      .split("-", 1)[0]
      .toLowerCase();

  const latinCount = countTtsMatches(
    source,
    /[A-Za-zÀ-ÖØ-öø-ÿŒœÆæ]/g
  );

  const hanCount = countTtsMatches(
    source,
    /[\u3400-\u4DBF\u4E00-\u9FFF]/g
  );
  const kanaCount = countTtsMatches(
    source,
    /[\u3040-\u30FF]/g
  );

  const scriptCandidates = [
    {
      locale: "bn-IN",
      count: countTtsMatches(
        source,
        /[\u0980-\u09FF]/g
      ),
    },
    {
      locale: "gu-IN",
      count: countTtsMatches(
        source,
        /[\u0A80-\u0AFF]/g
      ),
    },
    {
      locale: "pa-IN",
      count: countTtsMatches(
        source,
        /[\u0A00-\u0A7F]/g
      ),
    },
    {
      locale: "ta-IN",
      count: countTtsMatches(
        source,
        /[\u0B80-\u0BFF]/g
      ),
    },
    {
      locale: "te-IN",
      count: countTtsMatches(
        source,
        /[\u0C00-\u0C7F]/g
      ),
    },
    {
      locale: "kn-IN",
      count: countTtsMatches(
        source,
        /[\u0C80-\u0CFF]/g
      ),
    },
    {
      locale: "ml-IN",
      count: countTtsMatches(
        source,
        /[\u0D00-\u0D7F]/g
      ),
    },
    {
      locale: "th-TH",
      count: countTtsMatches(
        source,
        /[\u0E00-\u0E7F]/g
      ),
    },
    {
      locale: "he-IL",
      count: countTtsMatches(
        source,
        /[\u0590-\u05FF]/g
      ),
    },
    {
      locale: "el-GR",
      count: countTtsMatches(
        source,
        /[\u0370-\u03FF]/g
      ),
    },
    {
      locale: "ko-KR",
      count: countTtsMatches(
        source,
        /[\uAC00-\uD7AF]/g
      ),
    },
    {
      locale:
        uiBase === "ur" ||
        /[ٹڈڑںھہےۓ]/u.test(source)
          ? "ur-IN"
          : "ar-XA",
      count: countTtsMatches(
        source,
        /[\u0600-\u06FF\u0750-\u077F]/g
      ),
    },
    {
      locale:
        uiBase === "mr" ||
        /[ळऴ]/u.test(source)
          ? "mr-IN"
          : "hi-IN",
      count: countTtsMatches(
        source,
        /[\u0900-\u097F]/g
      ),
    },
    {
      locale:
        /[іїєґ]/iu.test(source)
          ? "uk-UA"
          : /[љњђћџ]/iu.test(source)
          ? "sr-RS"
          : "ru-RU",
      count: countTtsMatches(
        source,
        /[\u0400-\u04FF]/g
      ),
    },
    {
      locale: "ja-JP",
      count:
        kanaCount > 0
          ? kanaCount + hanCount
          : 0,
    },
    {
      locale:
        uiBase === "yue"
          ? "yue-HK"
          : "cmn-CN",
      count:
        kanaCount === 0
          ? hanCount
          : 0,
    },
  ].sort((a, b) => b.count - a.count);

  const strongestNonLatin =
    scriptCandidates[0];

  // This is a dominant-language hint, not "any matching character wins".
  // A mostly-English answer containing one Bengali word therefore remains an
  // English/Latin hint; a mostly-Bengali answer with a few English terms is bn-IN.
  if (
    strongestNonLatin?.count > 0 &&
    strongestNonLatin.count > latinCount
  ) {
    return strongestNonLatin.locale;
  }

  // Latin-language dominant inference for common AskLawly languages.
  const latinCandidates = [
    {
      locale: "es-US",
      score: scoreLatinSpeechLanguage(
        source,
        [
          "que",
          "para",
          "con",
          "una",
          "como",
          "pero",
          "porque",
          "también",
          "usted",
          "esto",
          "esta",
        ],
        /[¿¡ñ]/i
      ),
    },
    {
      locale: "fr-FR",
      score: scoreLatinSpeechLanguage(
        source,
        [
          "avec",
          "pour",
          "dans",
          "mais",
          "vous",
          "nous",
          "cette",
          "être",
          "peut",
          "également",
        ],
        /[àâçéèêëîïôûùüÿœ]/i
      ),
    },
    {
      locale: "de-DE",
      score: scoreLatinSpeechLanguage(
        source,
        [
          "und",
          "der",
          "die",
          "das",
          "mit",
          "für",
          "nicht",
          "auch",
          "eine",
          "werden",
        ],
        /[äöüß]/i
      ),
    },
    {
      locale: "pt-BR",
      score: scoreLatinSpeechLanguage(
        source,
        [
          "que",
          "para",
          "com",
          "uma",
          "não",
          "também",
          "você",
          "isso",
          "esta",
          "como",
        ],
        /[ãõçáéíóúâêô]/i
      ),
    },
    {
      locale: "it-IT",
      score: scoreLatinSpeechLanguage(
        source,
        [
          "che",
          "per",
          "con",
          "una",
          "non",
          "anche",
          "questo",
          "questa",
          "come",
          "sono",
        ],
        /[àèéìòù]/i
      ),
    },
  ].sort((a, b) => b.score - a.score);

  if (latinCandidates[0]?.score >= 3) {
    return latinCandidates[0].locale;
  }

  // Latin is dominant but no stronger alternate signal was found.
  if (latinCount > 0) {
    if (
      uiLocale &&
      ["en", "es", "fr", "de", "pt", "it"].includes(
        uiBase
      )
    ) {
      return uiLocale;
    }

    return "en-US";
  }

  if (strongestNonLatin?.count > 0) {
    return strongestNonLatin.locale;
  }

  return uiLocale || TTS_DEFAULT_LANGUAGE;
}

function browserQuoteMarkers(languageCode) {
  const base = String(languageCode || "")
    .split("-", 1)[0]
    .toLowerCase();

  const markers = {
    en: ["Quote", "End quote"],
    es: ["Cita", "Fin de la cita"],
    fr: ["Citation", "Fin de la citation"],
    bn: ["উদ্ধৃতি", "উদ্ধৃতি শেষ"],
    hi: ["उद्धरण", "उद्धरण समाप्त"],
    ar: ["اقتباس", "نهاية الاقتباس"],
    ur: ["اقتباس", "اقتباس ختم"],
    de: ["Zitat", "Ende des Zitats"],
    pt: ["Citação", "Fim da citação"],
    it: ["Citazione", "Fine della citazione"],
    ja: ["引用", "引用終わり"],
    ko: ["인용", "인용 끝"],
    ru: ["Цитата", "Конец цитаты"],
  };

  return markers[base] || ['"', '"'];
}

function stripBrowserLiteralPunctuation(value) {
  let out = String(value || "");

  // Keep decimal points such as 12.5 while removing punctuation that some
  // browser voices literally announce as "period", "stop", "comma", etc.
  const decimalToken =
    "__ASKLAWLY_DECIMAL_POINT__";

  out = out.replace(
    /(\d)\.(\d)/g,
    `$1${decimalToken}$2`
  );

  out = out
    .replace(/[!?;:]+/g, "\n")
    .replace(/,+/g, " ")
    .replace(/\.+/g, "\n");

  out = out.replace(
    new RegExp(decimalToken, "g"),
    "."
  );

  out = out
    .replace(
      /[#*_`~|\\/<>{}\[\]()]+/g,
      " "
    )
    .replace(/[–—]+/g, " ")
    .replace(/\s+-\s+/g, " ")
    .replace(/(^|\s)-(?=\s|$)/g, " ")
    .replace(/&/g, " ");

  // Ordinary quotation marks remain because quotation can carry meaning.
  return out;
}

function prepareBrowserSpeechText(
  text,
  languageCode = TTS_DEFAULT_LANGUAGE
) {
  let value = String(text || "")
    .replace(/\r\n?/g, "\n")
    .trim();

  if (!value) return "";

  value = value.replace(
    /<!--[\s\S]*?-->/g,
    ""
  );

  value = value.replace(
    /(?:cite|filecite|url|entity|product|products|navlist|image_group|video|genui).*?/g,
    ""
  );

  // Fenced code is visual content, not read-aloud prose.
  value = value.replace(
    /```[\s\S]*?```|~~~[\s\S]*?~~~/g,
    "\n"
  );

  // Final Sources/References/Citations/Follow-up sections are not spoken.
  const sectionLines = value.split("\n");
  let cutIndex = -1;

  for (
    let index = sectionLines.length - 1;
    index >= 0;
    index -= 1
  ) {
    if (
      /^\s{0,3}(?:#{1,6}\s*)?(?:sources?|references?|citations?|follow[- ]?up(?:\s+questions?)?)\s*:?\s*$/i.test(
        sectionLines[index] || ""
      )
    ) {
      cutIndex = index;
      break;
    }
  }

  if (cutIndex >= 0) {
    value = sectionLines
      .slice(0, cutIndex)
      .join("\n");
  }

  // Keep link/image labels, never raw URLs.
  value = value.replace(
    /!\[([^\]]*)\]\((?:[^()]|\([^)]*\))*\)/g,
    "$1"
  );
  value = value.replace(
    /\[([^\]]+)\]\((?:[^()]|\([^)]*\))*\)/g,
    "$1"
  );
  value = value.replace(
    /https?:\/\/[^\s<>()]+/gi,
    ""
  );
  value = value.replace(
    /\[\^[^\]]+\]/g,
    ""
  );
  value = value.replace(
    /\[(?:\d+(?:\s*[-,]\s*\d+)*)\]/g,
    ""
  );

  const [quoteStart, quoteEnd] =
    browserQuoteMarkers(languageCode);

  const output = [];
  let inBlockQuote = false;

  const closeBlockQuote = () => {
    if (inBlockQuote) {
      output.push(quoteEnd);
      inBlockQuote = false;
    }
  };

  for (const rawLine of value.split("\n")) {
    const line = String(rawLine || "")
      .trimEnd();

    if (
      /^\s*\|?\s*:?-{3,}:?\s*(?:\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(
        line
      )
    ) {
      continue;
    }

    if (
      /^\s*(?:[-*_]\s*){3,}$/.test(line)
    ) {
      closeBlockQuote();
      continue;
    }

    const quoteMatch = line.match(
      /^\s{0,3}>\s?(.*)$/
    );

    if (quoteMatch) {
      if (!inBlockQuote) {
        output.push(quoteStart);
        inBlockQuote = true;
      }

      const quoteText = String(
        quoteMatch[1] || ""
      ).trim();

      if (quoteText) {
        output.push(quoteText);
      }
      continue;
    }

    closeBlockQuote();

    const heading = line.match(
      /^\s{0,3}#{1,6}\s+(.+?)\s*$/
    );

    if (heading) {
      output.push(heading[1].trim());
      output.push("");
      continue;
    }

    const unordered = line.match(
      /^\s*[-+*•]\s+(.*)$/
    );

    if (unordered) {
      const item = String(
        unordered[1] || ""
      )
        .replace(
          /^\[(?: |x|X)\]\s*/,
          ""
        )
        .trim();

      if (item) output.push(item);
      continue;
    }

    const ordered = line.match(
      /^\s*(\d+)[.)]\s+(.*)$/
    );

    if (ordered) {
      output.push(
        `${ordered[1]} ${ordered[2].trim()}`
      );
      continue;
    }

    if (line.includes("|")) {
      const cells = line
        .trim()
        .replace(/^\||\|$/g, "")
        .split("|")
        .map((cell) => cell.trim())
        .filter(Boolean);

      if (cells.length >= 2) {
        output.push(cells.join(" "));
        continue;
      }
    }

    output.push(line.trim());
  }

  closeBlockQuote();
  value = output.join("\n");

  value = value.replace(
    /`([^`\n]+)`/g,
    "$1"
  );
  value = value.replace(
    /\*\*(.+?)\*\*/g,
    "$1"
  );
  value = value.replace(
    /__(.+?)__/g,
    "$1"
  );
  value = value.replace(
    /~~(.+?)~~/g,
    "$1"
  );
  value = value.replace(
    /(?<!\*)\*([^*\n]+)\*(?!\*)/g,
    "$1"
  );
  value = value.replace(
    /(?<!_)_([^_\n]+)_(?!_)/g,
    "$1"
  );
  value = value.replace(
    /<[^>]+>/g,
    " "
  );

  // Never let the browser say pound/hash/asterisk presentation syntax.
  value = value.replace(
    /#\s*(\d+)/g,
    "$1"
  );
  value = value.replace(
    /#+/g,
    " "
  );

  value =
    stripBrowserLiteralPunctuation(value);

  value = value
    .replace(/[ \t]+/g, " ")
    .replace(
      /[ \t]*\n[ \t]*/g,
      "\n"
    )
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  return value;
}

function getTtsBackendUrl() {
  const base = String(
    process.env.REACT_APP_BACKEND_URL || ""
  )
    .trim()
    .replace(/\/+$/, "");

  return base
    ? `${base}${TTS_ROUTE}`
    : "";
}

function concatUint8Arrays(left, right) {
  const a =
    left instanceof Uint8Array
      ? left
      : new Uint8Array(0);

  const b =
    right instanceof Uint8Array
      ? right
      : new Uint8Array(0);

  if (!a.length) return b.slice();
  if (!b.length) return a.slice();

  const joined = new Uint8Array(
    a.length + b.length
  );

  joined.set(a, 0);
  joined.set(b, a.length);

  return joined;
}

function pcm16LeBytesToFloat32(bytes) {
  const source =
    bytes instanceof Uint8Array
      ? bytes
      : new Uint8Array(0);

  const evenLength =
    source.length -
    (source.length %
      TTS_PCM_SAMPLE_WIDTH_BYTES);

  if (evenLength <= 0) {
    return new Float32Array(0);
  }

  const view = new DataView(
    source.buffer,
    source.byteOffset,
    evenLength
  );

  const samples = new Float32Array(
    evenLength /
      TTS_PCM_SAMPLE_WIDTH_BYTES
  );

  for (
    let offset = 0, sampleIndex = 0;
    offset < evenLength;
    offset += 2, sampleIndex += 1
  ) {
    const sample = view.getInt16(
      offset,
      true
    );

    samples[sampleIndex] =
      sample < 0
        ? sample / 32768
        : sample / 32767;
  }

  return samples;
}

// ✅ Composer response mode. Auto remains the safe/default contract for old callers.
const normalizeChatMode = (value) => (String(value || "").trim().toLowerCase() === "research" ? "research" : "auto");

// ✅ Document Intelligence chat-use mode. The UI displays "Always Use" while
// transport/backend policy uses the compact internal value "force".
const normalizeDocumentChatMode = (value) => {
  const mode = String(value || "auto")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  if (["off", "disabled", "disable", "none", "never"].includes(mode)) {
    return "off";
  }

  return ["force", "forced", "always", "always_use", "use_always"].includes(mode)
    ? "force"
    : "auto";
};

const MAX_DOCUMENT_PAGE_SCOPE_REFERENCES = 25;

const normalizeDocumentActionId = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  return /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/.test(raw) ? raw : "";
};

const toPositiveDocumentPageNumber = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  const page = Math.floor(parsed);
  return page > 0 ? page : null;
};

const normalizeDocumentPageScope = (value, pageCount = null) => {
  if (!value || typeof value !== "object") return null;

  const listedPages = Array.isArray(value.pageNumbers)
    ? value.pageNumbers
    : Array.isArray(value.page_numbers)
    ? value.page_numbers
    : Array.isArray(value.pages)
    ? value.pages
    : [];

  let pageNumbers = Array.from(
    new Set(
      listedPages
        .map(toPositiveDocumentPageNumber)
        .filter(Boolean)
    )
  ).sort((a, b) => a - b);

  let startPage =
    toPositiveDocumentPageNumber(value.startPage) ||
    toPositiveDocumentPageNumber(value.start_page) ||
    toPositiveDocumentPageNumber(value.pageStart) ||
    toPositiveDocumentPageNumber(value.page_start) ||
    toPositiveDocumentPageNumber(value.page);

  let endPage =
    toPositiveDocumentPageNumber(value.endPage) ||
    toPositiveDocumentPageNumber(value.end_page) ||
    toPositiveDocumentPageNumber(value.pageEnd) ||
    toPositiveDocumentPageNumber(value.page_end);

  if (!pageNumbers.length && startPage) {
    const low = Math.min(startPage, endPage || startPage);
    const high = Math.max(startPage, endPage || startPage);
    pageNumbers = Array.from(
      {
        length: Math.min(
          MAX_DOCUMENT_PAGE_SCOPE_REFERENCES,
          high - low + 1
        ),
      },
      (_, index) => low + index
    );
  }

  if (pageNumbers.length) {
    pageNumbers = pageNumbers.slice(
      0,
      MAX_DOCUMENT_PAGE_SCOPE_REFERENCES
    );
    startPage = pageNumbers[0];
    endPage = pageNumbers[pageNumbers.length - 1];
  }

  if (!startPage) return null;
  endPage = endPage || startPage;

  const maxPage = toPositiveDocumentPageNumber(pageCount);
  if (maxPage) {
    pageNumbers = pageNumbers.filter((page) => page <= maxPage);
    if (!pageNumbers.length) return null;
    startPage = pageNumbers[0];
    endPage = pageNumbers[pageNumbers.length - 1];
  }

  const documentId = String(
    value.documentId ||
      value.document_id ||
      ""
  ).trim();

  return {
    ...(documentId ? { documentId } : {}),
    startPage,
    endPage,
    pageNumbers,
    label: String(
      value.label ||
        value.title ||
        value.heading ||
        (startPage === endPage
          ? `Page ${startPage}`
          : `Pages ${startPage}–${endPage}`)
    ).trim(),
    source: String(
      value.source ||
        value.scopeSource ||
        value.scope_source ||
        "page_scope"
    ).trim(),
  };
};

const documentPageScopesEqual = (left, right) => {
  const a = normalizeDocumentPageScope(left);
  const b = normalizeDocumentPageScope(right);
  if (!a || !b) return !a && !b;

  return (
    a.startPage === b.startPage &&
    a.endPage === b.endPage &&
    a.pageNumbers.join(",") === b.pageNumbers.join(",")
  );
};

const buildDocumentRoutingFields = (documentContext) => {
  const context =
    documentContext && typeof documentContext === "object"
      ? documentContext
      : {};

  const documentId = String(
    context.documentId ||
      context.document_id ||
      context.activeDocumentId ||
      context.active_document_id ||
      ""
  ).trim();

  if (!documentId) return {};

  const baseMode = normalizeDocumentChatMode(
    context.mode ||
      context.documentContextMode ||
      context.document_context_mode ||
      "auto"
  );

  const actionId = normalizeDocumentActionId(
    context.actionId ||
      context.action_id ||
      context.documentActionId ||
      context.document_action_id
  );

  const pageScope = normalizeDocumentPageScope(
    context.pageScope ||
      context.page_scope ||
      context.documentPageScope ||
      context.document_page_scope
  );

  // Explicit action/page focus is one-turn routing. It never mutates the
  // persistent Auto/Always Use state. Off remains authoritative unless the
  // caller deliberately passed a force context (for example a new attachment).
  const mode =
    baseMode === "off"
      ? "off"
      : actionId || pageScope
      ? "force"
      : baseMode;

  const nestedContext = {
    documentId,
    mode,
    ...(actionId ? { actionId } : {}),
    ...(pageScope ? { pageScope } : {}),
  };

  return {
    active_document_id: documentId,
    document_context_mode: mode,
    ...(actionId ? { document_action_id: actionId } : {}),
    ...(pageScope ? { document_page_scope: pageScope } : {}),
    document_context: nestedContext,
  };
};

const coerceObject = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value;
  }

  if (typeof value !== "string") return null;

  const raw = value.trim();
  if (!raw || !raw.startsWith("{") || !raw.endsWith("}")) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : null;
  } catch {
    return null;
  }
};

const formatDocumentPageScopeRange = (scope) => {
  const normalized = normalizeDocumentPageScope(scope);
  if (!normalized) return "";

  return normalized.startPage === normalized.endPage
    ? `Page ${normalized.startPage}`
    : `Pages ${normalized.startPage}–${normalized.endPage}`;
};

const documentPageScopeDetail = (scope) => {
  const normalized = normalizeDocumentPageScope(scope);
  if (!normalized) return "";

  const range = formatDocumentPageScopeRange(normalized);
  const label = String(normalized.label || "").replace(/\s+/g, " ").trim();

  if (!label) return "";
  if (label.toLowerCase() === range.toLowerCase()) return "";
  if (/^pages?\s+\d/i.test(label)) return "";

  return label;
};

const prettifyDocumentActionId = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "Document analysis";

  return raw
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const parseDocumentActionDisplayText = (value) => {
  const raw = String(value || "").replace(/\s+/g, " ").trim();
  if (!raw) return null;

  const parts = raw
    .split("·")
    .map((part) => part.trim())
    .filter(Boolean);

  if (
    parts.length < 2 ||
    parts[0].toLowerCase() !== "document intelligence"
  ) {
    return null;
  }

  return {
    actionLabel: parts[1] || "Document analysis",
    documentName:
      parts.length > 2
        ? parts.slice(2).join(" · ")
        : "",
  };
};


const GENERIC_CHAT_ERROR_MESSAGE = "Something went wrong. Please try again.";

// -----------------------------------------------------------------------------
// BILLING / ENTITLEMENT USER-MESSAGE CONTRACT
// -----------------------------------------------------------------------------
// Known billing denials are mapped by machine-readable backend code. We do NOT
// expose backend/HTTP/worker details and we do NOT guess billing from arbitrary
// error prose. Anything that is not a recognized billing code falls back to the
// existing generic System message.
//
// Chat Stream appends ASKLAWLY_BILLING_META_V1 for known Chat billing denials.
// AttachmentController preserves /reserve, /commit, and terminal DI codes on the
// thrown Error object. ChatInterface is the single user-facing mapping layer.
const BILLING_META_MARKER = "ASKLAWLY_BILLING_META_V1";
const BILLING_META_SCHEMA_VERSION = 1;

const BILLING_NOTICE_BY_CODE = Object.freeze({
  no_remaining_questions: {
    title: "Free Chat limit reached",
    message:
      "You've used all 30 questions included with your Free account. Upgrade your plan to continue chatting.",
    product: "chat",
  },
  free_lifetime_document_intelligence_exhausted: {
    title: "Document Intelligence limit reached",
    message:
      "You've used all 2 Document Intelligence analyses included with your Free account. Upgrade your plan to analyze additional documents.",
    product: "document_intelligence",
  },
  monthly_plan_expired: {
    title: "Plan expired",
    message:
      "Your monthly plan has expired. Renew your subscription to continue using AskLawly.",
    product: "account",
  },
  trial_expired: {
    title: "Trial expired",
    message:
      "Your trial has expired. Choose a paid plan to continue using AskLawly.",
    product: "account",
  },
  inactive_or_unsupported_plan: {
    title: "Active plan required",
    message:
      "Your account does not currently have an active supported plan for this feature. Choose or renew a plan to continue.",
    product: "account",
  },
  document_intelligence_not_available_for_plan: {
    title: "Document Intelligence unavailable",
    message:
      "Document Intelligence is not available with your current plan. Upgrade your plan to analyze documents.",
    product: "document_intelligence",
  },
  billing_document_not_found: {
    title: "Billing plan unavailable",
    message:
      "We couldn't verify an active billing plan for this account. Please review your plan or contact support.",
    product: "account",
  },
});

function normalizeBillingCode(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

function parseJsonObjectMaybe(value) {
  if (!value || typeof value !== "string") return null;
  const raw = value.trim();
  if (!raw || !raw.startsWith("{") || !raw.endsWith("}")) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : null;
  } catch {
    return null;
  }
}

function extractBillingDenialEnvelope(value) {
  const raw = String(value || "");
  if (!raw) {
    return { visibleText: raw, billingMeta: null };
  }

  const escapedMarker = BILLING_META_MARKER.replace(
    /[.*+?^${}()|[\]\\]/g,
    "\\$&"
  );
  const re = new RegExp(
    `\\s*<!--${escapedMarker}\\s+({[\\s\\S]*?})\\s*-->\\s*`,
    "gi"
  );

  let billingMeta = null;
  const visibleText = raw.replace(re, (_whole, jsonPayload) => {
    try {
      const parsed = JSON.parse(String(jsonPayload || "{}"));
      if (
        Number(parsed?.schema_version || 0) === BILLING_META_SCHEMA_VERSION &&
        String(parsed?.type || "").trim().toLowerCase() === "billing_denial"
      ) {
        billingMeta = parsed;
      }
    } catch (error) {
      console.warn(
        "[ChatInterface] billing metadata parse failed:",
        error?.message || error
      );
    }
    return "";
  });

  return {
    visibleText: visibleText.trimEnd(),
    billingMeta,
  };
}

function backendErrorPayload(value) {
  if (!value) return null;

  if (value instanceof Error) {
    if (value.payload && typeof value.payload === "object") {
      return value.payload;
    }
    return parseJsonObjectMaybe(value.message);
  }

  if (typeof value === "object" && !Array.isArray(value)) {
    return value;
  }

  if (typeof value === "string") {
    return parseJsonObjectMaybe(value);
  }

  return null;
}

function backendBillingCode(value) {
  if (!value) return "";

  if (value instanceof Error) {
    const direct = normalizeBillingCode(
      value.code || value.rejectionCode || value.failureCode
    );
    if (direct) return direct;
  }

  if (typeof value === "string") {
    const envelope = extractBillingDenialEnvelope(value);
    const envelopeCode = normalizeBillingCode(envelope.billingMeta?.code);
    if (envelopeCode) return envelopeCode;
  }

  const payload = backendErrorPayload(value);
  if (!payload) return "";

  const document =
    payload.document && typeof payload.document === "object"
      ? payload.document
      : {};
  const access =
    payload.documentIntelligenceAccess &&
    typeof payload.documentIntelligenceAccess === "object"
      ? payload.documentIntelligenceAccess
      : payload.document_intelligence_access &&
        typeof payload.document_intelligence_access === "object"
      ? payload.document_intelligence_access
      : {};

  return normalizeBillingCode(
    payload.code ||
      payload.reason ||
      payload.rejection_code ||
      payload.rejectionCode ||
      payload.failure_code ||
      payload.failureCode ||
      document.rejection_code ||
      document.rejectionCode ||
      document.failure_code ||
      document.failureCode ||
      access.reason ||
      access.code ||
      ""
  );
}

function billingNoticeFromCode(
  code,
  { product = "", planType = "", expiresAt = null } = {}
) {
  const normalizedCode = normalizeBillingCode(code);
  if (!normalizedCode) return null;

  const exact = BILLING_NOTICE_BY_CODE[normalizedCode];
  if (exact) {
    return {
      ...exact,
      code: normalizedCode,
      product: exact.product || String(product || "").trim().toLowerCase(),
      planType: String(planType || "").trim().toLowerCase(),
      expiresAt: expiresAt || null,
    };
  }

  // Paid DI has a special rule: pages 1-5 are included, while a document over
  // five pages requires one available monthly DI analysis. Worker-side billing
  // owns the final authoritative chargeable-page decision, so accept its family
  // of monthly DI allowance-exhaustion codes without exposing internal names.
  const looksLikeMonthlyDiExhaustion =
    normalizedCode.includes("document_intelligence") &&
    (normalizedCode.includes("monthly") ||
      normalizedCode.includes("allowance") ||
      normalizedCode.includes("quota") ||
      normalizedCode.includes("analysis")) &&
    /(exhaust|deplet|insufficient|no_remaining|unavailable)/.test(
      normalizedCode
    );

  if (looksLikeMonthlyDiExhaustion) {
    return {
      code: normalizedCode,
      title: "Document Intelligence allowance used",
      message:
        "You've used all Document Intelligence analyses available for this billing period. Documents up to 5 pages are still included; for longer documents, wait for your allowance to reset or upgrade your plan.",
      product: "document_intelligence",
      planType: String(planType || "").trim().toLowerCase(),
      expiresAt: expiresAt || null,
    };
  }

  return null;
}

function getBillingNotice(value, { productHint = "" } = {}) {
  if (!value) return null;

  if (value instanceof Error && value.billingNotice) {
    const notice = value.billingNotice;
    return notice && typeof notice === "object" ? notice : null;
  }

  if (typeof value === "string") {
    const envelope = extractBillingDenialEnvelope(value);
    if (envelope.billingMeta) {
      const meta = envelope.billingMeta;
      const notice = billingNoticeFromCode(meta.code, {
        product: meta.product || productHint,
        planType: meta.plan_type || meta.planType || "",
        expiresAt: meta.expires_at || meta.expiresAt || null,
      });
      if (notice) return notice;
    }
  }

  const payload = backendErrorPayload(value) || {};
  const access =
    payload.documentIntelligenceAccess &&
    typeof payload.documentIntelligenceAccess === "object"
      ? payload.documentIntelligenceAccess
      : payload.document_intelligence_access &&
        typeof payload.document_intelligence_access === "object"
      ? payload.document_intelligence_access
      : {};

  return billingNoticeFromCode(backendBillingCode(value), {
    product:
      payload.product ||
      access.product ||
      productHint ||
      "",
    planType:
      payload.plan_type ||
      payload.planType ||
      access.plan_type ||
      access.planType ||
      value?.planType ||
      "",
    expiresAt:
      payload.expires_at ||
      payload.expiresAt ||
      access.expires_at ||
      access.expiresAt ||
      null,
  });
}

function createBillingAccessError(notice, cause = null) {
  const safeNotice =
    notice && typeof notice === "object" ? notice : null;
  const error = new Error(
    safeNotice?.message || GENERIC_CHAT_ERROR_MESSAGE
  );
  error.name = "BillingAccessError";
  error.isBillingAccessError = true;
  error.billingNotice = safeNotice;
  error.code = safeNotice?.code || null;
  error.product = safeNotice?.product || null;
  error.cause = cause || null;
  return error;
}

function toUserFacingChatError(
  value,
  fallback = GENERIC_CHAT_ERROR_MESSAGE,
  options = {}
) {
  const billingNotice = getBillingNotice(value, options);
  return billingNotice?.message || String(
    fallback || GENERIC_CHAT_ERROR_MESSAGE
  );
}

// ✅ Structured follow-up transport contract shared with chat_stream_handler.py.
// The backend keeps the visible Follow-up section for backward compatibility and,
// when this capability is advertised, appends a versioned HTML-comment envelope.
const STRUCTURED_FOLLOWUP_CAPABILITY = "structured_followups_v1";
const STRUCTURED_FOLLOWUP_MARKER = "ASKLAWLY_FOLLOWUP_META_V1";
const STRUCTURED_FOLLOWUP_SCHEMA_VERSION = 1;
const CHAT_MODE_STORAGE_KEY = "asklawly_chat_mode";
const DI_THREAD_SELECTION_STORAGE_PREFIX = "asklawly:document-intelligence:selected";
const DI_CHAT_MODE_STORAGE_PREFIX = "asklawly:document-intelligence:chat-mode";
const DI_PROCESSING_RECOVERY_STORAGE_PREFIX = "asklawly:document-intelligence:processing";
const FILE_VAULT_DI_HANDOFF_STORAGE_KEY = "asklawly:file-vault:document-intelligence:handoff";
const FILE_VAULT_DI_HANDOFF_NAV_STATE_KEY = "fileVaultDocumentIntelligenceHandoff";
const FILE_VAULT_DI_HANDOFF_SCHEMA_VERSION = 1;
// The early handoff should be consumed almost immediately after File Vault
// navigates to Chat. A stale browser record must never pull a user back into an
// old session days later.
const FILE_VAULT_DI_HANDOFF_MAX_AGE_MS = 30 * 60 * 1000;
const DI_PROCESSING_RECOVERY_POLL_MS = 5000;
const DI_PROCESSING_RECOVERY_MAX_ERRORS = 6;

const DI_PROCESSING_TERMINAL_STATUSES = new Set([
  "ready",
  "ready_with_warnings",
  "rejected",
  "failed",
  "abandoned",
  "deleted",
]);

const DI_PROCESSING_RECONNECTABLE_STATUSES = new Set([
  "committed",
  "queued",
  "dispatching",
  "starting",
  "claimed",
  "processing",
  "indexing",
  "analyzing",
  "retrying",
  "recovering",
  "verifying_source",
  "verifying_sources",
]);

const DI_CHAT_ELIGIBLE_STATUSES = new Set([
  "ready",
  "ready_with_warnings",
  ...DI_PROCESSING_RECONNECTABLE_STATUSES,
]);

function normalizeDocumentProcessingStatus(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

function isDocumentChatEligible(value) {
  const source =
    value && typeof value === "object"
      ? value
      : {};

  // If Main later exposes an explicit negative eligibility signal, honor it.
  if (
    source.chatEligible === false ||
    source.chat_eligible === false
  ) {
    return false;
  }

  // Lifecycle metadata is defensive only; public catalog projections may omit
  // these fields. Status remains the primary frontend signal.
  if (
    source.deleted === true ||
    source.abandoned === true
  ) {
    return false;
  }

  const status = normalizeDocumentProcessingStatus(
    source.status ||
      source.documentStatus ||
      source.document_status ||
      ""
  );

  return DI_CHAT_ELIGIBLE_STATUSES.has(status);
}

// Customer-facing Document Intelligence progress copy.
//
// IMPORTANT:
// - progress_percent remains exactly backend-authoritative.
// - raw backend progress_message is operational data and is NEVER rendered.
// - known Worker/Main stages are translated into plain, truthful language.
// - unknown future stages fall back to the real percentage range, never to
//   technical backend text.
function customerDocumentProgressMessage({
  stage,
  status,
  percent,
} = {}) {
  const normalizedStage =
    normalizeDocumentProcessingStatus(stage);
  const normalizedStatus =
    normalizeDocumentProcessingStatus(status);

  const key =
    normalizedStage ||
    normalizedStatus ||
    "processing";

  const exactStageMessages = {
    reserving:
      "Getting your document ready to upload…",
    reserved:
      "Your document is ready to upload…",
    uploading:
      "Uploading your document securely…",
    uploaded:
      "Upload complete. Starting document analysis…",
    committing:
      "Starting document analysis…",
    committed:
      "Your document is ready. Starting analysis…",
    queued:
      "Your document is queued and analysis will begin shortly…",
    dispatching:
      "Starting document analysis…",
    dispatched:
      "Starting document analysis…",
    starting:
      "Starting document analysis…",

    claimed:
      "Document analysis has started…",
    verifying_source:
      "Checking your file and confirming it can be processed…",
    verifying_sources:
      "Checking your file and confirming it can be processed…",
    source_downloaded:
      "Your file is ready. Checking the document structure…",
    validated:
      "Document checks are complete. Preparing the pages…",
    converted:
      "Preparing the document pages for reading…",
    canonical_ingested:
      "Reading the document and organizing its pages…",

    // The supplied Worker shows that the substantial intelligence pass occurs
    // after this checkpoint, so this wording can honestly remain for a while.
    canonical_persisted:
      "Reading and analyzing the document in depth…",

    intelligence_complete:
      "Key information has been identified. Organizing it for accurate answers…",
    indexed:
      "Preparing fast page references and document search…",
    actions_ready:
      "Preparing useful document analysis options…",
    finalizing_artifacts:
      "Finalizing your document analysis…",
    manifest_committed:
      "Finishing up and saving the completed analysis…",
    completed:
      "Document analysis complete.",

    retrying:
      "A temporary issue occurred. Retrying your document analysis…",
    recovering:
      "A temporary issue occurred. Continuing your document analysis…",
    worker_failed:
      "A temporary issue occurred while processing the document…",
    processing:
      "Processing your document…",
  };

  if (exactStageMessages[key]) {
    return exactStageMessages[key];
  }

  if (
    normalizedStatus &&
    exactStageMessages[normalizedStatus]
  ) {
    return exactStageMessages[normalizedStatus];
  }

  const numericPercent = Number(percent);
  const hasPercent = Number.isFinite(numericPercent);

  if (!hasPercent || numericPercent <= 0) {
    return "Getting your document ready for analysis…";
  }

  if (numericPercent < 7) {
    return "Starting document analysis…";
  }

  if (numericPercent < 18) {
    return "Checking your file and document structure…";
  }

  if (numericPercent < 36) {
    return "Preparing and reading the document pages…";
  }

  if (numericPercent < 72) {
    return "Reading and analyzing the document in depth…";
  }

  if (numericPercent < 84) {
    return "Organizing key information for accurate answers…";
  }

  if (numericPercent < 94) {
    return "Preparing document search and analysis tools…";
  }

  if (numericPercent < 100) {
    return "Finalizing your document analysis…";
  }

  return "Document analysis complete.";
}

function documentProcessingSessionId(value) {
  const source =
    value && typeof value === "object"
      ? value
      : {};

  const attachment =
    source.attachment &&
    typeof source.attachment === "object"
      ? source.attachment
      : {};

  return String(
    source.sessionId ||
      source.session_id ||
      source.chatSessionId ||
      source.chat_session_id ||
      source.sourceSessionId ||
      source.source_session_id ||
      attachment.sessionId ||
      attachment.session_id ||
      ""
  ).trim();
}

function normalizeDocumentProcessingPayload(value, fallback = {}) {
  const source =
    value && typeof value === "object"
      ? value
      : {};

  const document =
    source.document &&
    typeof source.document === "object"
      ? source.document
      : {};

  const projection =
    source.documentIntelligence &&
    typeof source.documentIntelligence === "object"
      ? source.documentIntelligence
      : source.document_intelligence &&
        typeof source.document_intelligence === "object"
      ? source.document_intelligence
      : {};

  const progress =
    source.progress &&
    typeof source.progress === "object"
      ? source.progress
      : {};

  const projectedProgress =
    projection.progress &&
    typeof projection.progress === "object"
      ? projection.progress
      : {};

  const documentId = String(
    source.documentId ||
      source.document_id ||
      document.documentId ||
      document.document_id ||
      projection.documentId ||
      projection.document_id ||
      fallback.documentId ||
      ""
  ).trim();

  const status = normalizeDocumentProcessingStatus(
    source.status ||
      source.documentStatus ||
      source.document_status ||
      document.status ||
      projection.status ||
      projection.documentStatus ||
      projection.document_status ||
      fallback.status ||
      fallback.documentStatus ||
      ""
  );

  const progressStage = String(
    source.progressStage ||
      source.progress_stage ||
      progress.stage ||
      document.progressStage ||
      document.progress_stage ||
      projection.progressStage ||
      projection.progress_stage ||
      projectedProgress.stage ||
      fallback.progressStage ||
      fallback.progress_stage ||
      status ||
      "processing"
  ).trim();

  const progressPercentRaw =
    source.progressPercent ??
    source.progress_percent ??
    progress.percent ??
    document.progressPercent ??
    document.progress_percent ??
    projection.progressPercent ??
    projection.progress_percent ??
    projectedProgress.percent ??
    fallback.progressPercent ??
    fallback.progress_percent ??
    null;

  const hasBackendPercent =
    progressPercentRaw !== null &&
    progressPercentRaw !== undefined &&
    String(progressPercentRaw).trim() !== "";

  const parsedPercent = hasBackendPercent
    ? Number(progressPercentRaw)
    : Number.NaN;

  const progressPercent = Number.isFinite(parsedPercent)
    ? Math.max(0, Math.min(100, parsedPercent))
    : null;

  const progressMessage = String(
    source.progressMessage ||
      source.progress_message ||
      progress.message ||
      document.progressMessage ||
      document.progress_message ||
      projection.progressMessage ||
      projection.progress_message ||
      projectedProgress.message ||
      fallback.progressMessage ||
      fallback.progress_message ||
      ""
  ).trim();

  const fileName = String(
    source.fileName ||
      source.file_name ||
      source.filename ||
      document.fileName ||
      document.file_name ||
      document.filename ||
      projection.fileName ||
      projection.file_name ||
      fallback.fileName ||
      "Document"
  ).trim();

  return {
    documentId,
    status,
    progressStage:
      progressStage ||
      status ||
      "processing",
    progressPercent,
    progressMessage,
    fileName: fileName || "Document",
    sessionId:
      documentProcessingSessionId(source) ||
      documentProcessingSessionId(document) ||
      documentProcessingSessionId(projection) ||
      String(fallback.sessionId || "").trim(),
  };
}

function normalizeFileVaultDocumentIntelligenceHandoff(value) {
  const source =
    value && typeof value === "object" && !Array.isArray(value)
      ? value
      : {};

  const schemaVersion = Number(
    source.schemaVersion ??
      source.schema_version ??
      FILE_VAULT_DI_HANDOFF_SCHEMA_VERSION
  );

  if (schemaVersion !== FILE_VAULT_DI_HANDOFF_SCHEMA_VERSION) {
    return null;
  }

  const sessionId = String(
    source.sessionId ||
      source.session_id ||
      source.chatSession?.sessionId ||
      source.chatSession?.session_id ||
      ""
  ).trim();

  const documentId = String(
    source.documentId ||
      source.document_id ||
      source.document?.documentId ||
      source.document?.document_id ||
      ""
  ).trim();

  if (!sessionId || !documentId) return null;

  const sizeBytesRaw =
    source.sizeBytes ??
    source.size_bytes ??
    source.document?.size_bytes ??
    null;

  const parsedSizeBytes = Number(sizeBytesRaw);

  return {
    schemaVersion: FILE_VAULT_DI_HANDOFF_SCHEMA_VERSION,
    source: "file_vault",
    sessionId,
    documentId,
    turnId: String(
      source.turnId ||
        source.turn_id ||
        ""
    ).trim(),
    fileName: String(
      source.fileName ||
        source.file_name ||
        source.filename ||
        source.fileVaultSource?.name ||
        source.document?.filename ||
        "Document"
    ).trim() || "Document",
    mime: String(
      source.mime ||
        source.mimeType ||
        source.mime_type ||
        source.contentType ||
        source.content_type ||
        source.fileVaultSource?.contentType ||
        source.document?.mime_type ||
        ""
    ).trim(),
    sizeBytes:
      Number.isFinite(parsedSizeBytes) && parsedSizeBytes > 0
        ? parsedSizeBytes
        : null,
    commitRequired:
      source.commitRequired !== false &&
      source.commit_required !== false,
    createdAt: Number(
      source.createdAt ||
        source.created_at ||
        Date.now()
    ),
  };
}

function isFreshFileVaultDocumentIntelligenceHandoff(handoff) {
  if (!handoff) return false;

  const createdAt = Number(handoff.createdAt || 0);
  if (!Number.isFinite(createdAt) || createdAt <= 0) return true;

  const age = Date.now() - createdAt;

  // Allow a small amount of clock skew, but reject browser handoffs that are
  // clearly stale. Once /commit is durably running, the separate authoritative
  // DI recovery record owns refresh/restart recovery instead.
  return (
    age <= FILE_VAULT_DI_HANDOFF_MAX_AGE_MS &&
    age >= -5 * 60 * 1000
  );
}

const makeClientMessageId = (prefix = "msg") => {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return `${prefix}_${crypto.randomUUID()}`;
    }
  } catch {}
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

// One logical Send identity. Generate exactly once inside sendMessage() and pass
// this opaque ID through every backend operation belonging to that Send.
const makeLogicalTurnId = () => makeClientMessageId("turn");

const normalizeFollowUpMetaItem = (item, fallbackQuestion = "", fallbackId = "") => {
  const src = item && typeof item === "object" ? item : {};
  const question = String(src.question || fallbackQuestion || "").replace(/\s+/g, " ").trim();
  if (!question) return null;

  let type = String(src.type || "text").trim().toLowerCase();
  if (!["text", "yes_no", "choice"].includes(type)) type = "text";

  let options = Array.isArray(src.options)
    ? src.options.map((x) => String(x || "").replace(/\s+/g, " ").trim()).filter(Boolean)
    : [];

  if (type === "yes_no") {
    options = ["Yes", "No"];
  } else if (type === "choice") {
    const seen = new Set();
    options = options.filter((option) => {
      const key = option.toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    if (options.length < 2) {
      type = "text";
      options = [];
    }
  } else {
    options = [];
  }

  return {
    id: String(src.id || fallbackId || makeClientMessageId("fu")),
    question,
    type,
    options,
    send_behavior: type === "text" ? "compose" : "direct",
    allow_custom: src.allow_custom !== false,
    parent_message_id: String(src.parent_message_id || src.parentMessageId || ""),
  };
};

function extractStructuredFollowUpEnvelope(value) {
  const raw = String(value || "");
  if (!raw) return { visibleText: raw, originMode: null, followUps: [], schemaVersion: null };

  const escapedMarker = STRUCTURED_FOLLOWUP_MARKER.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`\\s*<!--${escapedMarker}\\s+({[\\s\\S]*?})\\s*-->\\s*`, "gi");

  let originMode = null;
  let schemaVersion = null;
  let followUps = [];

  const visibleText = raw.replace(re, (_whole, jsonPayload) => {
    try {
      const parsed = JSON.parse(String(jsonPayload || "{}"));
      const version = Number(parsed?.schema_version || 0);
      if (version !== STRUCTURED_FOLLOWUP_SCHEMA_VERSION) return "";

      schemaVersion = version;
      originMode = normalizeChatMode(parsed?.origin_mode || "auto");
      const rawItems = Array.isArray(parsed?.followups) ? parsed.followups : [];
      followUps = rawItems
        .map((item, idx) => normalizeFollowUpMetaItem(item, "", `fu_meta_${idx}`))
        .filter(Boolean);
    } catch (e) {
      console.warn("[ChatInterface] structured follow-up metadata parse failed:", e?.message || e);
    }
    // Metadata is transport-only and must never be visible in the answer body.
    return "";
  });

  return {
    visibleText: visibleText.trimEnd(),
    originMode,
    followUps,
    schemaVersion,
  };
}

function buildRenderableFollowUps(plainQuestions, structuredItems, parentMessageId = "") {
  const structured = Array.isArray(structuredItems)
    ? structuredItems.map((item, idx) => normalizeFollowUpMetaItem(item, "", `fu_struct_${idx}`)).filter(Boolean)
    : [];

  const plain = Array.isArray(plainQuestions) ? plainQuestions : [];
  const byQuestion = new Map(structured.map((item) => [item.question.toLowerCase(), item]));
  const used = new Set();
  const out = [];

  plain.forEach((question, idx) => {
    const q = String(question || "").trim();
    if (!q) return;
    const key = q.toLowerCase();
    const matched = byQuestion.get(key) || structured[idx] || null;
    const normalized = normalizeFollowUpMetaItem(matched || {}, q, `fu_legacy_${idx}`);
    if (!normalized) return;
    normalized.parent_message_id = normalized.parent_message_id || String(parentMessageId || "");
    out.push(normalized);
    used.add(normalized.id);
  });

  // If the visible section was malformed/omitted but valid structured metadata arrived,
  // keep the metadata actionable rather than silently discarding it.
  structured.forEach((item) => {
    if (used.has(item.id)) return;
    out.push({
      ...item,
      parent_message_id: item.parent_message_id || String(parentMessageId || ""),
    });
  });

  return out;
}

const normalizeFollowUpContextForSend = (followUp) => {
  const normalized = normalizeFollowUpMetaItem(followUp);
  if (!normalized) return null;
  return {
    id: normalized.id,
    question: normalized.question,
    type: normalized.type,
    options: normalized.options,
    parent_message_id: normalized.parent_message_id || "",
  };
};

// ✅ Pull a final "Follow-up" / "Follow-up questions" section out of a completed Lexa answer.
// The frontend does NOT decide whether a follow-up is required:
// - Research: backend is expected to always provide at least one.
// - Auto: backend decides according to the route it actually uses.
// This helper only separates the backend-provided section so it can be rendered after Sources.
function splitFollowUpSection(value) {
  const raw = String(value || "");
  if (!raw.trim()) return { answerText: raw, followUps: [] };

  const rows = raw.split(/\r?\n/);
  let headingIndex = -1;
  let inlineRemainder = "";

  for (let i = rows.length - 1; i >= 0; i -= 1) {
    const line = String(rows[i] || "");
    const match = line.match(
      /^\s*(?:#{1,6}\s*)?(?:\*\*|__)?follow[- ]?up(?:\s+questions?)?(?:\*\*|__)?\s*:?\s*(.*)$/i
    );
    if (match) {
      headingIndex = i;
      inlineRemainder = String(match[1] || "").trim();
      break;
    }
  }

  if (headingIndex < 0) return { answerText: raw, followUps: [] };

  const answerText = rows.slice(0, headingIndex).join("\n").trimEnd();
  const tailLines = [inlineRemainder, ...rows.slice(headingIndex + 1)]
    .map((line) =>
      String(line || "")
        .replace(/^\s*(?:[-*•]|\d+[.)])\s*/, "")
        .trim()
    )
    .filter(Boolean);

  const followUps = [];
  const seen = new Set();

  const addQuestion = (candidate) => {
    const q = String(candidate || "").trim();
    if (!q) return;
    const key = q.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    followUps.push(q);
  };

  tailLines.forEach((line) => {
    const questionChunks = line.match(/[^?]+\?/g);
    if (questionChunks && questionChunks.length > 0) {
      questionChunks.forEach(addQuestion);
    } else {
      addQuestion(line);
    }
  });

  // Do not strip an accidental heading unless there is an actual follow-up payload.
  if (!followUps.length) return { answerText: raw, followUps: [] };

  return { answerText, followUps };
}

function formatIsoForFileName() {
  try {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(d.getHours())}-${pad(
      d.getMinutes()
    )}-${pad(d.getSeconds())}`;
  } catch {
    return String(Date.now());
  }
}


// -----------------------------------------------------------------------------
// DOCUMENT INTELLIGENCE — CHAT ADAPTER
// -----------------------------------------------------------------------------
// ChatInterface owns the bridge between legacy chat attachments and the reusable
// DocumentIntelligence UI. The new ingestion/retrieval pipeline can later replace
// this adapter without changing DocumentIntelligence itself.

const DOCUMENT_FILE_EXTENSION_RE = /\.(pdf|doc|docx|rtf|txt|odt|pages|wpd)$/i;
const IMAGE_FILE_EXTENSION_RE = /\.(png|jpe?g|webp|gif|bmp|heic|heif)$/i;

function getFileAccessBase() {
  const runtimeBase =
    typeof window !== "undefined"
      ? window.__FILE_ACCESS_BASE__ || ""
      : "";

  return String(
    process.env.REACT_APP_FILE_ACCESS_BASE ||
      runtimeBase ||
      process.env.REACT_APP_BACKEND_URL ||
      ""
  )
    .trim()
    .replace(/\/+$/, "");
}

function documentFileIdentity(document) {
  const source =
    document && typeof document === "object"
      ? document
      : {};

  const attachment =
    source.attachment &&
    typeof source.attachment === "object"
      ? source.attachment
      : {};

  const documentId = String(
    source.documentId ||
      source.document_id ||
      source.id ||
      attachment.documentId ||
      attachment.document_id ||
      ""
  ).trim();

  const fileName = String(
    source.fileName ||
      source.file_name ||
      source.name ||
      attachment.fileName ||
      attachment.file_name ||
      attachment.filename ||
      source.projectedFileName ||
      "Document"
  ).trim();

  const mimeType = String(
    source.mimeType ||
      source.mime_type ||
      source.contentType ||
      source.content_type ||
      attachment.mimeType ||
      attachment.mime_type ||
      attachment.mime ||
      attachment.contentType ||
      attachment.content_type ||
      attachment.projectedMimeType ||
      source.projectedMimeType ||
      ""
  ).trim();

  const extensionMatch =
    fileName.match(/\.([A-Za-z0-9]+)$/);
  const extension = String(
    extensionMatch?.[1] || ""
  ).toLowerCase();

  return {
    documentId,
    fileName: fileName || "Document",
    mimeType,
    extension,
    isPdf:
      extension === "pdf" ||
      /application\/pdf/i.test(mimeType),
  };
}

function buildDocumentIntelligenceGatewayViewerRecord({
  document,
  fileAccessUrl,
  browserTicketUrl,
  authorizationHeader,
  browserViewUrl = "",
  openSource = "",
}) {
  const identity = documentFileIdentity(document);
  if (!identity.documentId || !fileAccessUrl || !authorizationHeader) {
    return null;
  }

  // PDF.js can carry Authorization headers itself. Office/generic iframes cannot,
  // so those surfaces use the already-issued opaque AskLawly browser ticket.
  const viewerUrl = identity.isPdf
    ? fileAccessUrl
    : browserViewUrl;

  if (!viewerUrl) return null;

  const gatewayHeaders = {
    Authorization: authorizationHeader,
  };

  return {
    documentId: identity.documentId,
    title: identity.fileName,
    subject: identity.fileName,
    filename: identity.fileName,
    name: identity.fileName,
    ...(identity.extension
      ? {
          format: identity.extension,
          fileFormat: identity.extension,
          documentFormat: identity.extension,
        }
      : {}),
    ...(identity.mimeType
      ? {
          contentType: identity.mimeType,
          mimeType: identity.mimeType,
        }
      : {}),

    // Shared FileViewerModal / PdfViewer gateway contract.
    fileAccessGateway: true,
    gatewayAccess: true,
    fileAccessUrl,
    browserTicketUrl,
    fileAccessBrowserTicketUrl: browserTicketUrl,
    httpHeaders: gatewayHeaders,
    fileAccessHeaders: gatewayHeaders,
    withCredentials: false,
    fileAccessWithCredentials: false,

    // For PDFs this is the authenticated gateway route. For non-PDF/Office
    // content this is a short-lived AskLawly ticket route so an iframe/external
    // Office renderer never receives a cloud-storage URL.
    viewerUrl,
    url: viewerUrl,
    downloadUrl: viewerUrl,

    ...(identity.isPdf
      ? {
          pdfUrl: fileAccessUrl,
          pdfUrlInline: fileAccessUrl,
          urls: { pdf: fileAccessUrl },
        }
      : {}),

    allowDownload: true,
    linkAllowDownload: true,
    source:
      String(openSource || "").trim() ||
      "document_intelligence_original_gateway",
  };
}

function stableDocumentHash(value) {
  const raw = String(value || "");
  let hash = 2166136261;
  for (let i = 0; i < raw.length; i += 1) {
    hash ^= raw.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

function normalizeAttachmentUrls(attachment) {
  const source = attachment && typeof attachment === "object" ? attachment : {};
  const raw = Array.isArray(source.fileUrls) && source.fileUrls.length
    ? source.fileUrls
    : source.fileUrl
    ? [source.fileUrl]
    : [];
  return raw.map((url) => String(url || "").trim()).filter((url) => /^https?:\/\/.+/i.test(url));
}

function inferDocumentTypeFromName(fileName) {
  const name = String(fileName || "").trim().toLowerCase();
  if (!name) return "document";
  if (/\bcomplaint\b/.test(name)) return "complaint";
  if (/\bpetition\b/.test(name)) return "petition";
  if (/\bmotion\b/.test(name)) return "motion";
  if (/\border\b|\bjudgment\b|\bdecision\b/.test(name)) return "order";
  if (/\bnotice\b|\brfe\b|\bnoid\b|\bnotice of\b/.test(name)) return "notice";
  if (/\bcontract\b/.test(name)) return "contract";
  if (/\bagreement\b|\blease\b|\bnda\b/.test(name)) return "agreement";
  return "document";
}

function isDocumentLikeAttachment(attachment, message = null) {
  const source = attachment && typeof attachment === "object" ? attachment : {};
  const fileName = String(source.fileName || source.filename || "");
  const urls = normalizeAttachmentUrls(source);

  if (source.documentId || source.document_id || source.documentType || source.document_type) return true;
  if (message?.isVisionContext || source.isVisionContext) return false;
  if (DOCUMENT_FILE_EXTENSION_RE.test(fileName)) return true;
  if (IMAGE_FILE_EXTENSION_RE.test(fileName)) return false;

  // The legacy chat upload path converts a multipage document into one image URL
  // per page. More than one URL is therefore a strong document signal.
  return urls.length > 1;
}

function makeAttachmentDocumentId(attachment, messageId = "", index = 0) {
  const source = attachment && typeof attachment === "object" ? attachment : {};
  const explicit = source.documentId || source.document_id || source.uploadBatchId || source.upload_batch_id;
  if (explicit) return String(explicit);

  const urls = normalizeAttachmentUrls(source);
  const seed = [
    source.fileName || source.filename || "",
    urls[0] || "",
    messageId || "",
    index,
  ].join("|");

  return `doc_${stableDocumentHash(seed)}`;
}

function buildDocumentFromMessage(message, index = 0) {
  const msg = message && typeof message === "object" ? message : {};
  const attachment = msg.attachment && typeof msg.attachment === "object" ? msg.attachment : null;
  if (!attachment || !isDocumentLikeAttachment(attachment, msg)) return null;

  const urls = normalizeAttachmentUrls(attachment);
  if (!urls.length) return null;

  const id = makeAttachmentDocumentId(
    attachment,
    msg.messageId || msg.message_id || msg.id || "",
    index
  );
  const fileName = String(attachment.fileName || attachment.filename || `Document ${index + 1}`);
  const explicitPageCount =
    attachment.pageCount ?? attachment.page_count ?? attachment.totalPages ?? attachment.total_pages;
  const pageCount = Number.isFinite(Number(explicitPageCount))
    ? Math.max(1, Number(explicitPageCount))
    : Math.max(1, urls.length);

  return {
    id,
    documentId: id,
    name: fileName,
    fileName,
    pageCount,
    type: String(
      attachment.documentType ||
        attachment.document_type ||
        inferDocumentTypeFromName(fileName)
    ).toLowerCase(),
    status: attachment.documentStatus || attachment.document_status || "ready",
    pageMap: Array.isArray(attachment.pageMap)
      ? attachment.pageMap
      : Array.isArray(attachment.page_map)
      ? attachment.page_map
      : [],
    actions: Array.isArray(attachment.documentActions)
      ? attachment.documentActions
      : Array.isArray(attachment.document_actions)
      ? attachment.document_actions
      : null,
    completedActions: Array.isArray(attachment.completedActions)
      ? attachment.completedActions
      : [],
    attachment: {
      ...attachment,
      fileUrl: urls[0] || null,
      fileUrls: urls,
      fileName,
      documentId: id,
    },
    sourceMessageId: String(msg.messageId || msg.message_id || msg.id || ""),
    sourceMessageIndex: index,
  };
}

function buildDocumentFromCatalogEntry(entry, index = 0) {
  const source = entry && typeof entry === "object" ? entry : {};
  const documentId = String(
    source.documentId ||
      source.document_id ||
      source.id ||
      ""
  ).trim();

  if (!documentId) return null;

  const fileName = String(
    source.fileName ||
      source.file_name ||
      source.filename ||
      `Document ${index + 1}`
  ).trim();

  const pageCountRaw =
    source.pageCount ??
    source.page_count ??
    source.actualPageCount ??
    source.actual_page_count ??
    null;

  const pageCount =
    Number.isFinite(Number(pageCountRaw)) && Number(pageCountRaw) > 0
      ? Number(pageCountRaw)
      : null;

  const progress =
    source.progress && typeof source.progress === "object"
      ? source.progress
      : {};

  return {
    ...source,
    id: documentId,
    documentId,
    name: fileName || `Document ${index + 1}`,
    fileName: fileName || `Document ${index + 1}`,
    pageCount,
    type: String(
      source.documentType ||
        source.document_type ||
        inferDocumentTypeFromName(fileName)
    ).toLowerCase(),
    status: String(
      source.status ||
        source.documentStatus ||
        source.document_status ||
        "processing"
    ).toLowerCase(),
    progressStage:
      source.progressStage ||
      source.progress_stage ||
      progress.stage ||
      "",
    progressPercent:
      source.progressPercent ??
      source.progress_percent ??
      progress.percent ??
      null,
    progressMessage:
      source.progressMessage ||
      source.progress_message ||
      progress.message ||
      "",
    actions: Array.isArray(source.documentActions)
      ? source.documentActions
      : Array.isArray(source.document_actions)
      ? source.document_actions
      : null,
    pageMap: Array.isArray(source.pageMap)
      ? source.pageMap
      : Array.isArray(source.page_map)
      ? source.page_map
      : [],
  };
}

function attachmentFileTypeLabel(attachment) {
  const source =
    attachment && typeof attachment === "object"
      ? attachment
      : {};

  const fileName = String(
    source.fileName ||
      source.filename ||
      ""
  ).trim();

  const extensionMatch = fileName.match(/\.([A-Za-z0-9]+)$/);
  if (extensionMatch?.[1]) {
    const ext = extensionMatch[1].toUpperCase();
    if (ext === "JPG") return "JPEG";
    return ext;
  }

  const mime = String(
    source.mime ||
      source.mimeType ||
      source.mime_type ||
      ""
  )
    .trim()
    .toLowerCase();

  if (mime === "application/pdf") return "PDF";
  if (mime.includes("wordprocessingml")) return "DOCX";
  if (mime === "application/msword") return "DOC";
  if (mime.includes("spreadsheetml")) return "XLSX";
  if (mime === "application/vnd.ms-excel") return "XLS";
  if (mime === "text/csv") return "CSV";
  if (mime === "text/plain") return "TXT";
  if (mime === "image/jpeg") return "JPEG";
  if (mime === "image/png") return "PNG";
  if (mime === "image/webp") return "WEBP";

  return "FILE";
}

function attachmentPageLabel(attachment) {
  const source =
    attachment && typeof attachment === "object"
      ? attachment
      : {};

  const pageCountRaw =
    source.pageCount ??
    source.page_count ??
    source.actualPageCount ??
    source.actual_page_count ??
    null;

  const pageCount = Number(pageCountRaw);
  if (!Number.isFinite(pageCount) || pageCount <= 0) {
    return "";
  }

  const pages = Math.round(pageCount);
  return `${pages} ${pages === 1 ? "page" : "pages"}`;
}

function attachmentDescriptor(attachment) {
  const parts = [
    attachmentFileTypeLabel(attachment),
    attachmentPageLabel(attachment),
  ].filter(Boolean);

  return parts.join(" · ");
}

// Document Intelligence owns its own file-viewing surface. Once an attachment
// belongs to DI, Chat should never duplicate the source as a raw inline image.
// Keep the compact transcript strip for conversation context, but leave file
// opening to the dedicated Document Intelligence View File surface. Legacy
// non-DI image attachments retain their existing inline-preview behavior.
function isDocumentIntelligenceChatAttachment(attachment) {
  const source =
    attachment && typeof attachment === "object"
      ? attachment
      : {};

  const mode = String(
    source.attachmentMode ||
      source.attachment_mode ||
      source.mode ||
      ""
  )
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  if (mode.includes("document_intelligence")) {
    return true;
  }

  const documentId = String(
    source.documentId ||
      source.document_id ||
      ""
  ).trim();

  if (!documentId) return false;

  // Compatibility for older DI transcript rows that predate attachmentMode.
  // Require DI-specific metadata as well as a document identity so legacy
  // image attachments with a synthetic documentId are not reclassified.
  return !!(
    source.documentStatus ||
    source.document_status ||
    source.documentType ||
    source.document_type ||
    (Array.isArray(source.documentActions) &&
      source.documentActions.length > 0) ||
    (Array.isArray(source.document_actions) &&
      source.document_actions.length > 0) ||
    (Array.isArray(source.pageMap) &&
      source.pageMap.length > 0) ||
    (Array.isArray(source.page_map) &&
      source.page_map.length > 0)
  );
}

function buildChatAttachmentViewerRecord(
  attachment,
  requestedUrl = "",
  pageNumber = 1
) {
  const source =
    attachment && typeof attachment === "object"
      ? attachment
      : {};

  // For the primary "View file" action, prefer the attachment's original
  // fileUrl. Never substitute a Document Intelligence canonical page/artifact.
  // Page-specific legacy links may still use the exact requested page URL.
  const originalFileUrl = String(
    source.fileUrl ||
      source.file_url ||
      ""
  ).trim();

  const explicitUrl = String(
    requestedUrl || ""
  ).trim();

  const viewUrl =
    Number(pageNumber) > 1
      ? explicitUrl || originalFileUrl
      : originalFileUrl || explicitUrl;

  if (!/^https?:\/\/.+/i.test(viewUrl)) {
    return null;
  }

  const fileName = String(
    source.fileName ||
      source.filename ||
      source.projectedFileName ||
      "Attached file"
  ).trim();

  const mimeType = String(
    source.mime ||
      source.mimeType ||
      source.mime_type ||
      source.contentType ||
      source.content_type ||
      source.projectedMimeType ||
      ""
  ).trim();

  const extensionMatch =
    fileName.match(/\.([A-Za-z0-9]+)$/);
  const extension = String(
    extensionMatch?.[1] || ""
  ).toLowerCase();

  return {
    title: fileName || "Attached file",
    subject: fileName || "Attached file",
    filename: fileName || "Attached file",
    name: fileName || "Attached file",
    ...(extension
      ? {
          format: extension,
          fileFormat: extension,
          documentFormat: extension,
        }
      : {}),
    ...(mimeType
      ? {
          contentType: mimeType,
          mimeType,
        }
      : {}),
    viewerUrl: viewUrl,
    url: viewUrl,
    downloadUrl: originalFileUrl || viewUrl,
    ...(extension === "pdf" || /application\/pdf/i.test(mimeType)
      ? {
          pdfUrl: viewUrl,
          pdfUrlInline: viewUrl,
          urls: { pdf: viewUrl },
        }
      : {}),
    allowDownload: true,
    linkAllowDownload: true,
    source: "chat_attachment_original",
  };
}

const DOCUMENT_ACTION_PROMPTS = {
  brief:
    "Provide a concise professional brief of this document. Identify its apparent document type and purpose, the key facts, material legal issues, requested relief or obligations, important dates, and the practical significance. Use page references whenever they can be determined from the supplied pages.",
  claims:
    "Identify and analyze every claim or cause of action in this document. For each claim, state who asserts it against whom, summarize the supporting allegations, identify the requested relief or damages, and cite the relevant page numbers whenever possible. Distinguish allegations from established facts.",
  parties:
    "Identify the parties, entities, agencies, courts, counsel, and other material actors in this document. Explain each person's or entity's role and relationship to the matter. Include page references whenever possible.",
  key_dates:
    "Extract the legally or procedurally important dates, deadlines, hearing dates, filing dates, response dates, effective dates, and time periods in this document. Separate dates expressly stated in the document from any date that would require calculation or outside-law verification. Include page references.",
  damages:
    "Analyze the damages, monetary demands, penalties, fees, costs, restitution, interest, or other financial relief described in this document. Identify amounts, who seeks or owes them, the stated basis, and page references. Do not invent missing amounts.",
  page_map:
    "Create a professional page map for this document. Group consecutive pages by their function or subject when sensible, and identify the principal topic, section, claim, clause, exhibit, or issue covered by each page range. Keep the map concise but useful for navigating the document.",
  obligations:
    "Identify the material obligations, duties, covenants, conditions, deliverables, payment duties, notice duties, and compliance requirements in this document. State who owes each obligation, any timing or conditions, and relevant page references.",
  risks:
    "Identify the material legal and practical risks visible in this document. Separate clear textual risks from issues that depend on missing facts or outside law. Point to the clauses or pages creating each risk and avoid overstating uncertainty.",
  termination:
    "Analyze every termination, cancellation, expiration, renewal, survival, default, cure, and notice provision in this document. Explain triggers, timing, consequences, and relevant page references.",
  holding:
    "Identify the court or decision-maker's holding, disposition, and controlling conclusions in this document. Distinguish the holding from background, arguments, dicta, or procedural history, and include page references.",
  orders:
    "Identify every operative order, command, prohibition, permission, deadline, and required next step imposed by this document. Explain who must act, what must be done, and by when, with page references.",
  issues:
    "Identify and organize the principal legal, factual, procedural, and evidentiary issues raised by this document. Explain why each issue matters and cite the relevant pages.",
  arguments:
    "Identify the principal arguments made in this document, who makes each argument, the factual or legal support offered, and the requested result. Include page references and distinguish argument from fact.",
  authorities:
    "Identify the statutes, regulations, cases, rules, contractual provisions, and other authorities expressly cited or relied on in this document. Do not invent authorities that are not present. Include page references.",
  requirements:
    "Explain exactly what this document requires the recipient or affected party to do. Identify requested information or evidence, deadlines, required forms or responses, consequences stated in the document, and page references.",
  next_steps:
    "Based only on what this document states, organize the apparent response steps or procedural next steps. Clearly separate explicit requirements from practical suggestions that would need legal verification. Include page references.",
  relief:
    "Identify every form of relief, remedy, order, declaration, injunction, damages request, fee request, or other outcome sought in this document, who seeks it, and where it appears.",
};


function normalizeDocumentIntelligenceProjection(value) {
  const source = value && typeof value === "object" ? value : null;
  if (!source) return null;

  const documentId = source.documentId || source.document_id || null;
  const status =
    source.status ||
    source.documentStatus ||
    source.document_status ||
    null;

  const rawActions = Array.isArray(source.documentActions)
    ? source.documentActions
    : Array.isArray(source.document_actions)
    ? source.document_actions
    : [];

  // Main already returns frontend-safe actions. Keep a tiny compatibility
  // normalizer so older/snake_case projections still render through the
  // reusable DocumentIntelligence UI without moving DIS logic into that component.
  const documentActions = rawActions
    .map((rawAction, index) => {
      if (typeof rawAction === "string") {
        const id = rawAction.trim();
        return id
          ? {
              id,
              actionId: id,
              label: id.replace(/[_-]+/g, " "),
            }
          : null;
      }

      const action =
        rawAction && typeof rawAction === "object"
          ? rawAction
          : {};
      const id = String(
        action.id ||
          action.actionId ||
          action.action_id ||
          action.key ||
          action.action ||
          `action_${index}`
      ).trim();

      if (!id) return null;

      return {
        ...action,
        id,
        actionId: id,
        label: String(
          action.label ||
            action.title ||
            id.replace(/[_-]+/g, " ")
        ),
        pageNumbers: Array.isArray(action.pageNumbers)
          ? action.pageNumbers
          : Array.isArray(action.page_numbers)
          ? action.page_numbers
          : [],
        interactiveComposer:
          action.interactiveComposer ??
          action.interactive_composer ??
          false,
        pageMapAction:
          action.pageMapAction ??
          action.page_map_action ??
          false,
      };
    })
    .filter(Boolean);

  return {
    ...source,
    documentId: documentId ? String(documentId) : null,
    status: status ? String(status) : null,
    pageMap: Array.isArray(source.pageMap)
      ? source.pageMap
      : Array.isArray(source.page_map)
      ? source.page_map
      : [],
    documentActions,
    completionMessage: String(
      source.completionMessage ||
        source.completion_message ||
        ""
    ).trim(),
  };
}

function attachmentFieldsFromDocumentIntelligence(projection) {
  const source =
    projection && typeof projection === "object"
      ? projection
      : null;
  if (!source) return {};

  const out = {};

  if (source.documentId) out.documentId = String(source.documentId);
  if (source.status) out.documentStatus = String(source.status);
  if (source.documentType) out.documentType = source.documentType;
  if (
    source.documentTypeConfidence !== null &&
    source.documentTypeConfidence !== undefined
  ) {
    out.documentTypeConfidence = source.documentTypeConfidence;
  }

  if (
    source.pageCount !== null &&
    source.pageCount !== undefined &&
    Number.isFinite(Number(source.pageCount))
  ) {
    out.pageCount = Number(source.pageCount);
  }
  if (
    source.processedPageCount !== null &&
    source.processedPageCount !== undefined &&
    Number.isFinite(Number(source.processedPageCount))
  ) {
    out.processedPageCount = Number(source.processedPageCount);
  }

  if (typeof source.truncated === "boolean") {
    out.truncated = source.truncated;
  }
  if (
    source.truncationStartPage !== null &&
    source.truncationStartPage !== undefined &&
    Number.isFinite(Number(source.truncationStartPage))
  ) {
    out.truncationStartPage = Number(source.truncationStartPage);
  }
  if (
    source.truncationOmittedPageCount !== null &&
    source.truncationOmittedPageCount !== undefined &&
    Number.isFinite(Number(source.truncationOmittedPageCount))
  ) {
    out.truncationOmittedPageCount = Number(
      source.truncationOmittedPageCount
    );
  }

  if (source.language) out.language = String(source.language);

  if (
    source.warningCount !== null &&
    source.warningCount !== undefined &&
    Number.isFinite(Number(source.warningCount))
  ) {
    out.warningCount = Number(source.warningCount);
  }

  if (Array.isArray(source.pageMap)) {
    out.pageMap = source.pageMap;
  }
  if (Array.isArray(source.documentActions)) {
    out.documentActions = source.documentActions;
  }

  // Preserve original attachment naming/mime when already present; these are
  // only fallbacks from Main's safe public projection.
  if (source.fileName) out.projectedFileName = String(source.fileName);
  if (source.mimeType) out.projectedMimeType = String(source.mimeType);
  if (
    source.sizeBytes !== null &&
    source.sizeBytes !== undefined &&
    Number.isFinite(Number(source.sizeBytes))
  ) {
    out.projectedSizeBytes = Number(source.sizeBytes);
  }

  return out;
}

function buildDocumentActionPrompt(action, document) {
  const actionId = String(action?.id || action?.actionId || "").trim().toLowerCase();
  const label = String(action?.label || action?.title || actionId || "Document analysis").trim();
  const fileName = String(document?.name || document?.fileName || "the attached document");
  const instruction =
    DOCUMENT_ACTION_PROMPTS[actionId] ||
    `Perform the requested document analysis "${label}" for this document. Be precise, practical, and page-aware. Cite relevant page numbers whenever they can be determined, distinguish the document's statements from inference, and do not invent missing facts.`;

  return (
    `DOCUMENT INTELLIGENCE REQUEST\n` +
    `Document: ${fileName}\n` +
    `Requested analysis: ${label}\n\n` +
    `${instruction}\n\n` +
    `Use only the attached document and the existing conversation unless the user's request clearly requires outside research.`
  );
}

// ---------------- Toast (in-app, non-blocking) ----------------
function ExportToast({ toast, onClose }) {
  if (!toast?.open) return null;

  return (
    <div style={styles.toastWrap} className="no-print" aria-live="polite" aria-atomic="true">
      <div style={styles.toastCard} role="status">
        <div style={styles.toastTopRow}>
          <div style={styles.toastTitle}>{toast.title || "Preparing…"}</div>
          <button type="button" onClick={onClose} style={styles.toastCloseBtn} aria-label="Close">
            <FiX />
          </button>
        </div>

        {!!toast.message && <div style={styles.toastMessage}>{toast.message}</div>}

        <div style={styles.toastBottomRow}>
          {toast.showSpinner ? (
            <div style={styles.toastSpinnerRow}>
              <span style={styles.spinner} aria-hidden="true" />
              <span style={styles.toastHint}>{toast.hint || "Please wait…"}</span>
            </div>
          ) : (
            <div style={styles.toastHint}>{toast.hint || ""}</div>
          )}
        </div>
      </div>
    </div>
  );
}


function BillingNoticeDialog({ notice, onClose }) {
  if (!notice) return null;

  return (
    <div
      className="no-print"
      role="presentation"
      style={styles.billingDialogOverlay}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="asklawly-billing-dialog-title"
        aria-describedby="asklawly-billing-dialog-message"
        style={styles.billingDialogCard}
      >
        <div style={styles.billingDialogIcon} aria-hidden="true">
          <FiShield />
        </div>

        <div
          id="asklawly-billing-dialog-title"
          style={styles.billingDialogTitle}
        >
          {notice.title || "Account notice"}
        </div>

        <div
          id="asklawly-billing-dialog-message"
          style={styles.billingDialogMessage}
        >
          {notice.message || GENERIC_CHAT_ERROR_MESSAGE}
        </div>

        <div style={styles.billingDialogActions}>
          <button
            type="button"
            onClick={onClose}
            autoFocus
            style={styles.billingDialogPrimaryButton}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

const ChatInterface = ({ inputRef, activeChat, setActiveChat }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const { currentUser, billing, userType } = useContext(AuthContext);
  const planType = billing?.plan_type || "free";

  const [messages, setMessages] = useState([]);
  const { upgradeMessage, showUpgrade } = useQuota();
  const [input, setInput] = useState(localStorage.getItem("chat_input") || "");

  // ✅ ChatInterface owns the mode so direct follow-up choices respect the user's
  // current manual Auto/Research selection. ChatInput will consume these props.
  const [composerMode, setComposerMode] = useState(() => {
    try {
      return normalizeChatMode(localStorage.getItem(CHAT_MODE_STORAGE_KEY) || "auto");
    } catch {
      return "auto";
    }
  });

  // ✅ Open-text follow-up context. Clicking a Lexa follow-up no longer sends Lexa's
  // question back to Lexa; it arms the composer so the user's next message answers it.
  const [pendingFollowUp, setPendingFollowUp] = useState(null);

  // ✅ Document Intelligence state.
  // The reusable dock is discovered from Main's authenticated OWNER-WIDE catalog,
  // independent of the current chat thread. A chat session may reference/reuse a
  // document, but it does not own whether the persistent DI dock exists.
  // Message-derived documents are
  // retained only as attachment metadata fallbacks (for example the signed
  // original-file URL used by the chat's View file control).
  const [pendingDocumentContext, setPendingDocumentContext] = useState(null);
  const [activeDocumentId, setActiveDocumentId] = useState("");
  const [documentChatMode, setDocumentChatMode] = useState("auto");
  // Page/section focus is one-turn UI state. It is not persisted across
  // refresh/thread changes and is cleared after the focused Send succeeds.
  const [documentPageScope, setDocumentPageScope] = useState(null);
  const [hydratedDocumentSelectionScope, setHydratedDocumentSelectionScope] = useState("");
  const [documentActionState, setDocumentActionState] = useState({});
  const [authoritativeDocumentCatalog, setAuthoritativeDocumentCatalog] = useState([]);
  const [documentCatalogResolved, setDocumentCatalogResolved] = useState(false);
  const [documentCatalogRefreshKey, setDocumentCatalogRefreshKey] = useState(0);
  const [hiddenDocumentIds, setHiddenDocumentIds] = useState(() => new Set());
  // null preserves DocumentIntelligence's normal uncontrolled/default behavior.
  // File Vault handoff temporarily takes control so the right-side DI dock opens
  // as soon as the accepted document reaches ChatInterface.
  const [documentIntelligenceDockOpen, setDocumentIntelligenceDockOpen] = useState(null);

  // One request-scoped progress state for a newly sent DI file.
  // Percentages come directly from AttachmentController/Main's backend /status polling.
  // Backend progress messages remain internal; the card translates backend stages
  // into customer-facing copy and never calculates a competing "fake" percentage.
  const [documentProcessingState, setDocumentProcessingState] = useState(null);
  const documentCatalogRequestRef = useRef(0);
  const documentProcessingRequestRef = useRef(0);
  const documentProcessingReconnectRef = useRef({
    key: "",
    controller: null,
  });
  const documentSelectionScopeRef = useRef("");
  const fileVaultDocumentHandoffRef = useRef({
    key: "",
    sessionId: "",
    documentId: "",
    running: false,
  });
  const sendMessageRef = useRef(null);

  // ✅ Empty-state task router: hides as soon as user chooses chat or starts typing
  const [emptyRouterDismissed, setEmptyRouterDismissed] = useState(false);

  const [loading, setLoading] = useState(false);
  const [streamingText, setStreamingText] = useState("");

  // ✅ NEW: history loading flag (prevents empty-state flash on refresh/thread-switch)
  const [historyLoading, setHistoryLoading] = useState(false);

  // ✅ Older-history pagination is independent from the normal initial-history load.
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyNextCursor, setHistoryNextCursor] = useState("");
  const [historyLoadingPrevious, setHistoryLoadingPrevious] = useState(false);

  // ✅ Frontend-only ↓ control. No backend/cursor behavior is attached to it.
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);

  const chatScrollRef = useRef(null);
  const chatWindowRef = useRef(null);

  const inputContainerRef = useRef(null);
  const [topSpacerHeight, setTopSpacerHeight] = useState(0);

  // ⬇️ Live height of the sticky input (via ResizeObserver)
  const [bottomSpacerHeight, setBottomSpacerHeight] = useState(70);

  // ✅ Extra bottom inset for mobile toolbars / on-screen keyboard (visualViewport-aware)
  const [vvInset, setVvInset] = useState(0);

  // ⬇️ Narrow-screen detector (phones/tablets) — reserve space only here
  const [isNarrow, setIsNarrow] = useState(() => {
    try {
      return window.innerWidth <= 1024;
    } catch {
      return true;
    }
  });

  const [isPhone, setIsPhone] = useState(() => {
    try {
      return window.innerWidth <= 640;
    } catch {
      return true;
    }
  });

  // ✅ Desktop-only dynamic inset so the inner chat scrollbar never sits under the browser scrollbar
  const [desktopChatScrollbarInsetPx, setDesktopChatScrollbarInsetPx] = useState(DESKTOP_CHAT_SCROLLBAR_INSET_PX);

  const session_id = activeChat?.session_id || null;

  const documentProcessingRecoveryStorageKey = useMemo(() => {
    const ownerKey = String(
      currentUser?.uid ||
        currentUser?.email ||
        "owner"
    );

    return `${DI_PROCESSING_RECOVERY_STORAGE_PREFIX}:${ownerKey}`;
  }, [
    currentUser?.email,
    currentUser?.uid,
  ]);

  const persistDocumentProcessingRecovery = useCallback(
    ({
      documentId,
      sessionId: processingSessionId,
      fileName,
    } = {}) => {
      const id = String(documentId || "").trim();
      if (!id || !currentUser) return;

      try {
        localStorage.setItem(
          documentProcessingRecoveryStorageKey,
          JSON.stringify({
            documentId: id,
            sessionId: String(
              processingSessionId ||
                session_id ||
                ""
            ),
            fileName: String(
              fileName ||
                "Document"
            ),
            updatedAt: Date.now(),
          })
        );
      } catch {}
    },
    [
      currentUser,
      documentProcessingRecoveryStorageKey,
      session_id,
    ]
  );

  const clearDocumentProcessingRecovery = useCallback(
    (documentId = "") => {
      try {
        const raw = localStorage.getItem(
          documentProcessingRecoveryStorageKey
        );

        if (!raw) return;

        if (documentId) {
          try {
            const parsed = JSON.parse(raw);
            const storedId = String(
              parsed?.documentId ||
                ""
            ).trim();

            if (
              storedId &&
              storedId !==
                String(documentId)
            ) {
              return;
            }
          } catch {}
        }

        localStorage.removeItem(
          documentProcessingRecoveryStorageKey
        );
      } catch {}
    },
    [documentProcessingRecoveryStorageKey]
  );

  const applyLiveDocumentProcessingProgress = useCallback(
    ({
      requestId,
      processingSessionId,
      fileName,
      progressUpdate = {},
    } = {}) => {
      const stage = String(
        progressUpdate?.progressStage ||
          progressUpdate?.progress_stage ||
          progressUpdate?.stage ||
          "processing"
      ).trim();

      const percentRaw =
        progressUpdate?.progressPercent ??
        progressUpdate?.progress_percent ??
        progressUpdate?.progress ??
        null;

      const hasBackendPercent =
        percentRaw !== null &&
        percentRaw !== undefined &&
        String(percentRaw).trim() !== "";

      const parsedPercent = hasBackendPercent
        ? Number(percentRaw)
        : Number.NaN;

      const progressPercent = Number.isFinite(parsedPercent)
        ? Math.max(0, Math.min(100, parsedPercent))
        : null;

      const progressMessage = String(
        progressUpdate?.progressMessage ||
          progressUpdate?.progress_message ||
          ""
      ).trim();

      const progressDocumentId = String(
        progressUpdate?.documentId ||
          progressUpdate?.document_id ||
          ""
      ).trim();

      const safeFileName = String(
        progressUpdate?.fileName ||
          progressUpdate?.file_name ||
          fileName ||
          "Document"
      ).trim() || "Document";

      if (progressDocumentId) {
        persistDocumentProcessingRecovery({
          documentId: progressDocumentId,
          sessionId:
            processingSessionId ||
            session_id ||
            "",
          fileName: safeFileName,
        });
      }

      try {
        console.log("[ChatInterface][DI processing]", {
          stage: stage || "processing",
          progress: progressPercent,
          message: progressMessage || null,
          documentId: progressDocumentId || null,
        });
      } catch {}

      setDocumentProcessingState((previous) => {
        if (
          !previous ||
          previous.requestId !== requestId
        ) {
          return previous;
        }

        return {
          ...previous,
          documentId:
            progressDocumentId ||
            previous.documentId ||
            null,
          fileName:
            safeFileName ||
            previous.fileName ||
            "Document",
          progressStage:
            stage ||
            previous.progressStage ||
            "processing",
          progressPercent:
            progressPercent !== null
              ? progressPercent
              : previous.progressPercent,
          progressMessage:
            progressMessage ||
            previous.progressMessage ||
            "",
        };
      });

      return {
        stage,
        progressPercent,
        progressMessage,
        progressDocumentId,
        fileName: safeFileName,
      };
    },
    [
      persistDocumentProcessingRecovery,
      session_id,
    ]
  );

  const messageDocumentFallbacks = useMemo(() => {
    const byId = new Map();

    (messages || []).forEach((msg, index) => {
      if (!msg || msg.user === "Lexa" || msg.user === "System") return;
      const doc = buildDocumentFromMessage(msg, index);
      if (!doc) return;

      const existing = byId.get(doc.id);
      if (!existing) {
        byId.set(doc.id, doc);
        return;
      }

      // Prefer the original visible upload over a later reused-document turn,
      // while retaining richer worker metadata when it appears later.
      const existingHidden = !!existing?.attachment?.displayHidden;
      const incomingHidden = !!doc?.attachment?.displayHidden;
      const preferred = existingHidden && !incomingHidden ? doc : existing;

      byId.set(doc.id, {
        ...preferred,
        pageCount:
          Math.max(
            Number(existing.pageCount || 0),
            Number(doc.pageCount || 0)
          ) || preferred.pageCount,
        pageMap: doc.pageMap?.length ? doc.pageMap : existing.pageMap,
        actions: doc.actions?.length ? doc.actions : existing.actions,
      });
    });

    return Array.from(byId.values());
  }, [messages]);

  const documentIntelligenceDocuments = useMemo(() => {
    // Before the authoritative list resolves we deliberately mount no dock.
    // This prevents a stale transcript record from resurrecting a document that
    // Main has hidden through abandonment or user soft-delete.
    if (!documentCatalogResolved) return [];

    const fallbackById = new Map(
      messageDocumentFallbacks.map((doc) => [String(doc.id), doc])
    );

    return (authoritativeDocumentCatalog || [])
      .map((entry, index) => {
        const catalogDoc = buildDocumentFromCatalogEntry(entry, index);
        if (!catalogDoc) return null;
        if (hiddenDocumentIds.has(catalogDoc.id)) return null;

        const fallback = fallbackById.get(catalogDoc.id);
        if (!fallback) {
          return {
            ...catalogDoc,
            attachment: {
              documentId: catalogDoc.id,
              fileName: catalogDoc.fileName,
              documentStatus: catalogDoc.status,
              documentType: catalogDoc.type,
              pageCount: catalogDoc.pageCount,
              attachmentMode: "chat_document_intelligence",
            },
          };
        }

        return {
          ...fallback,
          ...catalogDoc,
          attachment: {
            ...(fallback.attachment || {}),
            documentId: catalogDoc.id,
            fileName:
              catalogDoc.fileName ||
              fallback.attachment?.fileName ||
              fallback.fileName,
            documentStatus: catalogDoc.status,
            documentType: catalogDoc.type,
            pageCount: catalogDoc.pageCount ?? fallback.pageCount ?? null,
          },
        };
      })
      .filter(Boolean);
  }, [
    authoritativeDocumentCatalog,
    documentCatalogResolved,
    hiddenDocumentIds,
    messageDocumentFallbacks,
  ]);

  const chatEligibleDocumentIntelligenceDocuments = useMemo(
    () =>
      (documentIntelligenceDocuments || []).filter(
        (document) => isDocumentChatEligible(document)
      ),
    [documentIntelligenceDocuments]
  );

  const activeDocumentPageScope = useMemo(() => {
    const normalized = normalizeDocumentPageScope(documentPageScope);
    if (!normalized) return null;

    const scopedDocumentId = String(
      normalized.documentId ||
        documentPageScope?.documentId ||
        documentPageScope?.document_id ||
        ""
    ).trim();

    if (
      scopedDocumentId &&
      String(activeDocumentId || "") !== scopedDocumentId
    ) {
      return null;
    }

    return {
      ...normalized,
      documentId:
        scopedDocumentId ||
        String(activeDocumentId || ""),
    };
  }, [
    activeDocumentId,
    documentPageScope,
  ]);

  const documentSelectionScope = useMemo(() => {
    const ownerKey = String(
      currentUser?.uid ||
        currentUser?.email ||
        "owner"
    );
    return `${ownerKey}:${session_id || "draft"}`;
  }, [
    currentUser?.email,
    currentUser?.uid,
    session_id,
  ]);

  const documentSelectionStorageKey = useMemo(
    () =>
      `${DI_THREAD_SELECTION_STORAGE_PREFIX}:${documentSelectionScope}`,
    [documentSelectionScope]
  );

  const documentChatModeStorageKey = useMemo(
    () =>
      `${DI_CHAT_MODE_STORAGE_PREFIX}:${documentSelectionScope}`,
    [documentSelectionScope]
  );

  const persistDocumentChatMode = useCallback(
    (nextMode, explicitSessionId = undefined) => {
      if (!currentUser) return;

      const normalizedMode = normalizeDocumentChatMode(nextMode);
      const ownerKey = String(
        currentUser?.uid ||
          currentUser?.email ||
          "owner"
      );
      const threadKey = String(
        explicitSessionId !== undefined
          ? explicitSessionId || "draft"
          : session_id || "draft"
      );

      // A draft has no durable chat identity yet. Keep the live selection in
      // React state and persist it once Main creates the real chat session.
      if (threadKey === "draft") return;

      try {
        localStorage.setItem(
          `${DI_CHAT_MODE_STORAGE_PREFIX}:${ownerKey}:${threadKey}`,
          normalizedMode
        );
      } catch {}
    },
    [
      currentUser,
      session_id,
    ]
  );

  const handleDocumentChatModeChange = useCallback(
    (nextMode) => {
      const normalizedMode = normalizeDocumentChatMode(nextMode);
      setDocumentChatMode(normalizedMode);
      persistDocumentChatMode(normalizedMode);

      if (normalizedMode === "off") {
        // Off means completely unavailable to Chat. Remove any one-turn focus
        // that could otherwise revive the document on the next Send.
        setDocumentPageScope(null);
        setPendingDocumentContext(null);
        try {
          window.dispatchEvent(
            new CustomEvent("asklawly_document_context_clear")
          );
        } catch {}
      }
    },
    [persistDocumentChatMode]
  );

  useEffect(() => {
    if (!currentUser) {
      setDocumentChatMode("auto");
      setDocumentPageScope(null);
      return;
    }

    // New/draft chat always starts safely in Auto. If the user switches to
    // Always Use or Off before the first Send, that live state is copied to the
    // newly created session below.
    if (!session_id) {
      setDocumentChatMode("auto");
      return;
    }

    try {
      setDocumentChatMode(
        normalizeDocumentChatMode(
          localStorage.getItem(documentChatModeStorageKey) || "auto"
        )
      );
    } catch {
      setDocumentChatMode("auto");
    }
  }, [
    currentUser,
    documentChatModeStorageKey,
    session_id,
  ]);

  const persistDocumentSelection = useCallback(
    (documentId, explicitSessionId = undefined) => {
      const id = String(documentId || "").trim();
      if (!id || !currentUser) return;

      const ownerKey = String(
        currentUser?.uid ||
          currentUser?.email ||
          "owner"
      );
      const threadKey = String(
        explicitSessionId !== undefined
          ? explicitSessionId || "draft"
          : session_id || "draft"
      );

      try {
        localStorage.setItem(
          `${DI_THREAD_SELECTION_STORAGE_PREFIX}:${ownerKey}:${threadKey}`,
          id
        );
      } catch {}
    },
    [
      currentUser,
      session_id,
    ]
  );

  const clearPersistedDocumentSelectionsForId = useCallback(
    (documentId) => {
      const id = String(documentId || "").trim();
      if (!id || !currentUser) return;

      const ownerKey = String(
        currentUser?.uid ||
          currentUser?.email ||
          "owner"
      );
      const prefix =
        `${DI_THREAD_SELECTION_STORAGE_PREFIX}:${ownerKey}:`;

      try {
        for (
          let index = localStorage.length - 1;
          index >= 0;
          index -= 1
        ) {
          const key = localStorage.key(index);
          if (!key || !key.startsWith(prefix)) continue;

          const storedId = String(
            localStorage.getItem(key) || ""
          ).trim();

          if (storedId === id) {
            localStorage.removeItem(key);
          }
        }
      } catch {}
    },
    [
      currentUser,
      currentUser?.email,
      currentUser?.uid,
    ]
  );

  const dropDocumentFromChatState = useCallback(
    (
      documentId,
      {
        refreshCatalog = false,
      } = {}
    ) => {
      const id = String(documentId || "").trim();
      if (!id) return;

      // A missing/deleted/abandoned document must never keep a reconnecting
      // "processing" card alive in Chat.
      clearDocumentProcessingRecovery(id);
      clearPersistedDocumentSelectionsForId(id);

      const reconnectKey = String(
        documentProcessingReconnectRef.current?.key ||
          ""
      );

      if (
        reconnectKey === id ||
        reconnectKey.startsWith(`${id}:`)
      ) {
        try {
          documentProcessingReconnectRef.current
            .controller?.abort?.();
        } catch {}

        documentProcessingReconnectRef.current = {
          key: "",
          controller: null,
        };
      }

      documentProcessingRequestRef.current += 1;

      setDocumentProcessingState((previous) =>
        String(previous?.documentId || "") === id
          ? null
          : previous
      );

      setDocumentPageScope((current) =>
        String(
          current?.documentId ||
            current?.document_id ||
            ""
        ) === id
          ? null
          : current
      );

      if (
        String(
          pendingDocumentContext?.document?.id ||
            pendingDocumentContext?.document?.documentId ||
            ""
        ) === id
      ) {
        setPendingDocumentContext(null);
        try {
          window.dispatchEvent(
            new CustomEvent(
              "asklawly_document_context_clear"
            )
          );
        } catch {}
      }

      setActiveDocumentId((current) =>
        String(current || "") === id
          ? ""
          : current
      );

      if (refreshCatalog) {
        setDocumentCatalogRefreshKey(
          (value) => value + 1
        );
      }
    },
    [
      clearDocumentProcessingRecovery,
      clearPersistedDocumentSelectionsForId,
      pendingDocumentContext?.document?.documentId,
      pendingDocumentContext?.document?.id,
    ]
  );

  const selectDocumentForCurrentThread = useCallback(
    (documentId, { persist = true } = {}) => {
      const id = String(documentId || "").trim();
      if (!id) return;

      if (
        activeDocumentId &&
        String(activeDocumentId) !== id
      ) {
        setDocumentPageScope(null);

        if (
          pendingDocumentContext?.document?.id &&
          String(pendingDocumentContext.document.id) !== id
        ) {
          setPendingDocumentContext(null);
          try {
            window.dispatchEvent(
              new CustomEvent("asklawly_document_context_clear")
            );
          } catch {}
        }
      }

      setActiveDocumentId(id);
      if (persist) {
        persistDocumentSelection(id);
      }
    },
    [
      activeDocumentId,
      pendingDocumentContext?.document?.id,
      persistDocumentSelection,
    ]
  );

  useEffect(() => {
    if (!documentCatalogResolved || !currentUser) return;

    const scopeChanged =
      documentSelectionScopeRef.current !==
      documentSelectionScope;

    const readRememberedSelection = () => {
      try {
        return String(
          localStorage.getItem(
            documentSelectionStorageKey
          ) || ""
        ).trim();
      } catch {
        return "";
      }
    };

    const isChatEligibleDocument = (documentId) =>
      !!documentId &&
      chatEligibleDocumentIntelligenceDocuments.some(
        (doc) =>
          String(doc?.id || "") ===
          String(documentId)
      );

    if (scopeChanged) {
      documentSelectionScopeRef.current =
        documentSelectionScope;

      const rememberedId =
        readRememberedSelection();
      const activeFileVaultHandoffId = String(
        fileVaultDocumentHandoffRef.current?.documentId || ""
      ).trim();
      const rememberedVisible =
        isChatEligibleDocument(rememberedId) ||
        (!!rememberedId && rememberedId === activeFileVaultHandoffId);

      const nextId = rememberedVisible
        ? rememberedId
        : String(
            chatEligibleDocumentIntelligenceDocuments[0]?.id ||
              ""
          );

      setActiveDocumentId(nextId);
      setHydratedDocumentSelectionScope(
        documentSelectionScope
      );

      if (
        rememberedId &&
        !rememberedVisible
      ) {
        try {
          localStorage.removeItem(
            documentSelectionStorageKey
          );
        } catch {}
      }
      return;
    }

    if (!chatEligibleDocumentIntelligenceDocuments.length) {
      const activeFileVaultHandoffId = String(
        fileVaultDocumentHandoffRef.current?.documentId || ""
      ).trim();

      // A File Vault document is intentionally created in the pre-commit
      // "uploading" state. Do not let the ordinary catalog-eligibility cleanup
      // erase that exact selection while ChatInterface is taking ownership of
      // /commit. Once Main advances the document, normal catalog eligibility
      // takes over again.
      if (activeFileVaultHandoffId) {
        if (
          String(activeDocumentId || "") !==
          activeFileVaultHandoffId
        ) {
          setActiveDocumentId(
            activeFileVaultHandoffId
          );
        }

        if (
          hydratedDocumentSelectionScope !==
          documentSelectionScope
        ) {
          setHydratedDocumentSelectionScope(
            documentSelectionScope
          );
        }

        return;
      }

      if (activeDocumentId) {
        setActiveDocumentId("");
      }
      if (
        hydratedDocumentSelectionScope !==
        documentSelectionScope
      ) {
        setHydratedDocumentSelectionScope(
          documentSelectionScope
        );
      }
      try {
        localStorage.removeItem(
          documentSelectionStorageKey
        );
      } catch {}
      return;
    }

    if (isChatEligibleDocument(activeDocumentId)) {
      return;
    }

    const rememberedId =
      readRememberedSelection();
    const activeFileVaultHandoffId = String(
      fileVaultDocumentHandoffRef.current?.documentId || ""
    ).trim();
    const rememberedVisible =
      isChatEligibleDocument(rememberedId) ||
      (!!rememberedId && rememberedId === activeFileVaultHandoffId);

    const fallbackId = rememberedVisible
      ? rememberedId
      : String(
          chatEligibleDocumentIntelligenceDocuments[0]?.id ||
            ""
        );

    setActiveDocumentId(fallbackId);

    if (
      rememberedId &&
      !rememberedVisible
    ) {
      try {
        localStorage.removeItem(
          documentSelectionStorageKey
        );
      } catch {}
    }
  }, [
    activeDocumentId,
    currentUser,
    documentCatalogResolved,
    chatEligibleDocumentIntelligenceDocuments,
    documentSelectionScope,
    documentSelectionStorageKey,
    hydratedDocumentSelectionScope,
  ]);

  // ✅ Persist active chat for refresh (but do NOT auto-restore after user intentionally clears it)
  const triedRestoreRef = useRef(false);

  // ✅ Scroll control: prevent landing auto-scroll; keep during active chat
  const historyJustLoadedRef = useRef(false);
  const didUserSendRef = useRef(false);
  const pendingScrollToBottomRef = useRef(false);
  const lastMsgCountRef = useRef(0);
  const lastStreamingRef = useRef("");

  // ✅ Track user scroll position only; never use it to force-follow streaming
  const atBottomRef = useRef(true);
  const rafScrollRef = useRef(0);

  // ✅ Guard timers (ONLY for empty-state + thinking flash suppression)
  const emptySuppressUntilRef = useRef(0);
  const thinkingSuppressUntilRef = useRef(0);
  const [, forceGuardTick] = useState(0); // forces re-render when a guard window expires

  // ✅ NEW: Option A (deterministic) expand/collapse for multipage attachments (per message index)
  const [expandedAttachments, setExpandedAttachments] = useState({});

  // ✅ Thinking robustness (request-scoped)
  const requestIdRef = useRef(0);
  const thinkingLockedUntilRef = useRef(0);
  const gotAnyOutputForRequestRef = useRef(false);

  // ✅ NEW: track which session the current UI messages belong to
  const uiSessionIdRef = useRef(null);

  // ✅ NEW: prevent repeated history fetches for same sid in a tight loop
  const lastHistoryFetchSidRef = useRef(null);

  // ✅ NEW: protects against in-flight history overwriting optimistic UI (fixes “AI first reply missing until refresh”)
  const currentMessagesLenRef = useRef(0);
  const historyFetchTokenRef = useRef(0);

  // When older messages are prepended, preserve the exact visible reading
  // position instead of jumping to the new top or chasing the bottom.
  const historyPrependScrollRestoreRef = useRef(null);

  // ---------------- CHAT ATTACHMENT FILE VIEWER ----------------
  // "View file" opens the original uploaded attachment inside the shared
  // FileViewerModal. It never substitutes a DI canonical/generated artifact.
  const [attachmentViewerRecord, setAttachmentViewerRecord] = useState(null);

  // ---------------- CHAT TRANSCRIPT PDF VIEWER ----------------
  // ChatInterface owns transcript generation + modal state.
  // AttachmentMenu/ChatInput only forward the user's "Chat transcript" action.
  const [exporting, setExporting] = useState(false);
  const [transcriptRecord, setTranscriptRecord] = useState(null);
  const transcriptObjectUrlRef = useRef(null);

  const [toast, setToast] = useState({
    open: false,
    title: "",
    message: "",
    hint: "",
    showSpinner: false,
  });

  // Billing/entitlement denials use a centered in-app dialog. The same friendly
  // message is also inserted into the chat thread as a System message.
  const [billingNotice, setBillingNotice] = useState(null);

  const toastTimerRef = useRef(null);
  const closeToast = useCallback(() => {
    try {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
        toastTimerRef.current = null;
      }
    } catch {}
    setToast((prev) => ({ ...(prev || {}), open: false }));
  }, []);


  const closeBillingNotice = useCallback(() => {
    setBillingNotice(null);
  }, []);

  useEffect(() => {
    if (!billingNotice) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setBillingNotice(null);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [billingNotice]);

  const showToast = useCallback((next, { autoCloseMs = 0 } = {}) => {
    setToast((prev) => ({ ...(prev || {}), ...next, open: true }));
    try {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
        toastTimerRef.current = null;
      }
      if (autoCloseMs && autoCloseMs > 0) {
        toastTimerRef.current = setTimeout(() => {
          toastTimerRef.current = null;
          setToast((p) => ({ ...(p || {}), open: false }));
        }, autoCloseMs);
      }
    } catch {}
  }, []);

  const closeAttachmentViewer = useCallback(() => {
    setAttachmentViewerRecord(null);
  }, []);

  const openChatAttachmentViewer = useCallback(
    (attachment, requestedUrl = "", pageNumber = 1) => {
      const record = buildChatAttachmentViewerRecord(
        attachment,
        requestedUrl,
        pageNumber
      );

      if (!record) {
        showToast(
          {
            title: "File unavailable",
            message: "This file is not available to preview right now.",
            hint: "",
            showSpinner: false,
          },
          { autoCloseMs: 3500 }
        );
        return;
      }

      setAttachmentViewerRecord(record);
    },
    [showToast]
  );

  const revokeTranscriptObjectUrl = useCallback(() => {
    const url = transcriptObjectUrlRef.current;
    transcriptObjectUrlRef.current = null;
    if (!url) return;

    try {
      window.URL.revokeObjectURL(url);
    } catch {}
  }, []);

  const closeTranscriptViewer = useCallback(() => {
    const url = transcriptObjectUrlRef.current;
    transcriptObjectUrlRef.current = null;
    setTranscriptRecord(null);

    // Let PdfViewer unmount before revoking the Blob URL it was rendering.
    if (url) {
      setTimeout(() => {
        try {
          window.URL.revokeObjectURL(url);
        } catch {}
      }, 100);
    }
  }, []);

  useEffect(() => {
    return () => {
      try {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      } catch {}
      revokeTranscriptObjectUrl();
    };
  }, [revokeTranscriptObjectUrl]);

  useEffect(() => {
    currentMessagesLenRef.current = messages.length || 0;
  }, [messages.length]);

  useEffect(() => {
    try {
      localStorage.setItem(CHAT_MODE_STORAGE_KEY, normalizeChatMode(composerMode));
    } catch {}
  }, [composerMode]);

  const getDistanceFromLatest = () => {
    const el = chatWindowRef.current;
    if (!el) return 0;

    const scrollTop = el.scrollTop || 0;
    const clientH = el.clientHeight || 0;
    const scrollH = el.scrollHeight || 0;

    return Math.max(0, scrollH - (scrollTop + clientH));
  };

  const syncScrollNavigationState = () => {
    const distance = getDistanceFromLatest();
    atBottomRef.current = distance <= 8;
    setShowJumpToLatest(
      distance > CHAT_JUMP_TO_LATEST_DISTANCE_PX
    );
  };

  // Existing response-anchor behavior stays immediate/deterministic.
  const scrollToBottom = () => {
    const el = chatWindowRef.current;
    if (!el) return;
    try {
      el.scrollTop = el.scrollHeight;
    } catch {}

    atBottomRef.current = true;
    setShowJumpToLatest(false);
  };

  // Manual ↓ control may animate; it is completely frontend-only.
  const jumpToLatest = () => {
    const el = chatWindowRef.current;
    if (!el) return;

    try {
      el.scrollTo({
        top: el.scrollHeight,
        behavior: "smooth",
      });
    } catch {
      scrollToBottom();
      return;
    }

    setShowJumpToLatest(false);
  };

  // -----------------------------------------
  // ✅ VisualViewport listener (bottom inset only)
  // -----------------------------------------
  useEffect(() => {
    const vv = window.visualViewport;

    const computeInset = () => {
      try {
        const h = window.innerHeight || 0;
        const vvH = vv?.height || h;
        const vvTop = vv?.offsetTop || 0;

        // Keyboard / toolbar overlap in px
        const raw = Math.max(0, Math.round(h - (vvH + vvTop)));
        const clamped = Math.min(raw, 400);
        setVvInset(clamped);
      } catch {
        setVvInset(0);
      }
    };

    computeInset();

    if (!vv) {
      const onResize = () => computeInset();
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
    }

    vv.addEventListener("resize", computeInset);
    vv.addEventListener("scroll", computeInset);
    window.addEventListener("orientationchange", computeInset);

    return () => {
      vv.removeEventListener("resize", computeInset);
      vv.removeEventListener("scroll", computeInset);
      window.removeEventListener("orientationchange", computeInset);
    };
  }, []);

  // ✅ Desktop-only: dynamically compute the page scrollbar width and inset the chat scroller by that amount (+ buffer)
  useEffect(() => {
    if (isNarrow) return;

    const computeDesktopInset = () => {
      try {
        const pageScrollbarWidth = Math.max(0, (window.innerWidth || 0) - (document.documentElement?.clientWidth || 0));
        const buffer = 2; // minimal separation so chat scrollbar never touches the browser scrollbar
        const inset = Math.max(buffer, pageScrollbarWidth + buffer);
        setDesktopChatScrollbarInsetPx(inset);
      } catch {
        setDesktopChatScrollbarInsetPx(DESKTOP_CHAT_SCROLLBAR_INSET_PX);
      }
    };

    computeDesktopInset();
    window.addEventListener("resize", computeDesktopInset);
    return () => window.removeEventListener("resize", computeDesktopInset);
  }, [isNarrow]);

  // Measure top bar and keep bottom reserve equal to real input height
  useEffect(() => {
    const computeTopSpacer = () => {
      try {
        const narrowNow = window.innerWidth <= 1024;
        const topBar = document.querySelector("#top-bar");

        if (!topBar) {
          setTopSpacerHeight(0);
          return;
        }

        // ✅ Match VisionTranscriptPage behavior:
        // Only add spacing when the top bar overlays content (fixed/sticky).
        const cs = window.getComputedStyle(topBar);
        const overlays = cs?.position === "fixed" || cs?.position === "sticky";

        const extra = narrowNow ? TOP_BAR_EXTRA_NARROW_PX : TOP_BAR_EXTRA_WIDE_PX;
        setTopSpacerHeight(overlays ? topBar.offsetHeight + extra : 0);
      } catch {
        setTopSpacerHeight(0);
      }
    };

    computeTopSpacer();

    const el = inputContainerRef.current;
    if (!el) return;

    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const h = entry.contentRect?.height || el.offsetHeight || 0;
        const extra = 12; // breathing room
        setBottomSpacerHeight(Math.ceil(h + extra));
      }
    });
    ro.observe(el);

    const onResize = () => {
      try {
        setIsNarrow(window.innerWidth <= 1024);
        setIsPhone(window.innerWidth <= 640);
      } catch {}
      computeTopSpacer(); // ✅ keep top spacer in sync with breakpoint + top bar height
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  // ✅ Track scroll position without forcing scroll.
  // The ↓ button is derived only from distance to the latest message; it does
  // not read or mutate backend pagination state.
  useEffect(() => {
    const el = chatWindowRef.current;
    if (!el) return;

    const onScroll = () => {
      if (rafScrollRef.current) return;
      rafScrollRef.current = requestAnimationFrame(() => {
        rafScrollRef.current = 0;
        syncScrollNavigationState();
      });
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    syncScrollNavigationState();

    return () => {
      el.removeEventListener("scroll", onScroll);
      if (rafScrollRef.current) cancelAnimationFrame(rafScrollRef.current);
      rafScrollRef.current = 0;
    };
  }, []);

  // ✅ Save active chat to localStorage (for refresh persistence)
  useEffect(() => {
    try {
      if (activeChat?.session_id) {
        const toStore = {
          session_id: activeChat.session_id,
          session_name: activeChat.session_name || null,
        };
        localStorage.setItem(ACTIVE_CHAT_STORAGE_KEY, JSON.stringify(toStore));
      }
    } catch {}
  }, [activeChat?.session_id, activeChat?.session_name]);

  // ✅ Listen: "New Chat" signal from Sidebar/Layout → clear immediately + prevent restore snap-back
  useEffect(() => {
    const onNewChat = () => {
      try {
        triedRestoreRef.current = true; // prevent restore this mount
        uiSessionIdRef.current = null;
        lastHistoryFetchSidRef.current = null;

        // ✅ reset send intent too
        didUserSendRef.current = false;

        // ✅ invalidate in-flight history so it can't overwrite anything
        historyFetchTokenRef.current += 1;

        setMessages([]);
        setInput("");
        setPendingFollowUp(null);
        setPendingDocumentContext(null);
        setDocumentChatMode("auto");
        setDocumentPageScope(null);
        setDocumentActionState({});
        setDocumentIntelligenceDockOpen(null);
        fileVaultDocumentHandoffRef.current = {
          key: "",
          sessionId: "",
          documentId: "",
          running: false,
        };

        try {
          const ownerKey = String(
            currentUser?.uid ||
              currentUser?.email ||
              "owner"
          );
          localStorage.removeItem(
            `${DI_THREAD_SELECTION_STORAGE_PREFIX}:${ownerKey}:draft`
          );
          localStorage.removeItem(
            `${DI_CHAT_MODE_STORAGE_PREFIX}:${ownerKey}:draft`
          );
        } catch {}
        documentSelectionScopeRef.current = "";
        setHydratedDocumentSelectionScope("");

        setDocumentProcessingState(null);
        documentProcessingRequestRef.current += 1;
        try {
          documentProcessingReconnectRef.current
            .controller?.abort?.();
        } catch {}
        documentProcessingReconnectRef.current = {
          key: "",
          controller: null,
        };
        setEmptyRouterDismissed(false);
        setStreamingText("");
        setLoading(false);
        setHistoryLoading(false);
        setHistoryHasMore(false);
        setHistoryNextCursor("");
        setHistoryLoadingPrevious(false);
        setShowJumpToLatest(false);
        historyPrependScrollRestoreRef.current = null;
        try {
          localStorage.removeItem("chat_input");
          localStorage.removeItem(ACTIVE_CHAT_STORAGE_KEY);
          localStorage.setItem(SUPPRESS_RESTORE_KEY, String(Date.now() + 15000)); // 15s
        } catch {}
        if (typeof setActiveChat === "function") setActiveChat(null);
      } catch {}
    };

    window.addEventListener("asklawly_new_chat", onNewChat);
    return () => window.removeEventListener("asklawly_new_chat", onNewChat);
  }, [
    currentUser?.email,
    currentUser?.uid,
    setActiveChat,
  ]);

  // ✅ On first mount only (BEFORE PAINT): if no activeChat, try restore from localStorage (refresh case)
  useLayoutEffect(() => {
    if (activeChat?.session_id) return;

    // Only attempt restore once per mount (so New Chat → null won't re-restore)
    if (triedRestoreRef.current) return;
    triedRestoreRef.current = true;

    try {
      const suppressUntil = parseInt(localStorage.getItem(SUPPRESS_RESTORE_KEY) || "0", 10);
      if (Number.isFinite(suppressUntil) && suppressUntil > Date.now()) return;

      const raw = localStorage.getItem(ACTIVE_CHAT_STORAGE_KEY);
      if (!raw) return;

      const parsed = JSON.parse(raw);
      const sid = parsed?.session_id;
      if (sid && typeof setActiveChat === "function") {
        // Restore minimal activeChat object so history can load
        setActiveChat({
          session_id: sid,
          session_name: parsed?.session_name || null,
        });
      }
    } catch {}
  }, [activeChat?.session_id, setActiveChat]);

  // ✅ When active chat is cleared AFTER initial restore attempt, treat as intentional (New Chat)
  useEffect(() => {
    if (!activeChat?.session_id && triedRestoreRef.current) {
      uiSessionIdRef.current = null; // ✅ reset UI session ownership
      lastHistoryFetchSidRef.current = null;

      // ✅ reset send intent (prevents “pending first message” guard from blocking clears)
      didUserSendRef.current = false;

      // ✅ invalidate in-flight history so it can't overwrite anything
      historyFetchTokenRef.current += 1;

      setMessages([]);
      setInput("");
      setPendingFollowUp(null);
      setPendingDocumentContext(null);
      setDocumentPageScope(null);
      setDocumentActionState({});
      setDocumentIntelligenceDockOpen(null);
      fileVaultDocumentHandoffRef.current = {
        key: "",
        sessionId: "",
        documentId: "",
        running: false,
      };
      documentSelectionScopeRef.current = "";
      setHydratedDocumentSelectionScope("");
      // Keep the authenticated owner's DI catalog mounted with no active thread.
      setDocumentProcessingState(null);
      documentProcessingRequestRef.current += 1;
      setEmptyRouterDismissed(false);
      setStreamingText("");
      setHistoryLoading(false); // ✅ stop history loading for new chat
      setHistoryHasMore(false);
      setHistoryNextCursor("");
      setHistoryLoadingPrevious(false);
      setShowJumpToLatest(false);
      historyPrependScrollRestoreRef.current = null;
      localStorage.removeItem("chat_input");
      localStorage.removeItem(ACTIVE_CHAT_STORAGE_KEY);
    }
  }, [activeChat?.session_id]);

  const getChatTitle = () => activeChat?.session_name || `Chat on ${new Date().toLocaleString()}`;

  const getAuthHeaders = async () => {
    if (!currentUser) return null;

    const token = await currentUser.getIdToken();
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  };

  const loadAuthoritativeDocumentCatalog = useCallback(
    async () => {
      const requestNumber = ++documentCatalogRequestRef.current;

      if (!currentUser) {
        setAuthoritativeDocumentCatalog([]);
        setDocumentCatalogResolved(false);
        return;
      }

      setDocumentCatalogResolved(false);

      try {
        const token = await currentUser.getIdToken();
        if (!token) throw new Error("Authentication not ready.");

        const params = new URLSearchParams();
        params.set("limit", "10");
        params.set("consumer", "chat");

        // No session_id filter here. The reusable DI dock belongs to the
        // authenticated owner and remains available across chat threads.
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/document-intelligence/documents?${params.toString()}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const payload = await res.json().catch(() => null);
        if (!res.ok) {
          throw new Error(
            payload?.message ||
              payload?.error ||
              "Unable to load Document Intelligence documents."
          );
        }

        if (requestNumber !== documentCatalogRequestRef.current) return;

        const documents = Array.isArray(payload?.documents)
          ? payload.documents
          : [];

        setAuthoritativeDocumentCatalog(documents);
        setDocumentCatalogResolved(true);
      } catch (error) {
        if (requestNumber !== documentCatalogRequestRef.current) return;

        console.warn(
          "[ChatInterface] Document Intelligence catalog unavailable:",
          error?.message || error
        );

        // Fail closed: transcript-derived metadata never becomes catalog authority.
        setAuthoritativeDocumentCatalog([]);
        setDocumentCatalogResolved(true);
      }
    },
    [currentUser]
  );

  useEffect(() => {
    if (!currentUser) {
      documentCatalogRequestRef.current += 1;
      setAuthoritativeDocumentCatalog([]);
      setDocumentCatalogResolved(false);
      return;
    }

    loadAuthoritativeDocumentCatalog();
  }, [
    currentUser,
    documentCatalogRefreshKey,
    loadAuthoritativeDocumentCatalog,
  ]);

  // ✅ Refresh / browser-restart recovery for a committed DI document.
  //
  // Live AttachmentController polling is request-local and disappears with the
  // page. Once the browser comes back, ChatInterface reconnects to Main's
  // authoritative /status route. No percentage is reconstructed locally.
  useEffect(() => {
    if (
      !currentUser ||
      !documentCatalogResolved
    ) {
      return;
    }

    // Never create a second watcher while the current page already owns a live
    // upload/processing request.
    if (
      documentProcessingState?.source ===
      "live"
    ) {
      return;
    }

    let storedRecovery = null;

    try {
      const raw = localStorage.getItem(
        documentProcessingRecoveryStorageKey
      );

      if (raw) {
        const parsed = JSON.parse(raw);
        const storedDocumentId = String(
          parsed?.documentId ||
            ""
        ).trim();

        if (storedDocumentId) {
          storedRecovery = {
            documentId:
              storedDocumentId,
            sessionId: String(
              parsed?.sessionId ||
                ""
            ).trim(),
            fileName: String(
              parsed?.fileName ||
                "Document"
            ).trim(),
          };
        }
      }
    } catch {}

    const catalogProcessingCandidates =
      (authoritativeDocumentCatalog || [])
        .map((entry) => ({
          entry,
          normalized:
            normalizeDocumentProcessingPayload(
              entry
            ),
        }))
        .filter(({ normalized }) =>
          !!normalized.documentId &&
          DI_PROCESSING_RECONNECTABLE_STATUSES.has(
            normalized.status
          )
        );

    let candidate = null;

    // Same-browser refresh: persisted documentId is the strongest identity,
    // but localStorage is never allowed to override Main's current lifecycle.
    if (storedRecovery?.documentId) {
      const storedCatalogMatch =
        catalogProcessingCandidates.find(
          ({ normalized }) =>
            normalized.documentId ===
            storedRecovery.documentId
        );

      const storedCatalogEntry =
        (authoritativeDocumentCatalog || []).find(
          (entry) =>
            String(
              entry?.documentId ||
                entry?.document_id ||
                entry?.id ||
                ""
            ).trim() ===
            storedRecovery.documentId
        );

      if (storedCatalogMatch) {
        candidate = {
          ...storedCatalogMatch.normalized,
          fileName:
            storedCatalogMatch.normalized.fileName ||
            storedRecovery.fileName ||
            "Document",
          sessionId:
            storedCatalogMatch.normalized.sessionId ||
            storedRecovery.sessionId ||
            "",
        };
      } else if (storedCatalogEntry) {
        // Main already knows this record and no longer classifies it as
        // reconnectable. Drop the stale browser recovery pointer immediately.
        clearDocumentProcessingRecovery(
          storedRecovery.documentId
        );
        storedRecovery = null;
      } else {
        // The owner catalog is paginated. If the persisted document is not on
        // the first page, verify it once against the strict Chat status route
        // rather than resurrecting it from cached state indefinitely.
        candidate = {
          documentId:
            storedRecovery.documentId,
          fileName:
            storedRecovery.fileName ||
            "Document",
          sessionId:
            storedRecovery.sessionId ||
            "",
          status: "processing",
          progressStage: "processing",
          progressPercent: null,
          progressMessage: "",
        };
      }
    }

    // Cross-device/browser fallback: use a processing catalog record only when
    // Main identifies it as belonging to the restored current chat session.
    if (!candidate && session_id) {
      const sessionMatch =
        catalogProcessingCandidates.find(
          ({ normalized }) =>
            normalized.sessionId &&
            normalized.sessionId ===
              String(session_id)
        );

      if (sessionMatch) {
        candidate =
          sessionMatch.normalized;
      }
    }

    // Existing transcript attachment identity is another safe thread-local
    // fallback after history has loaded.
    if (!candidate) {
      const messageDocumentIds =
        new Set(
          (messageDocumentFallbacks || [])
            .map((doc) =>
              String(
                doc?.id ||
                  doc?.documentId ||
                  ""
              ).trim()
            )
            .filter(Boolean)
        );

      const messageMatch =
        catalogProcessingCandidates.find(
          ({ normalized }) =>
            messageDocumentIds.has(
              normalized.documentId
            )
        );

      if (messageMatch) {
        candidate =
          messageMatch.normalized;
      }
    }

    if (!candidate?.documentId) {
      if (
        documentProcessingState?.source ===
        "recovered"
      ) {
        setDocumentProcessingState(null);
      }
      return;
    }

    // A stored pointer from another restored thread must not attach itself to
    // the wrong chat.
    if (
      candidate.sessionId &&
      session_id &&
      candidate.sessionId !==
        String(session_id)
    ) {
      return;
    }

    const reconnectKey = `${candidate.documentId}:${session_id || candidate.sessionId || "owner"}`;

    if (
      documentProcessingReconnectRef.current
        .key === reconnectKey
    ) {
      return;
    }

    try {
      documentProcessingReconnectRef.current
        .controller?.abort?.();
    } catch {}

    const controller =
      typeof AbortController !== "undefined"
        ? new AbortController()
        : null;

    documentProcessingReconnectRef.current = {
      key: reconnectKey,
      controller,
    };

    const recoveryRequestId =
      ++documentProcessingRequestRef.current;

    setDocumentProcessingState({
      requestId:
        recoveryRequestId,
      source: "recovered",
      documentId:
        candidate.documentId,
      fileName:
        candidate.fileName ||
        "Document",
      progressStage:
        candidate.progressStage ||
        candidate.status ||
        "processing",
      progressPercent:
        candidate.progressPercent,
      progressMessage:
        candidate.progressMessage ||
        "Document Intelligence is processing your file…",
    });

    persistDocumentProcessingRecovery({
      documentId:
        candidate.documentId,
      sessionId:
        candidate.sessionId ||
        session_id ||
        "",
      fileName:
        candidate.fileName ||
        "Document",
    });

    let cancelled = false;
    let consecutiveErrors = 0;

    const run = async () => {
      while (!cancelled) {
        try {
          const token =
            await currentUser.getIdToken();

          if (!token) {
            throw new Error(
              "Authentication unavailable."
            );
          }

          const res = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/chat/attachment/${encodeURIComponent(
              candidate.documentId
            )}/status`,
            {
              method: "GET",
              headers: {
                Authorization:
                  `Bearer ${token}`,
              },
              ...(controller
                ? {
                    signal:
                      controller.signal,
                  }
                : null),
            }
          );

          const payload =
            await res.json().catch(
              () => null
            );

          if (res.status === 404) {
            // Main's strict Chat visibility boundary intentionally returns
            // document_not_found for abandoned/deleted documents. That means
            // "drop from Chat now", not "keep the last cached processing card".
            dropDocumentFromChatState(
              candidate.documentId,
              {
                refreshCatalog: true,
              }
            );
            break;
          }

          if (!res.ok) {
            throw new Error(
              `Status request failed (${res.status}).`
            );
          }

          const latest =
            normalizeDocumentProcessingPayload(
              payload,
              candidate
            );

          consecutiveErrors = 0;

          const latestStatus =
            normalizeDocumentProcessingStatus(
              latest.status
            );

          if (
            DI_PROCESSING_TERMINAL_STATUSES.has(
              latestStatus
            )
          ) {
            clearDocumentProcessingRecovery(
              candidate.documentId
            );

            setDocumentProcessingState(
              (previous) =>
                previous?.source ===
                  "recovered" &&
                previous?.documentId ===
                  candidate.documentId
                  ? null
                  : previous
            );

            setDocumentCatalogRefreshKey(
              (value) => value + 1
            );

            break;
          }

          // Reserved/upload-only records mean the browser disappeared before
          // commit established a durable DI processing job. Do not pretend a
          // backend job is running in that case.
          if (
            latestStatus &&
            !DI_PROCESSING_RECONNECTABLE_STATUSES.has(
              latestStatus
            )
          ) {
            clearDocumentProcessingRecovery(
              candidate.documentId
            );

            setDocumentProcessingState(
              (previous) =>
                previous?.source ===
                  "recovered" &&
                previous?.documentId ===
                  candidate.documentId
                  ? null
                  : previous
            );

            break;
          }

          setDocumentProcessingState(
            (previous) => {
              if (
                previous?.source !==
                  "recovered" ||
                previous?.documentId !==
                  candidate.documentId
              ) {
                return previous;
              }

              return {
                ...previous,
                progressStage:
                  latest.progressStage ||
                  latestStatus ||
                  previous.progressStage ||
                  "processing",
                progressPercent:
                  latest.progressPercent,
                progressMessage:
                  latest.progressMessage ||
                  previous.progressMessage ||
                  "",
                fileName:
                  latest.fileName ||
                  previous.fileName ||
                  "Document",
              };
            }
          );
        } catch (error) {
          if (
            cancelled ||
            error?.name ===
              "AbortError"
          ) {
            break;
          }

          consecutiveErrors += 1;

          console.warn(
            "[ChatInterface] DI progress reconnect status check failed:",
            error?.message || error
          );

          if (
            consecutiveErrors >=
            DI_PROCESSING_RECOVERY_MAX_ERRORS
          ) {
            setDocumentProcessingState(
              (previous) =>
                previous?.source ===
                  "recovered" &&
                previous?.documentId ===
                  candidate.documentId
                  ? null
                  : previous
            );
            break;
          }
        }

        await new Promise((resolve) =>
          setTimeout(
            resolve,
            DI_PROCESSING_RECOVERY_POLL_MS
          )
        );
      }
    };

    run();

    return () => {
      cancelled = true;

      try {
        controller?.abort?.();
      } catch {}

      if (
        documentProcessingReconnectRef.current
          .key === reconnectKey
      ) {
        documentProcessingReconnectRef.current = {
          key: "",
          controller: null,
        };
      }
    };
  }, [
    authoritativeDocumentCatalog,
    clearDocumentProcessingRecovery,
    currentUser,
    documentCatalogResolved,
    documentProcessingRecoveryStorageKey,
    documentProcessingState?.source,
    dropDocumentFromChatState,
    messageDocumentFallbacks,
    persistDocumentProcessingRecovery,
    session_id,
  ]);


  const handleDocumentCatalogChange = useCallback(
    (documents) => {
      setAuthoritativeDocumentCatalog(
        Array.isArray(documents) ? documents : []
      );
      setDocumentCatalogResolved(true);
    },
    []
  );

  const handleDocumentSoftDeleted = useCallback(
    (document) => {
      const documentId = String(
        document?.id ||
          document?.documentId ||
          document?.document_id ||
          ""
      ).trim();

      if (!documentId) return;

      setHiddenDocumentIds((previous) => {
        const next = new Set(previous || []);
        next.add(documentId);
        return next;
      });

      setAuthoritativeDocumentCatalog((previous) =>
        (previous || []).filter((entry) => {
          const id = String(
            entry?.documentId ||
              entry?.document_id ||
              entry?.id ||
              ""
          );
          return id !== documentId;
        })
      );

      dropDocumentFromChatState(
        documentId,
        {
          refreshCatalog: true,
        }
      );
    },
    [dropDocumentFromChatState]
  );

  // ✅ Chat transcript: generate the existing PDF export, then preview it inside
  // the reusable FileViewerModal. PdfViewer inside that modal owns Print/Download.
  const handleOpenChatTranscript = async () => {
    if (exporting) return;

    if (!session_id) {
      showToast(
        {
          title: "No transcript yet",
          message: "Start a chat before opening the transcript.",
          hint: "",
          showSpinner: false,
        },
        { autoCloseMs: 3500 }
      );
      return;
    }

    setExporting(true);
    showToast(
      {
        title: "Preparing transcript…",
        message: "Generating your PDF chat transcript.",
        hint: "This can take a moment for long threads.",
        showSpinner: true,
      },
      { autoCloseMs: 0 }
    );

    try {
      const headers = await getAuthHeaders();
      if (!headers) throw new Error("Authentication not ready.");

      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/chat/session/${encodeURIComponent(session_id)}/export/pdf`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({ download: false }),
        }
      );

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || "Unable to generate the PDF.");
      }

      const blob = await res.blob();
      if (!blob || blob.size <= 0) {
        throw new Error("The generated transcript PDF was empty.");
      }

      const objectUrl = window.URL.createObjectURL(blob);
      const fileName = `chat_transcript_${session_id || "thread"}_${formatIsoForFileName()}.pdf`;
      const title = activeChat?.session_name
        ? `${activeChat.session_name} — Chat transcript`
        : "Chat transcript";

      // Replace any older transcript preview safely.
      revokeTranscriptObjectUrl();
      transcriptObjectUrlRef.current = objectUrl;

      setTranscriptRecord({
        title,
        subject: title,
        filename: fileName,
        name: fileName,
        format: "pdf",
        fileFormat: "pdf",
        contentType: "application/pdf",
        mimeType: "application/pdf",
        viewerUrl: objectUrl,
        url: objectUrl,
        downloadUrl: objectUrl,
        pdfUrl: objectUrl,
        pdfUrlInline: objectUrl,
        urls: { pdf: objectUrl },
        allowDownload: true,
        linkAllowDownload: true,
        createdAt: new Date().toISOString(),
      });

      closeToast();
    } catch (e) {
      try {
        console.warn("[ChatInterface] transcript PDF preview failed:", e?.message || e);
      } catch {}

      showToast(
        {
          title: "Transcript failed",
          message: toUserFacingChatError(
            e,
            "Unable to prepare the transcript right now. Please try again."
          ),
          hint: "",
          showSpinner: false,
        },
        { autoCloseMs: 5000 }
      );
    } finally {
      setExporting(false);
    }
  };

  const tryStreamChat = async (
    text,
    updatedMessages,
    updatedSessionId,
    attachment,
    requestedMode = "auto",
    followUpContext = null,
    messageType = "",
    turnId = "",
    documentContext = null
  ) => {
    const mode = normalizeChatMode(requestedMode);
    const stableTurnId = String(turnId || "").trim();
    if (!stableTurnId) throw new Error("MISSING_TURN_ID");
    if (DISABLE_STREAM) throw new Error("STREAMING_DISABLED_BY_FLAG");

    const headers = await getAuthHeaders();
    if (!headers) throw new Error("MISSING_AUTH");

    const normalizedFollowUp = normalizeFollowUpContextForSend(followUpContext);
    const isFollowUpAnswer = String(messageType || "").trim().toLowerCase() === "follow_up_answer" && !!normalizedFollowUp;
    // Stream and Nonstream share one serializer so fallback cannot lose
    // document mode, action identity, or page/section scope.
    const documentRoutingFields =
      buildDocumentRoutingFields(documentContext);

    const messagePayload = {
      text,
      attachment,
      turn_id: stableTurnId,
      ...documentRoutingFields,
      ...(isFollowUpAnswer
        ? {
            message_type: "follow_up_answer",
            follow_up: normalizedFollowUp,
          }
        : null),
    };

    const requestPayload = {
      session_id: updatedSessionId,
      turn_id: stableTurnId,
      message: messagePayload,
      mode,
      sender: "user",
      is_new: messages.length === 0,
      title: getChatTitle(),
      capabilities: [STRUCTURED_FOLLOWUP_CAPABILITY],
      ...documentRoutingFields,
      ...(isFollowUpAnswer
        ? {
            message_type: "follow_up_answer",
            follow_up: normalizedFollowUp,
          }
        : null),
    };

    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/chat/stream`, {
      method: "POST",
      headers,
      body: JSON.stringify(requestPayload),
    });

    if (!res.ok) {
      const rawError = await res.text().catch(() => "");
      const parsedError = parseJsonObjectMaybe(rawError) || rawError;
      const billingNotice = getBillingNotice(parsedError, {
        productHint: "chat",
      });
      if (billingNotice) {
        throw createBillingAccessError(billingNotice, parsedError);
      }
      throw new Error("Stream failed");
    }

    if (!res.body) throw new Error("Stream failed");

    const reader = res.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let streamText = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      streamText += decoder.decode(value, { stream: true });

      // ✅ Mark: first assistant output received for this request
      if (!gotAnyOutputForRequestRef.current && streamText.trim()) {
        gotAnyOutputForRequestRef.current = true;
        thinkingLockedUntilRef.current = Date.now() + THINKING_FLASH_SUPPRESS_MS;
        thinkingSuppressUntilRef.current = thinkingLockedUntilRef.current;
      }

      // Keep the raw transport while streaming; render path strips metadata envelopes.
      setStreamingText(streamText);
    }

    // ✅ Ensure lock is set when stream completes (even for tiny outputs)
    if (streamText.trim()) {
      gotAnyOutputForRequestRef.current = true;
      thinkingLockedUntilRef.current = Date.now() + THINKING_FLASH_SUPPRESS_MS;
      thinkingSuppressUntilRef.current = thinkingLockedUntilRef.current;
    }

    const billingTransport = extractBillingDenialEnvelope(streamText);
    if (billingTransport.billingMeta) {
      setStreamingText("");
      const billingNotice = getBillingNotice(billingTransport.billingMeta, {
        productHint: "chat",
      });
      if (billingNotice) {
        throw createBillingAccessError(
          billingNotice,
          billingTransport.billingMeta
        );
      }
      throw new Error("Billing denial could not be classified.");
    }

    const transport = extractStructuredFollowUpEnvelope(streamText);
    const finalMsg = {
      user: "Lexa",
      text: transport.visibleText || streamText,
      mode,
      followUpMeta: transport.followUps,
      followUpOriginMode: transport.originMode || mode,
      messageId: makeClientMessageId("lexa"),
      turnId: stableTurnId,
      timestamp: new Date().toISOString(),
    };
    setMessages([...updatedMessages, finalMsg]);
    setStreamingText("");

    // Return the raw transport string so /chat/message can persist the metadata envelope
    // without changing its existing string-message contract.
    return streamText;
  };

  const fallbackNonStreamChat = async (
    text,
    updatedMessages,
    updatedSessionId,
    attachment,
    requestedMode = "auto",
    followUpContext = null,
    messageType = "",
    turnId = "",
    documentContext = null
  ) => {
    const mode = normalizeChatMode(requestedMode);
    const stableTurnId = String(turnId || "").trim();
    if (!stableTurnId) throw new Error("MISSING_TURN_ID");
    const headers = await getAuthHeaders();
    if (!headers) throw new Error("MISSING_AUTH");

    const normalizedFollowUp = normalizeFollowUpContextForSend(followUpContext);
    const isFollowUpAnswer = String(messageType || "").trim().toLowerCase() === "follow_up_answer" && !!normalizedFollowUp;
    // Stream and Nonstream share one serializer so fallback cannot lose
    // document mode, action identity, or page/section scope.
    const documentRoutingFields =
      buildDocumentRoutingFields(documentContext);

    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/chat/nonstream`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        session_id: updatedSessionId,
        turn_id: stableTurnId,
        message: {
          text,
          attachment,
          turn_id: stableTurnId,
          ...documentRoutingFields,
          ...(isFollowUpAnswer
            ? {
                message_type: "follow_up_answer",
                follow_up: normalizedFollowUp,
              }
            : null),
        },
        mode,
        sender: "user",
        is_new: messages.length === 0,
        title: getChatTitle(),
        capabilities: [STRUCTURED_FOLLOWUP_CAPABILITY],
        ...documentRoutingFields,
        ...(isFollowUpAnswer
          ? {
              message_type: "follow_up_answer",
              follow_up: normalizedFollowUp,
            }
          : null),
      }),
    });

    if (!res.ok) {
      const rawError = await res.text().catch(() => "");
      const parsedError = parseJsonObjectMaybe(rawError) || rawError;
      const billingNotice = getBillingNotice(parsedError, {
        productHint: "chat",
      });
      if (billingNotice) {
        throw createBillingAccessError(billingNotice, parsedError);
      }
      throw new Error(GENERIC_CHAT_ERROR_MESSAGE);
    }

    const ct = res.headers.get("content-type") || "";
    let reply;
    if (ct.includes("application/json")) {
      const data = await res.json();

      const billingNotice = getBillingNotice(data, {
        productHint: "chat",
      });
      if (billingNotice) {
        throw createBillingAccessError(billingNotice, data);
      }

      reply = data.reply ?? data.message ?? data.text ?? "";
    } else {
      reply = (await res.text()).trim();
    }

    const billingTransport = extractBillingDenialEnvelope(reply);
    if (billingTransport.billingMeta) {
      const billingNotice = getBillingNotice(billingTransport.billingMeta, {
        productHint: "chat",
      });
      if (billingNotice) {
        throw createBillingAccessError(
          billingNotice,
          billingTransport.billingMeta
        );
      }
      throw new Error(GENERIC_CHAT_ERROR_MESSAGE);
    }

    // ✅ Mark: assistant output received for this request
    if ((reply || "").trim()) {
      gotAnyOutputForRequestRef.current = true;
      thinkingLockedUntilRef.current = Date.now() + THINKING_FLASH_SUPPRESS_MS;
      thinkingSuppressUntilRef.current = thinkingLockedUntilRef.current;
    }

    const transport = extractStructuredFollowUpEnvelope(reply);
    const assistantMessage = {
      user: "Lexa",
      text: transport.visibleText || reply,
      mode,
      followUpMeta: transport.followUps,
      followUpOriginMode: transport.originMode || mode,
      messageId: makeClientMessageId("lexa"),
      turnId: stableTurnId,
      timestamp: new Date().toISOString(),
    };
    setMessages([...updatedMessages, assistantMessage]);
    return reply;
  };

  const sendMessage = async (raw = input, attachmentFromChild = null) => {
    let text, attachment, requestedMode, explicitMessageType, explicitFollowUp, displayText;
    let explicitDocumentId = "";
    let explicitDocumentActionId = "";
    let explicitDocumentPageScope = null;
    let attachmentCameFromDocumentScope = false;
    let explicitTurnId = "";
    let explicitSessionId = "";

    if (typeof raw === "string") {
      text = raw;
      attachment = attachmentFromChild || null;
      requestedMode = composerMode || "auto";
      explicitMessageType = "";
      explicitFollowUp = null;
      displayText = null;
    } else {
      text = raw?.text || "";
      attachment = raw?.attachment || attachmentFromChild || null;
      requestedMode = raw?.mode || composerMode || "auto";
      explicitMessageType = raw?.message_type || raw?.messageType || "";
      explicitFollowUp = raw?.follow_up || raw?.followUp || null;
      displayText = raw?.display_text ?? raw?.displayText ?? null;
      explicitTurnId = String(
        raw?.turnId ||
          raw?.turn_id ||
          ""
      ).trim();
      explicitSessionId = String(
        raw?.sessionId ||
          raw?.session_id ||
          ""
      ).trim();

      const rawDocumentContext =
        raw?.documentContext && typeof raw.documentContext === "object"
          ? raw.documentContext
          : raw?.document_context && typeof raw.document_context === "object"
          ? raw.document_context
          : {};

      explicitDocumentId = String(
        raw?.documentId ||
          raw?.document_id ||
          raw?.activeDocumentId ||
          raw?.active_document_id ||
          rawDocumentContext.documentId ||
          rawDocumentContext.document_id ||
          ""
      ).trim();

      explicitDocumentActionId = normalizeDocumentActionId(
        raw?.actionId ||
          raw?.action_id ||
          raw?.documentActionId ||
          raw?.document_action_id ||
          rawDocumentContext.actionId ||
          rawDocumentContext.action_id ||
          rawDocumentContext.documentActionId ||
          rawDocumentContext.document_action_id
      );

      explicitDocumentPageScope = normalizeDocumentPageScope(
        raw?.pageScope ||
          raw?.page_scope ||
          raw?.documentPageScope ||
          raw?.document_page_scope ||
          rawDocumentContext.pageScope ||
          rawDocumentContext.page_scope ||
          rawDocumentContext.documentPageScope ||
          rawDocumentContext.document_page_scope
      );
    }

    // "Ask this document" scopes the next normal composer message without requiring
    // ChatInput to own document state. A newly selected attachment always wins.
    if (!attachment && pendingDocumentContext?.document?.attachment) {
      attachment = {
        ...pendingDocumentContext.document.attachment,
        documentId: pendingDocumentContext.document.id,
        reusedDocument: true,
        displayHidden: true,
      };
      attachmentCameFromDocumentScope = true;
    }

    // ✅ Frontend carries the user's current manual selection only.
    // Auto remains "auto"; Research remains "research". A follow-up answer never
    // silently changes the selector or forces a different mode.
    const mode = normalizeChatMode(requestedMode);
    if (mode !== composerMode) setComposerMode(mode);

    const explicitType = String(explicitMessageType || "").trim().toLowerCase();
    const suppressPendingFollowUp =
      explicitType === "document_action" ||
      explicitType === "document_page_map";
    const followUpForSend = suppressPendingFollowUp
      ? null
      : normalizeFollowUpContextForSend(explicitFollowUp || pendingFollowUp);
    const baseMessageType = followUpForSend ? "follow_up_answer" : explicitType;

    const pendingFastChatUpload = !!(
      attachment?.pendingUpload &&
      typeof attachment?.uploadOnSend === "function"
    );

    const hasExistingAttachmentIdentity = !!(
      attachment?.documentId ||
      attachment?.document_id ||
      attachment?.fileUrl ||
      (Array.isArray(attachment?.fileUrls) && attachment.fileUrls.length)
    );

    // An attachment-only send is valid even though the user's text is intentionally "".
    if (!String(text || "").trim() && !pendingFastChatUpload && !hasExistingAttachmentIdentity) {
      return;
    }
    if (!currentUser) return;

    // One logical Send = one stable identity. Do not regenerate this ID during
    // Stream -> Nonstream fallback or while an attachment progresses through
    // reserve/commit. Backend usage idempotency depends on this exact reuse.
    const turnId = explicitTurnId || makeLogicalTurnId();

    // User initiated a real Send transaction. Do not put the local file in GCS
    // until Main has created/resolved the session and /chat/attachment/reserve
    // has authorized the current attachment entitlement.
    setEmptyRouterDismissed(true);
    didUserSendRef.current = true;
    pendingScrollToBottomRef.current = true;

    requestIdRef.current += 1;
    gotAnyOutputForRequestRef.current = false;
    thinkingLockedUntilRef.current = 0;

    // Invalidate in-flight history so it cannot overwrite this transaction.
    historyFetchTokenRef.current += 1;

    localStorage.removeItem("chat_input");
    setLoading(true);

    // Trusted internal callers such as the File Vault handoff may bind this
    // logical Send to the exact backend-issued Chat session. Normal composer
    // sends leave this blank and retain the existing active-session behavior.
    let updatedSessionId = explicitSessionId || session_id;
    let attachmentUploadCompleted = false;
    let normalizedAttachment = null;
    let updatedMessages = messages;
    let effectiveMessageType = baseMessageType;
    let documentId = null;
    let documentIntelligenceProjection = null;
    let documentProcessingRequestId = null;

    try {
      const headersBasic = await getAuthHeaders();

      if (!headersBasic) {
        const msg = GENERIC_CHAT_ERROR_MESSAGE;

        if (pendingFastChatUpload) {
          // Throw before reserve/upload so ChatInput restores the untouched local draft.
          throw new Error(msg);
        }

        setMessages((prev) => [
          ...prev,
          {
            user: "System",
            text: msg,
            timestamp: new Date().toISOString(),
          },
        ]);
        return;
      }

      // -----------------------------------------------------------------
      // 1) Resolve/create Chat session first.
      // New-session access failures stop before any document reservation.
      // -----------------------------------------------------------------
      if (!updatedSessionId && setActiveChat) {
        const sessionRes = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/chat/session`,
          {
            method: "POST",
            headers: headersBasic,
            body: JSON.stringify({ autoCreate: true }),
          }
        );

        if (!sessionRes.ok) {
          const errJson = await sessionRes.json().catch(() => null);
          const billingNotice = getBillingNotice(errJson, {
            productHint: pendingFastChatUpload
              ? "document_intelligence"
              : "chat",
          });
          const msg = billingNotice?.message || GENERIC_CHAT_ERROR_MESSAGE;

          if (pendingFastChatUpload) {
            // Nothing has been uploaded. Let ChatInput restore the local file.
            if (billingNotice) {
              throw createBillingAccessError(billingNotice, errJson);
            }
            throw new Error(msg);
          }

          if (billingNotice) {
            setBillingNotice(billingNotice);
          }

          setMessages((prev) => [
            ...prev,
            {
              user: "System",
              text: msg,
              billingCode: billingNotice?.code || null,
              timestamp: new Date().toISOString(),
            },
          ]);
          return;
        }

        const sessionObj = await sessionRes.json().catch(() => null);
        const sid = sessionObj?.session_id || null;

        if (!sid) {
          const msg = GENERIC_CHAT_ERROR_MESSAGE;

          if (pendingFastChatUpload) {
            throw new Error(msg);
          }

          setMessages((prev) => [
            ...prev,
            {
              user: "System",
              text: msg,
              timestamp: new Date().toISOString(),
            },
          ]);
          return;
        }

        // Lock UI to this new session immediately.
        uiSessionIdRef.current = sid;
        lastHistoryFetchSidRef.current = null;

        if (activeDocumentId) {
          persistDocumentSelection(
            activeDocumentId,
            sid
          );
        }

        persistDocumentChatMode(
          documentChatMode,
          sid
        );

        setActiveChat(sessionObj);
        updatedSessionId = sid;
      }

      if (!updatedSessionId) {
        const msg = GENERIC_CHAT_ERROR_MESSAGE;

        if (pendingFastChatUpload) {
          throw new Error(msg);
        }

        setMessages((prev) => [
          ...prev,
          {
            user: "System",
            text: msg,
            timestamp: new Date().toISOString(),
          },
        ]);
        return;
      }

      // -----------------------------------------------------------------
      // 2) Fast Chat attachment upload executes ONLY because Send was pressed.
      //
      // AttachmentController owns the mechanics:
      //   /chat/attachment/reserve
      //      -> current billing/entitlement check
      //   signed PUT ORIGINAL file directly to GCS
      //   /chat/attachment/commit
      //      -> Document Intelligence worker
      //
      // A reserve/access failure throws before any GCS PUT.
      // -----------------------------------------------------------------
      if (pendingFastChatUpload) {
        documentProcessingRequestId =
          ++documentProcessingRequestRef.current;

        const processingFileName = String(
          attachment?.fileName ||
            attachment?.filename ||
            attachment?.localFile?.name ||
            "Document"
        );

        setDocumentProcessingState({
          requestId: documentProcessingRequestId,
          source: "live",
          documentId: null,
          fileName: processingFileName,
          progressStage: "reserving",
          progressPercent: null,
          progressMessage: "Preparing document processing…",
        });

        const uploadResult = await attachment.uploadOnSend({
          sessionId: updatedSessionId,
          turnId,
          onProgress: (progressUpdate = {}) => {
            applyLiveDocumentProcessingProgress({
              requestId: documentProcessingRequestId,
              processingSessionId: updatedSessionId,
              fileName: processingFileName,
              progressUpdate,
            });
          },
        });

        attachmentUploadCompleted = true;

        const completedUploadDocumentId = String(
          uploadResult?.documentId ||
            uploadResult?.document_id ||
            ""
        ).trim();

        if (completedUploadDocumentId) {
          clearDocumentProcessingRecovery(
            completedUploadDocumentId
          );
        }

        setDocumentProcessingState((previous) => {
          if (
            !previous ||
            previous.requestId !== documentProcessingRequestId
          ) {
            return previous;
          }

          const backendPercentRaw =
            uploadResult?.progressPercent ??
            uploadResult?.progress_percent ??
            null;

          const hasBackendPercent =
            backendPercentRaw !== null &&
            backendPercentRaw !== undefined &&
            String(backendPercentRaw).trim() !== "";

          const parsedBackendPercent = hasBackendPercent
            ? Number(backendPercentRaw)
            : Number.NaN;

          const backendProgressPercent = Number.isFinite(parsedBackendPercent)
            ? Math.max(0, Math.min(100, parsedBackendPercent))
            : null;

          return {
            ...previous,
            documentId:
              uploadResult?.documentId ||
              uploadResult?.document_id ||
              previous.documentId ||
              null,
            progressStage:
              uploadResult?.progressStage ||
              uploadResult?.progress_stage ||
              uploadResult?.documentStatus ||
              uploadResult?.document_status ||
              previous.progressStage ||
              "processing",
            progressPercent:
              backendProgressPercent !== null
                ? backendProgressPercent
                : previous.progressPercent,
            progressMessage:
              uploadResult?.progressMessage ||
              uploadResult?.progress_message ||
              previous.progressMessage ||
              "",
          };
        });

        documentIntelligenceProjection =
          normalizeDocumentIntelligenceProjection(
            uploadResult?.documentIntelligence ||
              uploadResult?.document_intelligence ||
              null
          );

        const projectedAttachmentFields =
          attachmentFieldsFromDocumentIntelligence(
            documentIntelligenceProjection
          );

        // Replace browser-only values with authoritative Main/worker metadata.
        // localFile and uploadOnSend must never be serialized to Firestore/chat APIs.
        //
        // The safe public DIS projection wins over legacy/default attachment
        // guesses for document type, page map, actions, warning count, and scope.
        attachment = {
          ...attachment,
          ...uploadResult,
          ...projectedAttachmentFields,
          fileName:
            attachment?.fileName ||
            attachment?.filename ||
            projectedAttachmentFields.projectedFileName ||
            null,
          mime:
            attachment?.mime ||
            projectedAttachmentFields.projectedMimeType ||
            null,
          sizeBytes:
            attachment?.sizeBytes ??
            projectedAttachmentFields.projectedSizeBytes ??
            null,
          pendingUpload: false,
          localFile: null,
          uploadOnSend: null,
          attachmentMode:
            uploadResult?.mode ||
            attachment?.attachmentMode ||
            attachment?.mode ||
            "chat_document_intelligence",
        };

        // Temporary adapter-only keys must never leave this component.
        delete attachment.projectedFileName;
        delete attachment.projectedMimeType;
        delete attachment.projectedSizeBytes;
      }

      // -----------------------------------------------------------------
      // 3) Normalize the transport/persistence attachment.
      // Prefer the authoritative DI document_id. A synthetic legacy hash is
      // only a compatibility fallback for older attachment records.
      // -----------------------------------------------------------------
      const rawFileUrl = attachment?.fileUrl || null;
      const rawFileUrls =
        attachment?.fileUrls ||
        (rawFileUrl ? [rawFileUrl] : []);
      const fileUrls = (rawFileUrls || []).filter((u) =>
        /^https?:\/\/.*/i.test(String(u || ""))
      );
      const fileUrl = fileUrls[0] || null;

      const previewUrl = null;
      const fileName =
        attachment?.fileName ||
        attachment?.filename ||
        attachment?.localFile?.name ||
        null;
      const summary = attachment?.summary || null;
      const isVisionContext = attachment?.isVisionContext || false;

      const frameIds = Array.isArray(attachment?.frameIds)
        ? attachment.frameIds
        : [];
      const uploadBatchId =
        attachment?.uploadBatchId ||
        attachment?.upload_batch_id ||
        null;

      const authoritativeDocumentId =
        attachment?.documentId ||
        attachment?.document_id ||
        null;

      documentId = authoritativeDocumentId
        ? String(authoritativeDocumentId)
        : fileUrls.length
        ? makeAttachmentDocumentId(
            {
              ...attachment,
              fileUrl,
              fileUrls,
              fileName,
              uploadBatchId,
            },
            "",
            messages.length
          )
        : null;

      const reusedDocument = !!(
        attachment?.reusedDocument ||
        attachment?.reused_document ||
        attachmentCameFromDocumentScope
      );
      const displayHidden = !!(
        attachment?.displayHidden ||
        attachment?.display_hidden ||
        reusedDocument
      );

      normalizedAttachment = attachment
        ? {
            fileUrl,
            fileUrls,
            fileName,
            previewUrl,
            frameIds,
            uploadBatchId,

            ...(attachment?.mime
              ? { mime: String(attachment.mime) }
              : null),
            ...(Number.isFinite(Number(attachment?.sizeBytes))
              ? { sizeBytes: Number(attachment.sizeBytes) }
              : null),

            ...(documentId ? { documentId } : null),
            ...(reusedDocument ? { reusedDocument: true } : null),
            ...(displayHidden ? { displayHidden: true } : null),

            ...(attachment?.documentStatus || attachment?.document_status
              ? {
                  documentStatus:
                    attachment.documentStatus ||
                    attachment.document_status,
                }
              : null),

            ...(attachment?.documentType || attachment?.document_type
              ? {
                  documentType:
                    attachment.documentType ||
                    attachment.document_type,
                }
              : null),

            ...(attachment?.documentTypeConfidence !== null &&
            attachment?.documentTypeConfidence !== undefined
              ? {
                  documentTypeConfidence:
                    attachment.documentTypeConfidence,
                }
              : attachment?.document_type_confidence !== null &&
                attachment?.document_type_confidence !== undefined
              ? {
                  documentTypeConfidence:
                    attachment.document_type_confidence,
                }
              : null),

            ...(Number.isFinite(
              Number(attachment?.pageCount ?? attachment?.page_count)
            )
              ? {
                  pageCount: Number(
                    attachment?.pageCount ??
                      attachment?.page_count
                  ),
                }
              : null),

            ...(Number.isFinite(
              Number(
                attachment?.processedPageCount ??
                  attachment?.processed_page_count
              )
            )
              ? {
                  processedPageCount: Number(
                    attachment?.processedPageCount ??
                      attachment?.processed_page_count
                  ),
                }
              : null),

            ...(typeof attachment?.truncated === "boolean"
              ? { truncated: attachment.truncated }
              : null),

            ...(Number.isFinite(
              Number(
                attachment?.truncationStartPage ??
                  attachment?.truncation_start_page
              )
            )
              ? {
                  truncationStartPage: Number(
                    attachment?.truncationStartPage ??
                      attachment?.truncation_start_page
                  ),
                }
              : null),

            ...(Number.isFinite(
              Number(
                attachment?.truncationOmittedPageCount ??
                  attachment?.truncation_omitted_page_count
              )
            )
              ? {
                  truncationOmittedPageCount: Number(
                    attachment?.truncationOmittedPageCount ??
                      attachment?.truncation_omitted_page_count
                  ),
                }
              : null),

            ...(attachment?.language
              ? { language: String(attachment.language) }
              : null),

            ...(attachment?.workerRegion || attachment?.worker_region
              ? {
                  workerRegion:
                    attachment.workerRegion ||
                    attachment.worker_region,
                }
              : null),

            ...(attachment?.manifestPath || attachment?.manifest_path
              ? {
                  manifestPath:
                    attachment.manifestPath ||
                    attachment.manifest_path,
                }
              : null),

            ...(attachment?.briefPath || attachment?.brief_path
              ? {
                  briefPath:
                    attachment.briefPath ||
                    attachment.brief_path,
                }
              : null),

            ...(attachment?.pageMapPath || attachment?.page_map_path
              ? {
                  pageMapPath:
                    attachment.pageMapPath ||
                    attachment.page_map_path,
                }
              : null),

            ...(attachment?.indexManifestPath ||
            attachment?.index_manifest_path
              ? {
                  indexManifestPath:
                    attachment.indexManifestPath ||
                    attachment.index_manifest_path,
                }
              : null),

            ...(Number.isFinite(
              Number(
                attachment?.warningCount ??
                  attachment?.warning_count
              )
            )
              ? {
                  warningCount: Number(
                    attachment?.warningCount ??
                      attachment?.warning_count
                  ),
                }
              : null),

            ...(attachment?.attachmentMode || attachment?.mode
              ? {
                  attachmentMode:
                    attachment.attachmentMode ||
                    attachment.mode,
                }
              : null),

            ...(Array.isArray(
              attachment?.pageMap ||
                attachment?.page_map
            )
              ? {
                  pageMap:
                    attachment.pageMap ||
                    attachment.page_map,
                }
              : null),

            ...(Array.isArray(
              attachment?.documentActions ||
                attachment?.document_actions
            )
              ? {
                  documentActions:
                    attachment.documentActions ||
                    attachment.document_actions,
                }
              : null),
          }
        : null;

      // A newly uploaded document with no typed question is a real attachment
      // message, not a fake natural-language prompt.
      if (
        !String(text || "").trim() &&
        documentId &&
        !effectiveMessageType
      ) {
        effectiveMessageType = "document_attachment";
      }

      const visibleUserText =
        displayText !== null && displayText !== undefined
          ? String(displayText)
          : String(text || "");

      const documentIntelligenceTurn = !!(
        documentId &&
        (pendingFastChatUpload ||
          reusedDocument ||
          attachmentCameFromDocumentScope ||
          documentIntelligenceProjection ||
          /document_intelligence/i.test(
            String(
              normalizedAttachment?.attachmentMode ||
                attachment?.attachmentMode ||
                attachment?.mode ||
                ""
            )
          ))
      );

      // Persistent selection is only an available candidate. Passive Auto does
      // not manufacture an attachment; backend relevance decides whether to use it.
      const selectedDocumentCandidate =
        chatEligibleDocumentIntelligenceDocuments.find(
          (doc) =>
            String(doc?.id || "") ===
            String(activeDocumentId || "")
        ) || null;

      const explicitAttachmentDocumentUse = !!(
        documentIntelligenceTurn &&
        documentId
      );

      const explicitEligibleDocument =
        explicitDocumentId
          ? chatEligibleDocumentIntelligenceDocuments.find(
              (doc) =>
                String(doc?.id || "") ===
                String(explicitDocumentId)
            ) || null
          : null;

      const activeDocumentIdForSend = explicitAttachmentDocumentUse
        ? String(documentId)
        : String(
            explicitEligibleDocument?.id ||
              selectedDocumentCandidate?.id ||
              ""
          ).trim();

      const activeDocumentForSend =
        explicitAttachmentDocumentUse
          ? (
              chatEligibleDocumentIntelligenceDocuments.find(
                (doc) =>
                  String(doc?.id || "") ===
                  String(activeDocumentIdForSend || "")
              ) ||
              null
            )
          : explicitEligibleDocument ||
            selectedDocumentCandidate ||
            null;

      const statePageScope =
        activeDocumentPageScope &&
        (!activeDocumentPageScope.documentId ||
          String(activeDocumentPageScope.documentId) ===
            String(activeDocumentIdForSend || ""))
          ? activeDocumentPageScope
          : null;

      const persistentDocumentMode =
        normalizeDocumentChatMode(documentChatMode);

      const documentPageScopeForSend =
        persistentDocumentMode === "off" &&
        !explicitAttachmentDocumentUse
          ? null
          : normalizeDocumentPageScope(
              explicitDocumentPageScope || statePageScope,
              activeDocumentForSend?.pageCount
            );

      const documentActionIdForSend =
        explicitDocumentActionId;

      const explicitDocumentRoutingUse = !!(
        activeDocumentIdForSend &&
        (explicitAttachmentDocumentUse ||
          documentActionIdForSend ||
          documentPageScopeForSend)
      );

      // Explicit action/page selection forces only this logical Send. We do not
      // mutate documentChatMode, so persistent Auto / Always Use remains unchanged.
      const documentContextModeForSend =
        explicitDocumentRoutingUse
          ? "force"
          : persistentDocumentMode;

      const documentContextForSend = activeDocumentIdForSend
        ? {
            documentId: activeDocumentIdForSend,
            mode: documentContextModeForSend,
            ...(documentActionIdForSend
              ? { actionId: documentActionIdForSend }
              : {}),
            ...(documentPageScopeForSend
              ? {
                  pageScope: {
                    ...documentPageScopeForSend,
                    documentId: activeDocumentIdForSend,
                  },
                }
              : {}),
          }
        : null;

      const documentRoutingFieldsForSend =
        buildDocumentRoutingFields(documentContextForSend);

      const deterministicDocumentGroundedTurn = !!(
        activeDocumentIdForSend &&
        documentContextModeForSend === "force"
      );

      const turnMemoryScope =
        documentIntelligenceTurn &&
        !String(text || "").trim() &&
        !reusedDocument
          ? "document_event"
          : documentIntelligenceTurn ||
            deterministicDocumentGroundedTurn
          ? "document_grounded"
          : "conversation";

      const userMessageId = makeClientMessageId("user");
      const userMessage = {
        user: t("you"),
        text: visibleUserText,
        mode,
        messageType: effectiveMessageType || null,
        followUp: followUpForSend || null,
        messageId: userMessageId,
        turnId,
        memoryScope: turnMemoryScope,
        attachment: normalizedAttachment,
        documentContext: documentContextForSend,
        documentActionId:
          documentContextForSend?.actionId || null,
        documentPageScope:
          documentContextForSend?.pageScope || null,
        documentScope:
          reusedDocument && documentId
            ? {
                documentId,
                fileName:
                  fileName ||
                  pendingDocumentContext?.document?.name ||
                  "Document",
              }
            : null,
        documentReference:
          activeDocumentIdForSend
            ? {
                documentId: activeDocumentIdForSend,
                fileName: String(
                  activeDocumentForSend?.fileName ||
                    activeDocumentForSend?.name ||
                    fileName ||
                    pendingDocumentContext?.document?.name ||
                    ""
                ).trim(),
              }
            : null,
        visionSummary: summary || null,
        isVisionContext: isVisionContext || false,
        timestamp: new Date().toISOString(),
      };

      updatedMessages = [...messages, userMessage];
      setMessages(updatedMessages);
      setInput("");

      if (documentProcessingRequestId) {
        setDocumentProcessingState((previous) =>
          previous?.requestId === documentProcessingRequestId
            ? null
            : previous
        );
      }

      if (documentId) {
        // One refresh key drives both ChatInterface's owner-wide authoritative
        // gate and the reusable dock's catalog refresh.
        setDocumentCatalogRefreshKey((value) => value + 1);
      }

      if (
        documentId &&
        isDocumentLikeAttachment(normalizedAttachment, userMessage)
      ) {
        selectDocumentForCurrentThread(
          documentId
        );
      }

      if (attachmentCameFromDocumentScope || reusedDocument) {
        setPendingDocumentContext(null);
        try {
          window.dispatchEvent(
            new CustomEvent("asklawly_document_context_clear")
          );
        } catch {}
      }

      if (followUpForSend) {
        setPendingFollowUp(null);
        try {
          window.dispatchEvent(
            new CustomEvent("asklawly_followup_context_clear")
          );
        } catch {}
      }

      // -----------------------------------------------------------------
      // 4) Persist the user turn.
      // For attachment-only sends, text is intentionally empty and the
      // backend message handler must accept document_attachment + documentId.
      // -----------------------------------------------------------------
      const userSaveRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/chat/message`,
        {
          method: "POST",
          headers: headersBasic,
          body: JSON.stringify({
            session_id: updatedSessionId,
            turn_id: turnId,
            memory_scope: turnMemoryScope,
            message: {
              // Persist exactly what the user sees. Never manufacture a
              // "Please analyze..." sentence for an attachment-only turn.
              text: visibleUserText,
              attachment: normalizedAttachment,
              ...documentRoutingFieldsForSend,
            },
            ...documentRoutingFieldsForSend,
            sender: "user",
            is_new: messages.length === 0,
            title: getChatTitle(),
            mode,
            type: effectiveMessageType || "text",
            ...(effectiveMessageType
              ? { message_type: effectiveMessageType }
              : null),
            ...(followUpForSend
              ? {
                  follow_up: followUpForSend,
                }
              : null),
          }),
        }
      );

      if (!userSaveRes.ok) {
        const txt = await userSaveRes.text().catch(() => "");
        const msg = toUserFacingChatError(
          txt,
          GENERIC_CHAT_ERROR_MESSAGE
        );

        setMessages((prev) => [
          ...prev,
          {
            user: "System",
            text: msg,
            timestamp: new Date().toISOString(),
          },
        ]);

        // If upload already completed, do NOT ask ChatInput to restore the old
        // pending local draft; retrying it would create a second DI document.
        return;
      }

      // -----------------------------------------------------------------
      // 5) ATTACHMENT ONLY:
      // Document Intelligence already performed the document analysis.
      // Render Main's deterministic completion message as a normal Lexa turn,
      // persist it through /chat/message, and STOP here. No /chat/stream,
      // no /chat/nonstream, and no Auto/Research behavior applies.
      // -----------------------------------------------------------------
      const attachmentOnlySend = !!(
        !String(text || "").trim() &&
        documentId &&
        !reusedDocument
      );

      if (attachmentOnlySend) {
        const completionMessage = String(
          documentIntelligenceProjection?.completionMessage ||
            attachment?.documentIntelligence?.completionMessage ||
            attachment?.documentIntelligence?.completion_message ||
            ""
        ).trim();

        if (!completionMessage) {
          throw new Error(
            "Document Intelligence completed without a completion message."
          );
        }

        gotAnyOutputForRequestRef.current = true;
        thinkingLockedUntilRef.current =
          Date.now() + THINKING_FLASH_SUPPRESS_MS;
        thinkingSuppressUntilRef.current =
          thinkingLockedUntilRef.current;

        const completionLexaMessage = {
          user: "Lexa",
          text: completionMessage,
          messageId: makeClientMessageId("lexa_di"),
          turnId,
          memoryScope: "document_event",
          timestamp: new Date().toISOString(),
        };

        setMessages([
          ...updatedMessages,
          completionLexaMessage,
        ]);
        setStreamingText("");

        const completionSaveRes = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/chat/message`,
          {
            method: "POST",
            headers: headersBasic,
            body: JSON.stringify({
              session_id: updatedSessionId,
              turn_id: turnId,
              memory_scope: "document_event",
              message_type: "document_intelligence_completion",
              message: completionMessage,
              sender: "lexa",
              is_new: false,
              title: getChatTitle(),
            }),
          }
        );

        if (!completionSaveRes.ok) {
          const txt = await completionSaveRes.text().catch(() => "");
          setMessages((prev) => [
            ...prev,
            {
              user: "System",
              text: toUserFacingChatError(
                txt,
                GENERIC_CHAT_ERROR_MESSAGE
              ),
              timestamp: new Date().toISOString(),
            },
          ]);
        }

        return {
          success: true,
          documentOnly: true,
          documentId,
          turnId,
        };
      }

      // -----------------------------------------------------------------
      // 6) TEXT ONLY or TEXT + DOCUMENT:
      // Preserve the existing Stream -> Nonstream transport fallback, but reuse
      // the SAME logical turnId so retry/fallback can never become a second billable Send.
      // -----------------------------------------------------------------
      let finalReply;

      try {
        finalReply = await tryStreamChat(
          text,
          updatedMessages,
          updatedSessionId,
          normalizedAttachment,
          mode,
          followUpForSend,
          effectiveMessageType,
          turnId,
          documentContextForSend
        );
      } catch (err) {
        const billingNotice = getBillingNotice(err, {
          productHint: "chat",
        });

        // A billing denial is a final access decision, not a transport failure.
        // Never hide it behind a Nonstream retry or turn it into a second request.
        if (billingNotice) {
          throw createBillingAccessError(billingNotice, err);
        }

        console.warn("⚠️ Stream failed, falling back:", err);

        finalReply = await fallbackNonStreamChat(
          text,
          updatedMessages,
          updatedSessionId,
          normalizedAttachment,
          mode,
          followUpForSend,
          effectiveMessageType,
          turnId,
          documentContextForSend
        );
      }

      // Page/section focus is one-turn state. Clear only the exact scope that
      // was consumed; preserve a newer scope selected while Lexa was answering.
      if (documentContextForSend?.pageScope) {
        const consumedScope =
          documentContextForSend.pageScope;

        setDocumentPageScope((current) => {
          const currentDocumentId = String(
            current?.documentId ||
              current?.document_id ||
              ""
          ).trim();
          const consumedDocumentId = String(
            documentContextForSend.documentId ||
              ""
          ).trim();

          if (
            currentDocumentId &&
            consumedDocumentId &&
            currentDocumentId !== consumedDocumentId
          ) {
            return current;
          }

          return documentPageScopesEqual(
            current,
            consumedScope
          )
            ? null
            : current;
        });
      }

      // Save assistant message
      const botSaveRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/chat/message`,
        {
          method: "POST",
          headers: headersBasic,
          body: JSON.stringify({
            session_id: updatedSessionId,
            turn_id: turnId,
            memory_scope: turnMemoryScope,
            message: finalReply,
            sender: "lexa",
            is_new: false,
            title: getChatTitle(),
            mode,
          }),
        }
      );

      if (!botSaveRes.ok) {
        const txt = await botSaveRes.text().catch(() => "");
        setMessages((prev) => [
          ...prev,
          {
            user: "System",
            text: toUserFacingChatError(
              txt,
              GENERIC_CHAT_ERROR_MESSAGE
            ),
            timestamp: new Date().toISOString(),
          },
        ]);
      }

      return {
        success: true,
        documentOnly: false,
        documentId,
        turnId,
      };
    } catch (err) {
      console.error("❌ Error sending message:", err);

      if (documentProcessingRequestId) {
        setDocumentProcessingState((previous) => {
          const failedDocumentId = String(
            previous?.documentId ||
              ""
          ).trim();

          if (
            previous?.requestId ===
            documentProcessingRequestId
          ) {
            if (failedDocumentId) {
              clearDocumentProcessingRecovery(
                failedDocumentId
              );
            }
            return null;
          }

          return previous;
        });
      }

      const localizedFallback = String(
        t("error_sending_message") ||
          GENERIC_CHAT_ERROR_MESSAGE
      );

      const billingNotice = getBillingNotice(err, {
        productHint: pendingFastChatUpload
          ? "document_intelligence"
          : "chat",
      });

      const userFacingMessage = billingNotice?.message ||
        (pendingFastChatUpload
          ? "Something went wrong while processing your attachment. Please try again."
          : localizedFallback);

      if (billingNotice) {
        setBillingNotice(billingNotice);
      }

      setMessages((prev) => [
        ...prev,
        {
          user: "System",
          text: userFacingMessage,
          billingCode: billingNotice?.code || null,
          timestamp: new Date().toISOString(),
        },
      ]);

      // Only restore the local draft when the Send transaction failed BEFORE
      // the authoritative upload/DI processing completed. Once a document was
      // committed, restoring the pending draft could duplicate the upload.
      if (pendingFastChatUpload && !attachmentUploadCompleted) {
        throw err;
      }

      return {
        success: false,
        documentOnly: false,
        documentId,
      };
    } finally {
      setLoading(false);
    }
  };

  // Keep an always-current Send implementation available to the asynchronous
  // File Vault continuation. DI may run for minutes while history/state changes.
  // Calling through this ref prevents the final attachment/completion turn from
  // using a stale messages closure captured when the handoff first arrived.
  sendMessageRef.current = sendMessage;

  // ---------------------------------------------------------------------------
  // FILE VAULT -> CHAT DOCUMENT INTELLIGENCE EARLY HANDOFF
  // ---------------------------------------------------------------------------
  // File Vault performs only the secure preflight/session resolution/canonical
  // source copy. It then navigates here with the accepted handoff directly in
  // React Router state AND stores the same record in localStorage as a refresh/
  // crash fallback. ChatInterface consumes either channel and enters the SAME
  // AttachmentController continuation used by a normal Chat upload:
  // /chat/attachment/commit + authoritative /status.
  //
  // Why two channels:
  // - Router state is the direct in-memory delivery path for the live transition.
  // - localStorage is only the recovery copy if navigation/remount timing changes.
  // Neither path creates a second DI/billing/progress implementation.
  useEffect(() => {
    if (!currentUser) return undefined;

    const navigationState =
      location?.state &&
      typeof location.state === "object" &&
      !Array.isArray(location.state)
        ? location.state
        : {};

    const navigationCandidate =
      navigationState?.[
        FILE_VAULT_DI_HANDOFF_NAV_STATE_KEY
      ] ||
      navigationState?.fileVaultDiHandoff ||
      null;

    const clearNavigationHandoffState = () => {
      try {
        const currentState =
          location?.state &&
          typeof location.state === "object" &&
          !Array.isArray(location.state)
            ? location.state
            : {};

        if (
          !Object.prototype.hasOwnProperty.call(
            currentState,
            FILE_VAULT_DI_HANDOFF_NAV_STATE_KEY
          ) &&
          !Object.prototype.hasOwnProperty.call(
            currentState,
            "fileVaultDiHandoff"
          )
        ) {
          return;
        }

        const nextState = {
          ...currentState,
        };

        delete nextState[
          FILE_VAULT_DI_HANDOFF_NAV_STATE_KEY
        ];
        delete nextState.fileVaultDiHandoff;

        navigate(
          `${location.pathname || "/dashboard"}${
            location.search || ""
          }${location.hash || ""}`,
          {
            replace: true,
            state:
              Object.keys(nextState).length
                ? nextState
                : null,
          }
        );
      } catch (error) {
        console.warn(
          "[ChatInterface][FileVaultDI] unable to clear navigation handoff state:",
          error?.message || error
        );
      }
    };

    let handoff = null;
    let handoffSource = "";

    if (navigationCandidate) {
      try {
        const normalized =
          normalizeFileVaultDocumentIntelligenceHandoff(
            navigationCandidate
          );

        if (
          normalized &&
          isFreshFileVaultDocumentIntelligenceHandoff(
            normalized
          )
        ) {
          handoff = normalized;
          handoffSource = "navigation";

          // Keep a recovery copy until Main proves /commit has durably started.
          try {
            localStorage.setItem(
              FILE_VAULT_DI_HANDOFF_STORAGE_KEY,
              JSON.stringify(normalized)
            );
          } catch {}
        } else if (normalized) {
          console.warn(
            "[ChatInterface][FileVaultDI] ignoring stale navigation handoff",
            {
              sessionId:
                normalized.sessionId,
              documentId:
                normalized.documentId,
            }
          );
          clearNavigationHandoffState();
        }
      } catch {}
    }

    if (!handoff) {
      try {
        const raw = localStorage.getItem(
          FILE_VAULT_DI_HANDOFF_STORAGE_KEY
        );

        if (raw) {
          const normalized =
            normalizeFileVaultDocumentIntelligenceHandoff(
              JSON.parse(raw)
            );

          if (
            normalized &&
            isFreshFileVaultDocumentIntelligenceHandoff(
              normalized
            )
          ) {
            handoff = normalized;
            handoffSource = "storage";
          } else {
            localStorage.removeItem(
              FILE_VAULT_DI_HANDOFF_STORAGE_KEY
            );
          }
        }
      } catch {
        try {
          localStorage.removeItem(
            FILE_VAULT_DI_HANDOFF_STORAGE_KEY
          );
        } catch {}
      }
    }

    if (!handoff || !handoff.commitRequired) {
      return undefined;
    }

    // The backend-issued handoff already contains the exact Chat session that
    // passed policy. Synchronize the visible Chat selection, but DO NOT make
    // React parent-state propagation a prerequisite for /commit. The handoff
    // session is authoritative for this continuation and for its final
    // attachment/completion transcript writes.
    if (
      String(session_id || "") !==
      handoff.sessionId &&
      typeof setActiveChat === "function"
    ) {
      console.log(
        "[ChatInterface][FileVaultDI] routing accepted handoff to Chat session without delaying /commit",
        {
          source: handoffSource,
          currentSessionId: session_id || null,
          handoffSessionId: handoff.sessionId,
          documentId: handoff.documentId,
        }
      );

      setActiveChat({
        session_id: handoff.sessionId,
        session_name: null,
      });
    }

    const handoffKey = [
      handoff.sessionId,
      handoff.documentId,
      handoff.turnId || "no-turn",
    ].join(":");

    if (
      fileVaultDocumentHandoffRef.current.running &&
      fileVaultDocumentHandoffRef.current.key ===
        handoffKey
    ) {
      return undefined;
    }

    console.log(
      "[ChatInterface][FileVaultDI] accepted handoff",
      {
        source: handoffSource,
        sessionId: handoff.sessionId,
        documentId: handoff.documentId,
        turnId: handoff.turnId || null,
        fileName: handoff.fileName,
      }
    );

    fileVaultDocumentHandoffRef.current = {
      key: handoffKey,
      sessionId: handoff.sessionId,
      documentId: handoff.documentId,
      running: true,
    };

    let cancelled = false;
    let delegatedToSendMessage = false;
    let refreshedAfterWorkerStart = false;
    let durableWorkerStartObserved = false;

    const processingRequestId =
      ++documentProcessingRequestRef.current;

    // The user came here specifically to watch this document. Open the reusable
    // DI dock, select the exact document, suppress the empty-state router, and
    // show the normal in-thread Document Intelligence processing card at once.
    setDocumentIntelligenceDockOpen(true);
    setEmptyRouterDismissed(true);
    pendingScrollToBottomRef.current = true;
    selectDocumentForCurrentThread(
      handoff.documentId
    );
    persistDocumentSelection(
      handoff.documentId,
      handoff.sessionId
    );
    setDocumentCatalogRefreshKey(
      (value) => value + 1
    );
    setLoading(true);

    setDocumentProcessingState({
      requestId: processingRequestId,
      source: "live",
      documentId: handoff.documentId,
      fileName: handoff.fileName,
      progressStage: "committing",
      progressPercent: null,
      progressMessage:
        "Starting Document Intelligence.",
    });

    persistDocumentProcessingRecovery({
      documentId: handoff.documentId,
      sessionId: handoff.sessionId,
      fileName: handoff.fileName,
    });

    try {
      window.requestAnimationFrame?.(() => {
        try {
          scrollToBottom();
        } catch {}
      });
    } catch {}

    const clearStoredHandoffRecord = () => {
      try {
        const raw = localStorage.getItem(
          FILE_VAULT_DI_HANDOFF_STORAGE_KEY
        );
        if (!raw) return;

        const current =
          normalizeFileVaultDocumentIntelligenceHandoff(
            JSON.parse(raw)
          );

        if (
          current?.sessionId ===
            handoff.sessionId &&
          current?.documentId ===
            handoff.documentId
        ) {
          localStorage.removeItem(
            FILE_VAULT_DI_HANDOFF_STORAGE_KEY
          );
        }
      } catch {}
    };

    const markDurableWorkerStart = (
      progressUpdate = {}
    ) => {
      if (durableWorkerStartObserved) return;

      const backendStatus =
        normalizeDocumentProcessingStatus(
          progressUpdate?.documentStatus ||
            progressUpdate?.document_status ||
            progressUpdate?.status ||
            ""
        );

      // "committing" is only a browser-side start signal. Clear the early
      // handoff only after Main /status proves that the document has advanced
      // into a durable backend lifecycle (or has reached a terminal record).
      if (
        !DI_PROCESSING_RECONNECTABLE_STATUSES.has(
          backendStatus
        ) &&
        !DI_PROCESSING_TERMINAL_STATUSES.has(
          backendStatus
        )
      ) {
        return;
      }

      durableWorkerStartObserved = true;
      clearStoredHandoffRecord();
      clearNavigationHandoffState();

      console.log(
        "[ChatInterface][FileVaultDI] /commit accepted; authoritative status owns recovery",
        {
          sessionId: handoff.sessionId,
          documentId: handoff.documentId,
          status: backendStatus,
        }
      );
    };

    const run = async () => {
      try {
        console.log(
          "[ChatInterface][FileVaultDI] starting shared /commit + /status continuation",
          {
            sessionId: handoff.sessionId,
            documentId: handoff.documentId,
            turnId: handoff.turnId || null,
          }
        );

        const result =
          await continueFastChatDocumentIntelligence({
            sessionId: handoff.sessionId,
            documentId: handoff.documentId,
            turnId: handoff.turnId,
            fileName: handoff.fileName,
            onProgress: (progressUpdate = {}) => {
              if (cancelled) return;

              markDurableWorkerStart(
                progressUpdate
              );

              const normalizedProgress =
                applyLiveDocumentProcessingProgress({
                  requestId:
                    processingRequestId,
                  processingSessionId:
                    handoff.sessionId,
                  fileName:
                    handoff.fileName,
                  progressUpdate,
                });

              // Refresh once when Main/worker starts publishing real processing
              // state so the already-open reusable DI dock picks up this exact
              // document without waiting for terminal completion.
              if (
                !refreshedAfterWorkerStart &&
                normalizedProgress?.progressDocumentId &&
                (
                  normalizedProgress.progressPercent !==
                    null ||
                  DI_PROCESSING_RECONNECTABLE_STATUSES.has(
                    normalizeDocumentProcessingStatus(
                      progressUpdate?.documentStatus ||
                        progressUpdate?.document_status ||
                        progressUpdate?.status ||
                        normalizedProgress.stage
                    )
                  )
                )
              ) {
                refreshedAfterWorkerStart = true;
                setDocumentCatalogRefreshKey(
                  (value) => value + 1
                );
              }
            },
          });

        if (cancelled) return;

        // A successful terminal return is also authoritative proof that the
        // early handoff no longer belongs in browser delivery state.
        clearStoredHandoffRecord();
        clearNavigationHandoffState();

        clearDocumentProcessingRecovery(
          handoff.documentId
        );

        setDocumentProcessingState((previous) =>
          previous?.requestId ===
          processingRequestId
            ? null
            : previous
        );

        selectDocumentForCurrentThread(
          handoff.documentId
        );
        setDocumentCatalogRefreshKey(
          (value) => value + 1
        );

        const handoffAttachment = {
          ...(result &&
          typeof result === "object"
            ? result
            : {}),
          documentId: handoff.documentId,
          fileName:
            String(
              result?.fileName ||
                result?.file_name ||
                handoff.fileName ||
                "Document"
            ).trim() || "Document",
          ...(handoff.mime
            ? { mime: handoff.mime }
            : null),
          ...(handoff.sizeBytes
            ? {
                sizeBytes:
                  handoff.sizeBytes,
              }
            : null),
          pendingUpload: false,
          attachmentMode:
            result?.mode ||
            "chat_document_intelligence",
          documentIntelligence:
            result?.documentIntelligence ||
            result?.document_intelligence ||
            null,
        };

        // Re-enter the existing attachment-only transcript behavior instead of
        // manufacturing a File Vault message. This persists the blank user
        // document event and the SAME deterministic Lexa completion message that
        // a normal Chat attachment receives, with the same logical turnId.
        const sender =
          sendMessageRef.current;

        if (typeof sender !== "function") {
          throw new Error(
            "Chat attachment continuation is unavailable."
          );
        }

        delegatedToSendMessage = true;

        const sendResult = await sender({
          text: "",
          attachment: handoffAttachment,
          mode: normalizeChatMode(
            composerMode
          ),
          sessionId: handoff.sessionId,
          turnId:
            handoff.turnId || "",
        });

        if (sendResult?.success) {
          fileVaultDocumentHandoffRef.current = {
            key: handoffKey,
            sessionId: handoff.sessionId,
            documentId:
              handoff.documentId,
            running: false,
          };
        }
      } catch (error) {
        if (cancelled) return;

        console.error(
          "[ChatInterface] File Vault Document Intelligence handoff failed:",
          error
        );

        setDocumentProcessingState((previous) =>
          previous?.requestId ===
          processingRequestId
            ? null
            : previous
        );

        const billingNotice =
          getBillingNotice(error, {
            productHint:
              "document_intelligence",
          });

        const terminalStatus =
          normalizeDocumentProcessingStatus(
            error?.documentStatus ||
              error?.payload?.document?.status ||
              ""
          );

        if (
          billingNotice ||
          DI_PROCESSING_TERMINAL_STATUSES.has(
            terminalStatus
          )
        ) {
          clearDocumentProcessingRecovery(
            handoff.documentId
          );
          clearStoredHandoffRecord();
          clearNavigationHandoffState();
          fileVaultDocumentHandoffRef.current = {
            key: "",
            sessionId: "",
            documentId: "",
            running: false,
          };
        }

        if (billingNotice) {
          setBillingNotice(
            billingNotice
          );
        }

        // sendMessage already owns its own System-message error path. Avoid a
        // duplicate System row if the continuation successfully reached that
        // shared transcript function and the failure happened there.
        if (!delegatedToSendMessage) {
          setMessages((previous) => [
            ...previous,
            {
              user: "System",
              text:
                billingNotice?.message ||
                "Something went wrong while processing your attachment. Please try again.",
              billingCode:
                billingNotice?.code ||
                null,
              timestamp:
                new Date().toISOString(),
            },
          ]);
        }
      } finally {
        if (!cancelled) {
          if (!delegatedToSendMessage) {
            setLoading(false);
          }

          if (
            fileVaultDocumentHandoffRef.current
              .key === handoffKey
          ) {
            fileVaultDocumentHandoffRef.current = {
              ...fileVaultDocumentHandoffRef.current,
              running: false,
            };
          }
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [
    currentUser,
    location.hash,
    location.pathname,
    location.search,
    navigate,
    setActiveChat,
  ]);

  // ✅ Normalize one backend history page into the existing ChatInterface message
  // shape. Backend pagination already guarantees chronological order, so pages
  // are never reversed or re-sorted on the client.
  const normalizeHistoryPageMessages = (rawList, sid) => {
    const source = Array.isArray(rawList) ? rawList : [];

    return source.map((msg, idx) => {
      const ms =
        Number(msg?.created_at_ms ?? msg?.createdAtMs ?? msg?.createdMs ?? msg?.timestamp_ms ?? 0) ||
        (typeof msg?.timestamp === "number" ? msg.timestamp : 0) ||
        (msg?.timestamp ? Date.parse(msg.timestamp) : 0) ||
        (msg?.created_at ? Date.parse(msg.created_at) : 0) ||
        0;

          const att = msg.attachment || {};
          const rawUrls = Array.isArray(att.fileUrls) ? att.fileUrls : att.fileUrl ? [att.fileUrl] : [];
          const fileUrls = (rawUrls || []).filter((u) => /^https?:\/\/.*/i.test(u));
          const fileUrl = fileUrls[0] || null;

          const frameIds = Array.isArray(att.frameIds) ? att.frameIds : [];
          const uploadBatchId = att.uploadBatchId || att.upload_batch_id || null;
          const documentId = att.documentId || att.document_id || null;
          const reusedDocument = !!(att.reusedDocument || att.reused_document);
          const displayHidden = !!(att.displayHidden || att.display_hidden || reusedDocument);
          const documentStatus = att.documentStatus || att.document_status || null;
          const documentType = att.documentType || att.document_type || null;
          const documentTypeConfidence =
            att.documentTypeConfidence ??
            att.document_type_confidence ??
            null;
          const pageCount = att.pageCount ?? att.page_count ?? null;
          const processedPageCount =
            att.processedPageCount ??
            att.processed_page_count ??
            null;
          const truncated =
            typeof att.truncated === "boolean"
              ? att.truncated
              : null;
          const truncationStartPage =
            att.truncationStartPage ??
            att.truncation_start_page ??
            null;
          const truncationOmittedPageCount =
            att.truncationOmittedPageCount ??
            att.truncation_omitted_page_count ??
            null;
          const language = att.language || null;
          const workerRegion = att.workerRegion || att.worker_region || null;
          const manifestPath = att.manifestPath || att.manifest_path || null;
          const briefPath = att.briefPath || att.brief_path || null;
          const pageMapPath = att.pageMapPath || att.page_map_path || null;
          const indexManifestPath =
            att.indexManifestPath ||
            att.index_manifest_path ||
            null;
          const warningCount = Number(att.warningCount ?? att.warning_count ?? 0) || 0;
          const attachmentMode =
            att.attachmentMode ||
            att.attachment_mode ||
            null;
          const mime = att.mime || att.mime_type || null;
          const sizeBytes = att.sizeBytes ?? att.size_bytes ?? null;
          const pageMap = Array.isArray(att.pageMap)
            ? att.pageMap
            : Array.isArray(att.page_map)
            ? att.page_map
            : [];
          const documentActions = Array.isArray(att.documentActions)
            ? att.documentActions
            : Array.isArray(att.document_actions)
            ? att.document_actions
            : null;

          const tsIso =
            msg.timestamp && typeof msg.timestamp === "string"
              ? msg.timestamp
              : ms > 0
              ? new Date(ms).toISOString()
              : new Date().toISOString();

          const historyMode = normalizeChatMode(
            msg?.mode ||
              msg?.request_mode ||
              msg?.response_mode ||
              "auto"
          );

          const storedMessageObject =
            coerceObject(msg?.message) || {};
          const storedDocumentContext =
            coerceObject(
              msg?.document_context ||
                msg?.documentContext ||
                storedMessageObject.document_context ||
                storedMessageObject.documentContext
            ) || {};
          const storedFollowUp =
            normalizeFollowUpContextForSend(
              msg?.follow_up ||
                msg?.followUp ||
                storedMessageObject.follow_up ||
                storedMessageObject.followUp
            );
          const storedPageScope =
            normalizeDocumentPageScope(
              msg?.document_page_scope ||
                msg?.documentPageScope ||
                storedDocumentContext.pageScope ||
                storedDocumentContext.page_scope ||
                storedMessageObject.document_page_scope ||
                storedMessageObject.documentPageScope
            );
          const storedActionId =
            normalizeDocumentActionId(
              msg?.document_action_id ||
                msg?.documentActionId ||
                storedDocumentContext.actionId ||
                storedDocumentContext.action_id ||
                storedMessageObject.document_action_id ||
                storedMessageObject.documentActionId
            );
          const storedActiveDocumentId = String(
            msg?.active_document_id ||
              msg?.activeDocumentId ||
              storedDocumentContext.documentId ||
              storedDocumentContext.document_id ||
              storedMessageObject.active_document_id ||
              storedMessageObject.activeDocumentId ||
              documentId ||
              ""
          ).trim();
          const storedDocumentMode =
            normalizeDocumentChatMode(
              msg?.document_context_mode ||
                msg?.documentContextMode ||
                storedDocumentContext.mode ||
                storedMessageObject.document_context_mode ||
                storedMessageObject.documentContextMode ||
                "auto"
            );
          const historyMessageType = String(
            msg?.message_type ||
              msg?.messageType ||
              msg?.type ||
              storedMessageObject.message_type ||
              storedMessageObject.messageType ||
              "text"
          )
            .trim()
            .toLowerCase();

          const historyText =
            typeof msg?.message === "string"
              ? msg.message
              : String(
                  storedMessageObject.text ||
                    msg?.text ||
                    ""
                );

          const transport = msg.sender === "user"
            ? {
                visibleText: historyText,
                originMode: null,
                followUps: [],
              }
            : extractStructuredFollowUpEnvelope(historyText);

          return {
            user: msg.sender === "user" ? t("you") : "Lexa",
            text: transport.visibleText,
            // Older history rows may not contain mode; Auto is the backward-compatible default.
            mode: historyMode,
            messageType: historyMessageType,
            followUp: storedFollowUp || null,
            followUpMeta: transport.followUps,
            followUpOriginMode: transport.originMode || historyMode,
            documentActionId: storedActionId || null,
            documentPageScope: storedPageScope || null,
            documentContext: storedActiveDocumentId
              ? {
                  documentId: storedActiveDocumentId,
                  mode: storedDocumentMode,
                  ...(storedActionId
                    ? { actionId: storedActionId }
                    : {}),
                  ...(storedPageScope
                    ? { pageScope: storedPageScope }
                    : {}),
                }
              : null,
            documentScope:
              reusedDocument && storedActiveDocumentId
                ? {
                    documentId: storedActiveDocumentId,
                    fileName:
                      att.fileName ||
                      att.filename ||
                      "Document",
                  }
                : null,
            messageId: String(msg?.message_id || msg?.id || msg?.chat_message_id || `history_${sid}_${idx}`),
            attachment: {
              fileUrl,
              fileUrls,
              fileName: att.fileName || att.filename || null,
              frameIds,
              uploadBatchId,
              documentId,
              reusedDocument,
              displayHidden,
              documentStatus,
              documentType,
              documentTypeConfidence,
              pageCount,
              processedPageCount,
              truncated,
              truncationStartPage,
              truncationOmittedPageCount,
              language,
              workerRegion,
              manifestPath,
              briefPath,
              pageMapPath,
              indexManifestPath,
              warningCount,
              attachmentMode,
              mime,
              sizeBytes,
              pageMap,
              documentActions,
            },
            timestamp: tsIso,
            __historyIndex: idx,
            __sortMs: ms,
          };
        
    });
  };

  // ✅ Initial history page: newest 40 stored messages, returned oldest -> newest.
  const loadChatHistory = async (sid) => {
    if (!sid || !currentUser) return;

    // If local UI already has messages, never let initial history overwrite it.
    if (currentMessagesLenRef.current > 0) return;

    // Avoid tight-loop refetching of the same session when messages are empty.
    if (lastHistoryFetchSidRef.current === sid) return;
    lastHistoryFetchSidRef.current = sid;

    setHistoryLoading(true);
    setHistoryHasMore(false);
    setHistoryNextCursor("");
    setHistoryLoadingPrevious(false);
    historyPrependScrollRestoreRef.current = null;

    const myToken = ++historyFetchTokenRef.current;

    try {
      const headers = await getAuthHeaders();
      if (!headers) return;

      const params = new URLSearchParams();
      params.set("session_id", sid);
      params.set("limit", String(CHAT_HISTORY_PAGE_SIZE));

      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/chat/history?${params.toString()}`,
        {
          method: "GET",
          headers,
        }
      );

      if (!res.ok) {
        const rawError = await res.text().catch(() => "");
        throw new Error(rawError || `History request failed (${res.status}).`);
      }

      const data = await res.json();

      // If something happened while we were fetching (user sent a message /
      // session changed / token bumped), ignore this stale result.
      if (historyFetchTokenRef.current !== myToken) return;
      if (currentMessagesLenRef.current > 0) return;
      if (didUserSendRef.current || loading || (streamingText && streamingText.trim())) return;

      const loadedMessages = normalizeHistoryPageMessages(
        data?.messages,
        sid
      );

      historyJustLoadedRef.current = true;

      setMessages(loadedMessages);
      setHistoryHasMore(Boolean(data?.has_more));
      setHistoryNextCursor(String(data?.next_cursor || ""));
      setStreamingText("");
      setLoading(false);

      uiSessionIdRef.current = sid;

      lastMsgCountRef.current = loadedMessages.length;
      lastStreamingRef.current = "";

      setTimeout(() => {
        historyJustLoadedRef.current = false;
        syncScrollNavigationState();
      }, 0);
    } catch (err) {
      console.error("❌ History load error:", err);
      if (historyFetchTokenRef.current === myToken) {
        setHistoryHasMore(false);
        setHistoryNextCursor("");
      }
    } finally {
      if (historyFetchTokenRef.current === myToken) {
        setHistoryLoading(false);
      }
    }
  };

  // ✅ Floating ↑ control: fetch the next OLDER 40 messages.
  // This is the only navigation arrow that talks to the backend.
  const loadPreviousChatHistory = async () => {
    const sid = String(session_id || "").trim();
    const cursor = String(historyNextCursor || "").trim();

    if (
      !sid ||
      !currentUser ||
      !historyHasMore ||
      !cursor ||
      historyLoading ||
      historyLoadingPrevious ||
      loading ||
      (streamingText && streamingText.trim())
    ) {
      return;
    }

    const scrollEl = chatWindowRef.current;
    const scrollSnapshot = scrollEl
      ? {
          sid,
          scrollHeight: scrollEl.scrollHeight || 0,
          scrollTop: scrollEl.scrollTop || 0,
        }
      : null;

    setHistoryLoadingPrevious(true);
    const myToken = ++historyFetchTokenRef.current;

    try {
      const headers = await getAuthHeaders();
      if (!headers) return;

      const params = new URLSearchParams();
      params.set("session_id", sid);
      params.set("limit", String(CHAT_HISTORY_PAGE_SIZE));
      params.set("cursor", cursor);

      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/chat/history?${params.toString()}`,
        {
          method: "GET",
          headers,
        }
      );

      if (!res.ok) {
        const rawError = await res.text().catch(() => "");
        throw new Error(rawError || `Previous-history request failed (${res.status}).`);
      }

      const data = await res.json();

      if (historyFetchTokenRef.current !== myToken) return;
      if (String(session_id || "") !== sid) return;

      const olderMessages = normalizeHistoryPageMessages(
        data?.messages,
        sid
      );

      // Backend now returns canonical message_id values. Dedupe defensively so a
      // retry or boundary race can never duplicate a visible transcript row.
      const existingIds = new Set(
        (messages || [])
          .map((message) =>
            String(
              message?.messageId ||
                message?.message_id ||
                ""
            ).trim()
          )
          .filter(Boolean)
      );

      const prependMessages = olderMessages.filter((message) => {
        const id = String(
          message?.messageId ||
            message?.message_id ||
            ""
        ).trim();

        return !id || !existingIds.has(id);
      });

      if (prependMessages.length > 0) {
        historyPrependScrollRestoreRef.current = scrollSnapshot;

        setMessages((previous) => {
          const currentIds = new Set(
            (previous || [])
              .map((message) =>
                String(
                  message?.messageId ||
                    message?.message_id ||
                    ""
                ).trim()
              )
              .filter(Boolean)
          );

          const uniqueOlder = prependMessages.filter((message) => {
            const id = String(
              message?.messageId ||
                message?.message_id ||
                ""
            ).trim();
            return !id || !currentIds.has(id);
          });

          return uniqueOlder.length
            ? [...uniqueOlder, ...(previous || [])]
            : previous;
        });
      } else {
        historyPrependScrollRestoreRef.current = null;
      }

      setHistoryHasMore(Boolean(data?.has_more));
      setHistoryNextCursor(String(data?.next_cursor || ""));
    } catch (err) {
      console.error("❌ Previous history load error:", err);
    } finally {
      if (historyFetchTokenRef.current === myToken) {
        setHistoryLoadingPrevious(false);
      }
    }
  };

  // Preserve the user's reading position when older history is prepended.
  useLayoutEffect(() => {
    const restore = historyPrependScrollRestoreRef.current;
    if (!restore) return;
    if (String(restore.sid || "") !== String(session_id || "")) {
      historyPrependScrollRestoreRef.current = null;
      return;
    }

    const el = chatWindowRef.current;
    if (!el) {
      historyPrependScrollRestoreRef.current = null;
      return;
    }

    const heightDelta = Math.max(
      0,
      (el.scrollHeight || 0) -
        Number(restore.scrollHeight || 0)
    );

    try {
      el.scrollTop =
        Number(restore.scrollTop || 0) +
        heightDelta;
    } catch {}

    historyPrependScrollRestoreRef.current = null;
    syncScrollNavigationState();
  }, [messages.length, session_id]);

  // ✅ Session-change behavior (keeps fast sidebar switching)
  useEffect(() => {
    if (!currentUser) return;

    // When session_id is null but user just sent a message, keep optimistic UI (do NOT clear)
    if (!session_id) {
      if (didUserSendRef.current && (loading || messages.length > 0 || (streamingText && streamingText.trim()))) {
        return;
      }

      uiSessionIdRef.current = null;
      lastHistoryFetchSidRef.current = null;
      didUserSendRef.current = false;

      // ✅ invalidate in-flight history
      historyFetchTokenRef.current += 1;

      setMessages([]);
      setStreamingText("");
      setEmptyRouterDismissed(false);
      setLoading(false);
      setHistoryLoading(false);
      setHistoryHasMore(false);
      setHistoryNextCursor("");
      setHistoryLoadingPrevious(false);
      setShowJumpToLatest(false);
      historyPrependScrollRestoreRef.current = null;
      return;
    }

    // If user clicked a different thread, clear immediately so UI switches instantly.
    // A File Vault handoff is the one exception: when React is merely catching
    // up to the backend-issued target session, preserve the live DI request,
    // processing card, selected document, and open dock. The handoff itself is
    // already bound to that exact session and must not be mistaken for a normal
    // manual thread switch.
    if (uiSessionIdRef.current && uiSessionIdRef.current !== session_id) {
      const activeFileVaultHandoff =
        fileVaultDocumentHandoffRef.current || {};
      const enteringFileVaultHandoffSession = !!(
        activeFileVaultHandoff.running &&
        String(activeFileVaultHandoff.sessionId || "") ===
          String(session_id || "")
      );

      lastHistoryFetchSidRef.current = null;

      // ✅ invalidate in-flight history (old session fetch results become stale)
      historyFetchTokenRef.current += 1;

      setHistoryHasMore(false);
      setHistoryNextCursor("");
      setHistoryLoadingPrevious(false);
      setShowJumpToLatest(false);
      historyPrependScrollRestoreRef.current = null;

      setMessages([]);
      setStreamingText("");
      setPendingFollowUp(null);
      setPendingDocumentContext(null);
      setDocumentPageScope(null);
      setDocumentActionState({});

      if (enteringFileVaultHandoffSession) {
        console.log(
          "[ChatInterface][FileVaultDI] preserving live handoff while Chat session state synchronizes",
          {
            sessionId,
            documentId:
              activeFileVaultHandoff.documentId || null,
          }
        );
        setDocumentIntelligenceDockOpen(true);
        setEmptyRouterDismissed(true);
      } else {
        setDocumentIntelligenceDockOpen(null);
        fileVaultDocumentHandoffRef.current = {
          key: "",
          sessionId: "",
          documentId: "",
          running: false,
        };
        documentSelectionScopeRef.current = "";
        setHydratedDocumentSelectionScope("");
        // Thread switching must not unmount the owner-wide DI catalog.
        setDocumentProcessingState(null);
        documentProcessingRequestRef.current += 1;
        setLoading(false);
      }

      // Any recovered watcher belonged to the previous visible thread. Live
      // File Vault continuation does not use this controller/ref, so it is safe
      // to stop the old reconnect loop even when preserving the new handoff.
      try {
        documentProcessingReconnectRef.current
          .controller?.abort?.();
      } catch {}
      documentProcessingReconnectRef.current = {
        key: "",
        controller: null,
      };

      lastMsgCountRef.current = 0;
      lastStreamingRef.current = "";
      didUserSendRef.current = false;
    }

    loadChatHistory(session_id);
  }, [currentUser, session_id, messages.length]);

  // ✅ ONE-TIME RESPONSE ANCHOR.
  // User message + Lexa Thinking render together. Scroll once before paint so
  // Thinking is visible. When the first streamed character arrives, Thinking
  // disappears and the answer grows downward from that same location.
  //
  // There is intentionally no automatic scroll after this point: not for
  // streaming chunks, not for non-stream completion, and not when Lexa finishes.
  // Any scrollbar movement by the user is therefore always respected.
  useLayoutEffect(() => {
    if (!pendingScrollToBottomRef.current) return;
    pendingScrollToBottomRef.current = false;
    scrollToBottom();
  }, [messages.length]);

  // Bookkeeping only. Never chase the streaming tail.
  useEffect(() => {
    lastMsgCountRef.current = messages.length;
    lastStreamingRef.current = streamingText || "";
  }, [messages.length, streamingText]);

  useEffect(() => {
    localStorage.setItem("chat_input", input);
  }, [input]);

  // --------------------------
  // ✅ Flash suppression guards (ONLY these two behaviors)
  // --------------------------
  const isEmpty = messages.length === 0 && !streamingText && !loading;

  // Empty-state suppression: once it goes away, don't allow it to reappear for 12s
  useEffect(() => {
    if (!isEmpty) {
      emptySuppressUntilRef.current = Date.now() + EMPTY_STATE_SUPPRESS_MS;
    }
  }, [isEmpty]);

  // Thinking flash suppression: after any assistant output, suppress typing bubble for 2s
  useEffect(() => {
    const lastMsg = messages[messages.length - 1];
    const hasAssistantMsg = !!lastMsg && lastMsg.user === "Lexa";
    const hasStreaming = !!(streamingText && streamingText.trim());

    if (hasStreaming || hasAssistantMsg) {
      const lockUntil = Date.now() + THINKING_FLASH_SUPPRESS_MS;
      thinkingSuppressUntilRef.current = lockUntil;

      thinkingLockedUntilRef.current = Math.max(thinkingLockedUntilRef.current || 0, lockUntil);
      gotAnyOutputForRequestRef.current = gotAnyOutputForRequestRef.current || hasStreaming || hasAssistantMsg;
    }
  }, [messages.length, streamingText]);

  // If a guard is currently hiding something, schedule a single re-render when it expires
  useEffect(() => {
    const now = Date.now();

    const nextEmpty = isEmpty && emptySuppressUntilRef.current > now ? emptySuppressUntilRef.current : null;

    const typingWouldShow = loading && !streamingText;
    const requestLockActive = gotAnyOutputForRequestRef.current && now < (thinkingLockedUntilRef.current || 0);

    const nextThinking =
      typingWouldShow && (thinkingSuppressUntilRef.current > now || requestLockActive)
        ? Math.max(thinkingSuppressUntilRef.current, thinkingLockedUntilRef.current)
        : null;

    const next = Math.min(nextEmpty ?? Number.POSITIVE_INFINITY, nextThinking ?? Number.POSITIVE_INFINITY);

    if (!Number.isFinite(next)) return;

    const delay = Math.max(0, next - now + 10);
    const tmr = setTimeout(() => forceGuardTick((n) => n + 1), delay);
    return () => clearTimeout(tmr);
  }, [isEmpty, loading, streamingText, messages.length]);

  // --------------------------
  // ✅ Lexa Read Aloud — streaming backend first, browser fallback second
  // --------------------------
  const ttsUtterRef = useRef(null);
  const ttsAbortRef = useRef(null);
  const ttsRequestIdRef = useRef(0);

  // Streaming PCM / Web Audio ownership.
  const ttsAudioContextRef = useRef(null);
  const ttsPcmSourcesRef = useRef(new Set());
  const ttsNextPlaybackTimeRef = useRef(0);
  const ttsStreamCompleteRef = useRef(false);

  const clearTtsPcmPlayback = useCallback(() => {
    const sources =
      ttsPcmSourcesRef.current ||
      new Set();

    for (const source of sources) {
      try {
        source.onended = null;
        source.stop?.(0);
      } catch {}

      try {
        source.disconnect?.();
      } catch {}
    }

    sources.clear();

    const context =
      ttsAudioContextRef.current;

    ttsAudioContextRef.current = null;
    ttsNextPlaybackTimeRef.current = 0;
    ttsStreamCompleteRef.current = false;

    if (context) {
      try {
        const closing =
          context.close?.();

        if (
          closing &&
          typeof closing.catch ===
            "function"
        ) {
          closing.catch(() => {});
        }
      } catch {}
    }
  }, []);

  const stopActiveTts = useCallback(() => {
    // Invalidate every old async callback before aborting it.
    ttsRequestIdRef.current += 1;

    try {
      ttsAbortRef.current?.abort?.();
    } catch {}

    ttsAbortRef.current = null;

    clearTtsPcmPlayback();

    try {
      if (
        typeof window !== "undefined"
      ) {
        window.speechSynthesis?.cancel?.();
      }
    } catch {}

    ttsUtterRef.current = null;
  }, [clearTtsPcmPlayback]);

  const createTtsAudioContext =
    useCallback(() => {
      if (
        typeof window === "undefined"
      ) {
        return null;
      }

      const AudioContextCtor =
        window.AudioContext ||
        window.webkitAudioContext;

      if (!AudioContextCtor) {
        return null;
      }

      clearTtsPcmPlayback();

      try {
        // Use the browser's native output rate. AudioBuffer is explicitly
        // created at 24 kHz, and Web Audio performs output resampling safely.
        const context =
          new AudioContextCtor();

        ttsAudioContextRef.current =
          context;
        ttsNextPlaybackTimeRef.current = 0;
        ttsStreamCompleteRef.current = false;

        // Call resume from the speaker click path so browser autoplay policy
        // sees a user gesture before the network request begins.
        try {
          const resuming =
            context.resume?.();

          if (
            resuming &&
            typeof resuming.catch ===
              "function"
          ) {
            resuming.catch(() => {});
          }
        } catch {}

        return context;
      } catch (error) {
        console.warn(
          "[TTS] Web Audio context unavailable:",
          error?.message || error
        );
        return null;
      }
    }, [clearTtsPcmPlayback]);

  const scheduleTtsPcmSamples =
    useCallback(
      (
        samples,
        context,
        requestId
      ) => {
        if (
          requestId !==
            ttsRequestIdRef.current ||
          !context ||
          context.state === "closed" ||
          !samples?.length
        ) {
          return false;
        }

        const audioBuffer =
          context.createBuffer(
            TTS_PCM_CHANNELS,
            samples.length,
            TTS_PCM_SAMPLE_RATE_HZ
          );

        audioBuffer.copyToChannel(
          samples,
          0
        );

        const source =
          context.createBufferSource();

        source.buffer = audioBuffer;
        source.connect(
          context.destination
        );

        // Keep a healthy rolling scheduling lead so Web Audio never has to
        // chase the network edge. This is the main frontend protection against
        // tiny underrun ticks between PCM blocks.
        const minimumLeadSeconds =
          TTS_PCM_MIN_SCHEDULE_LEAD_MS /
          1000;

        const startAt = Math.max(
          context.currentTime +
            minimumLeadSeconds,
          Number(
            ttsNextPlaybackTimeRef.current ||
              0
          )
        );

        const endAt =
          startAt +
          audioBuffer.duration;

        ttsNextPlaybackTimeRef.current =
          endAt;

        ttsPcmSourcesRef.current.add(
          source
        );

        source.onended = () => {
          try {
            source.disconnect?.();
          } catch {}

          ttsPcmSourcesRef.current.delete(
            source
          );

          if (
            requestId ===
              ttsRequestIdRef.current &&
            ttsStreamCompleteRef.current &&
            ttsPcmSourcesRef.current.size ===
              0 &&
            ttsAudioContextRef.current ===
              context
          ) {
            ttsAudioContextRef.current =
              null;
            ttsNextPlaybackTimeRef.current =
              0;

            try {
              const closing =
                context.close?.();

              if (
                closing &&
                typeof closing.catch ===
                  "function"
              ) {
                closing.catch(() => {});
              }
            } catch {}
          }
        };

        source.start(startAt);
        return true;
      },
      []
    );

  const speakWithBrowserFallback =
    useCallback(
      (
        rawText,
        languageCode
      ) => {
        try {
          if (
            typeof window === "undefined"
          ) {
            return false;
          }

          const locale =
            normalizeTtsLocale(
              languageCode
            ) ||
            TTS_DEFAULT_LANGUAGE;

          const speechText =
            prepareBrowserSpeechText(
              rawText,
              locale
            );

          if (!speechText) {
            return false;
          }

          const synth =
            window.speechSynthesis;

          const Utter =
            window.SpeechSynthesisUtterance;

          if (!synth || !Utter) {
            return false;
          }

          try {
            synth.cancel();
          } catch {}

          const utterance =
            new Utter(speechText);

          utterance.lang = locale;
          utterance.rate = 1;
          utterance.pitch = 1;
          utterance.volume = 1;

          try {
            const voices =
              synth.getVoices?.() || [];

            const normalizedLocale =
              locale
                .toLowerCase()
                .replace(/_/g, "-");

            const baseLanguage =
              normalizedLocale.split(
                "-"
              )[0];

            const voiceLocale = (
              voice
            ) =>
              String(
                voice?.lang || ""
              )
                .toLowerCase()
                .replace(/_/g, "-");

            const knownFemale = (
              voice
            ) =>
              FEMALE_BROWSER_VOICE_RE.test(
                String(
                  voice?.name || ""
                )
              );

            const preferred =
              voices.find(
                (voice) =>
                  voiceLocale(voice) ===
                    normalizedLocale &&
                  knownFemale(voice)
              ) ||
              voices.find(
                (voice) =>
                  voiceLocale(
                    voice
                  ).startsWith(
                    `${baseLanguage}-`
                  ) &&
                  knownFemale(voice)
              ) ||
              voices.find(
                (voice) =>
                  voiceLocale(voice) ===
                  normalizedLocale
              ) ||
              voices.find(
                (voice) =>
                  voiceLocale(
                    voice
                  ).startsWith(
                    `${baseLanguage}-`
                  ) ||
                  voiceLocale(voice) ===
                    baseLanguage
              ) ||
              voices.find(
                knownFemale
              ) ||
              null;

            if (preferred) {
              utterance.voice =
                preferred;
            }
          } catch {}

          utterance.onend = () => {
            if (
              ttsUtterRef.current ===
              utterance
            ) {
              ttsUtterRef.current =
                null;
            }
          };

          utterance.onerror = (
            event
          ) => {
            console.warn(
              "[TTS][browser] fallback failed:",
              event?.error ||
                event
            );

            if (
              ttsUtterRef.current ===
              utterance
            ) {
              ttsUtterRef.current =
                null;
            }
          };

          ttsUtterRef.current =
            utterance;

          console.log(
            "[TTS][browser] fallback started",
            {
              language: locale,
              chars:
                speechText.length,
            }
          );

          synth.speak(utterance);
          return true;
        } catch (error) {
          console.warn(
            "[TTS][browser] fallback unavailable:",
            error?.message ||
              error
          );
          return false;
        }
      },
      []
    );

  const handleSpeak = async (
    rawText,
    explicitLanguage = ""
  ) => {
    const textToSpeak =
      String(rawText || "").trim();

    if (!textToSpeak) return;

    // Stop earlier backend audio, browser speech, and in-flight fetches.
    stopActiveTts();

    const requestId =
      ++ttsRequestIdRef.current;

    const dominantLanguageHint =
      inferTtsDominantLocale(
        textToSpeak,
        explicitLanguage,
        i18n?.resolvedLanguage ||
          i18n?.language ||
          (typeof navigator !==
          "undefined"
            ? navigator.language
            : "")
      );

    const backendUrl =
      getTtsBackendUrl();

    const fallbackToBrowser =
      () => {
        if (
          requestId !==
          ttsRequestIdRef.current
        ) {
          return false;
        }

        return speakWithBrowserFallback(
          textToSpeak,
          dominantLanguageHint
        );
      };

    if (
      !backendUrl ||
      !currentUser
    ) {
      console.warn(
        "[TTS] backend unavailable before request; using browser fallback",
        {
          backendConfigured:
            !!backendUrl,
          authenticated:
            !!currentUser,
          dominantLanguageHint,
        }
      );

      fallbackToBrowser();
      return;
    }

    // Create/resume Web Audio synchronously in the click path before awaiting
    // Firebase/network work. This avoids autoplay-policy surprises later.
    const audioContext =
      createTtsAudioContext();

    if (!audioContext) {
      console.warn(
        "[TTS] Web Audio unavailable; using browser fallback"
      );
      fallbackToBrowser();
      return;
    }

    let controller = null;
    let firstAudioTimeoutId = null;
    let stallTimeoutId = null;
    let reader = null;

    let receivedAudioBytes = 0;
    let scheduledBackendAudio = false;

    // Incoming fetch chunks are arbitrary network boundaries. Keep one rolling
    // byte queue and release stable PCM blocks to Web Audio.
    let pcmQueueBytes =
      new Uint8Array(0);
    let oddByteCarry =
      new Uint8Array(0);

    const clearFirstAudioTimeout =
      () => {
        if (
          firstAudioTimeoutId
        ) {
          clearTimeout(
            firstAudioTimeoutId
          );
          firstAudioTimeoutId =
            null;
        }
      };

    const clearStallTimeout =
      () => {
        if (stallTimeoutId) {
          clearTimeout(
            stallTimeoutId
          );
          stallTimeoutId =
            null;
        }
      };

    const abortCurrentRequest =
      (reason) => {
        if (
          requestId !==
          ttsRequestIdRef.current
        ) {
          return;
        }

        console.warn(
          "[TTS] aborting backend stream:",
          reason
        );

        try {
          controller?.abort?.();
        } catch {}
      };

    const armStreamStallTimeout =
      () => {
        clearStallTimeout();

        stallTimeoutId = setTimeout(
          () => {
            abortCurrentRequest(
              "stream stalled"
            );
          },
          TTS_STREAM_STALL_TIMEOUT_MS
        );
      };

    const consumePcmBytes = (
      incomingBytes
    ) => {
      if (
        requestId !==
        ttsRequestIdRef.current
      ) {
        return false;
      }

      const incoming =
        incomingBytes instanceof
        Uint8Array
          ? incomingBytes
          : new Uint8Array(0);

      if (!incoming.length) {
        return false;
      }

      const combined =
        concatUint8Arrays(
          oddByteCarry,
          incoming
        );

      const evenLength =
        combined.length -
        (combined.length %
          TTS_PCM_SAMPLE_WIDTH_BYTES);

      if (evenLength <= 0) {
        oddByteCarry =
          combined.slice();
        return false;
      }

      const playableBytes =
        combined.subarray(
          0,
          evenLength
        );

      oddByteCarry =
        evenLength <
        combined.length
          ? combined
              .subarray(
                evenLength
              )
              .slice()
          : new Uint8Array(0);

      const samples =
        pcm16LeBytesToFloat32(
          playableBytes
        );

      if (!samples.length) {
        return false;
      }

      const scheduled =
        scheduleTtsPcmSamples(
          samples,
          audioContext,
          requestId
        );

      if (scheduled) {
        scheduledBackendAudio = true;
      }

      return scheduled;
    };

    const drainPcmQueue = (
      {
        force = false,
      } = {}
    ) => {
      if (
        requestId !==
        ttsRequestIdRef.current
      ) {
        return false;
      }

      let scheduledAny = false;

      // Before first playback, hold the slightly larger startup reserve.
      if (
        !scheduledBackendAudio &&
        !force &&
        pcmQueueBytes.length <
          TTS_PCM_START_BUFFER_BYTES
      ) {
        return false;
      }

      while (pcmQueueBytes.length) {
        // During normal streaming, emit fixed-size 120 ms blocks. At final
        // stream completion, force=true flushes the remaining short tail.
        if (
          !force &&
          pcmQueueBytes.length <
            TTS_PCM_PLAYBACK_BLOCK_BYTES
        ) {
          break;
        }

        const requestedLength =
          force
            ? Math.min(
                pcmQueueBytes.length,
                TTS_PCM_PLAYBACK_BLOCK_BYTES
              )
            : TTS_PCM_PLAYBACK_BLOCK_BYTES;

        const evenLength =
          requestedLength -
          (requestedLength %
            TTS_PCM_SAMPLE_WIDTH_BYTES);

        if (evenLength <= 0) {
          break;
        }

        const block =
          pcmQueueBytes
            .subarray(
              0,
              evenLength
            )
            .slice();

        pcmQueueBytes =
          pcmQueueBytes
            .subarray(
              evenLength
            )
            .slice();

        if (
          consumePcmBytes(block)
        ) {
          scheduledAny = true;
        }

        if (
          !scheduledBackendAudio &&
          !force
        ) {
          break;
        }
      }

      return scheduledAny;
    };

    try {
      const headers =
        await getAuthHeaders();

      if (
        requestId !==
        ttsRequestIdRef.current
      ) {
        return;
      }

      if (!headers) {
        throw new Error(
          "TTS authentication is not ready."
        );
      }

      controller =
        typeof AbortController !==
        "undefined"
          ? new AbortController()
          : null;

      ttsAbortRef.current =
        controller;

      // Because the backend does not send HTTP 200 until it has the first Google
      // PCM chunk, this timer measures "can backend/provider actually start?"
      if (controller) {
        firstAudioTimeoutId =
          setTimeout(() => {
            abortCurrentRequest(
              "first audio timeout"
            );
          }, TTS_FIRST_AUDIO_TIMEOUT_MS);
      }

      console.log(
        "[TTS] requesting streaming backend",
        {
          route: TTS_ROUTE,
          transport:
            "streaming-pcm",
          audio:
            "pcm_s16le",
          sampleRate:
            TTS_PCM_SAMPLE_RATE_HZ,
          channels:
            TTS_PCM_CHANNELS,
          dominantLanguageHint,
          firstAudioTimeoutMs:
            TTS_FIRST_AUDIO_TIMEOUT_MS,
          chars:
            textToSpeak.length,
        }
      );

      const response =
        await fetch(
          backendUrl,
          {
            method: "POST",
            headers,
            body: JSON.stringify({
              text: textToSpeak,

              // Frontend decides dominant language only. Backend owns final
              // mixed-language/code-switch segmentation.
              language_hint:
                dominantLanguageHint,

              clean_markdown: true,
              include_sources: false,
            }),
            ...(controller
              ? {
                  signal:
                    controller.signal,
                }
              : null),
          }
        );

      // Backend prefetches first provider PCM before it commits HTTP 200.
      // If fetch resolved with 200, Google has already produced audio.
      clearFirstAudioTimeout();

      if (
        requestId !==
        ttsRequestIdRef.current
      ) {
        return;
      }

      if (!response.ok) {
        const detail =
          await response
            .text()
            .catch(() => "");

        throw new Error(
          detail ||
            `TTS backend failed (${response.status}).`
        );
      }

      const contentType =
        String(
          response.headers.get(
            "content-type"
          ) || ""
        ).toLowerCase();

      if (
        !contentType.includes(
          "application/octet-stream"
        )
      ) {
        throw new Error(
          `Unexpected TTS stream type: ${
            contentType ||
            "missing"
          }`
        );
      }

      if (!response.body) {
        throw new Error(
          "TTS backend returned no readable stream."
        );
      }

      reader =
        response.body.getReader();

      armStreamStallTimeout();

      while (true) {
        const {
          done,
          value,
        } = await reader.read();

        clearStallTimeout();

        if (
          requestId !==
          ttsRequestIdRef.current
        ) {
          try {
            await reader.cancel();
          } catch {}
          return;
        }

        if (done) {
          break;
        }

        const chunk =
          value instanceof
          Uint8Array
            ? value
            : new Uint8Array(
                value || 0
              );

        if (!chunk.length) {
          armStreamStallTimeout();
          continue;
        }

        receivedAudioBytes +=
          chunk.length;

        const hadScheduledAudio =
          scheduledBackendAudio;

        pcmQueueBytes =
          concatUint8Arrays(
            pcmQueueBytes,
            chunk
          );

        drainPcmQueue();

        if (
          !hadScheduledAudio &&
          scheduledBackendAudio
        ) {
          console.log(
            "[TTS] backend PCM playback started",
            {
              provider:
                "google-cloud",
              model:
                "Chirp3-HD",
              voice:
                "Erinome",
              gender:
                "FEMALE",
              dominantLanguageHint,
              startBufferMs:
                TTS_PCM_START_BUFFER_MS,
              playbackBlockMs:
                TTS_PCM_PLAYBACK_BLOCK_MS,
              minimumScheduleLeadMs:
                TTS_PCM_MIN_SCHEDULE_LEAD_MS,
            }
          );
        }

        armStreamStallTimeout();
      }

      clearStallTimeout();

      // Very short answers or the final partial PCM block may finish below the
      // normal buffering thresholds. Flush the remaining queue once the network
      // stream is complete.
      drainPcmQueue({
        force: true,
      });

      if (
        !receivedAudioBytes ||
        !scheduledBackendAudio
      ) {
        throw new Error(
          "TTS backend returned no playable PCM audio."
        );
      }

      // A single trailing byte cannot form a signed 16-bit PCM sample.
      if (oddByteCarry.length) {
        console.warn(
          "[TTS] ignored incomplete trailing PCM byte",
          {
            bytes:
              oddByteCarry.length,
          }
        );
      }

      ttsStreamCompleteRef.current =
        true;

      console.log(
        "[TTS] backend PCM stream complete",
        {
          provider:
            "google-cloud",
          audio:
            "pcm_s16le",
          sampleRate:
            TTS_PCM_SAMPLE_RATE_HZ,
          channels:
            TTS_PCM_CHANNELS,
          receivedAudioBytes,
          dominantLanguageHint,
          startBufferMs:
            TTS_PCM_START_BUFFER_MS,
          playbackBlockMs:
            TTS_PCM_PLAYBACK_BLOCK_MS,
          minimumScheduleLeadMs:
            TTS_PCM_MIN_SCHEDULE_LEAD_MS,
        }
      );

      // If the final source already ended by the time the network closes,
      // clean the AudioContext immediately.
      if (
        ttsPcmSourcesRef.current.size ===
          0 &&
        ttsAudioContextRef.current ===
          audioContext
      ) {
        ttsAudioContextRef.current =
          null;
        ttsNextPlaybackTimeRef.current =
          0;

        try {
          const closing =
            audioContext.close?.();

          if (
            closing &&
            typeof closing.catch ===
              "function"
          ) {
            closing.catch(() => {});
          }
        } catch {}
      }
    } catch (error) {
      if (
        requestId !==
        ttsRequestIdRef.current
      ) {
        return;
      }

      const timedOut =
        error?.name ===
        "AbortError";

      console.warn(
        "[TTS] streaming backend failed; using browser fallback:",
        timedOut
          ? "backend request/stream timed out"
          : error?.message ||
              error,
        {
          receivedAudioBytes,
          backendPlaybackHadStarted:
            scheduledBackendAudio,
        }
      );

      // If a stream dies after partial Erinome playback, stop the partial queue
      // and restart the answer through the emergency browser voice. This keeps
      // the user from being left with a silently truncated answer.
      clearTtsPcmPlayback();
      fallbackToBrowser();
    } finally {
      clearFirstAudioTimeout();
      clearStallTimeout();

      if (
        requestId ===
          ttsRequestIdRef.current &&
        ttsAbortRef.current ===
          controller
      ) {
        ttsAbortRef.current =
          null;
      }
    }
  };

  // Stop read-aloud when this ChatInterface unmounts.
  useEffect(() => {
    return () => {
      ttsRequestIdRef.current +=
        1;

      try {
        ttsAbortRef.current?.abort?.();
      } catch {}

      try {
        if (
          typeof window !==
          "undefined"
        ) {
          window.speechSynthesis?.cancel?.();
        }
      } catch {}

      const sources =
        ttsPcmSourcesRef.current ||
        new Set();

      for (const source of sources) {
        try {
          source.onended = null;
          source.stop?.(0);
        } catch {}

        try {
          source.disconnect?.();
        } catch {}
      }

      sources.clear();

      const context =
        ttsAudioContextRef.current;

      ttsAudioContextRef.current =
        null;
      ttsNextPlaybackTimeRef.current =
        0;
      ttsStreamCompleteRef.current =
        false;
      ttsAbortRef.current = null;
      ttsUtterRef.current = null;

      if (context) {
        try {
          const closing =
            context.close?.();

          if (
            closing &&
            typeof closing.catch ===
              "function"
          ) {
            closing.catch(() => {});
          }
        } catch {}
      }
    };
  }, []);

  // --------------------------
  // ✅ Copy/Edit → clipboard + inject into ChatInput + focus
  // --------------------------
  const injectDraft = async (text) => {
    const s = (text || "").trim();
    if (!s) return;

    try {
      await navigator.clipboard.writeText(s);
    } catch {}

    try {
      window.dispatchEvent(new CustomEvent("asklawly_chat_draft", { detail: { text: s } }));
    } catch {}

    try {
      inputRef?.current?.focus?.();
    } catch {}
  };

  // --------------------------
  // ✅ Small “clicked” feedback for icons
  // --------------------------
  const [iconFlash, setIconFlash] = useState(null);
  const flashIcon = (key) => {
    setIconFlash(key);
    setTimeout(() => setIconFlash(null), 600);
  };
  const iconStyle = (key) => ({
    ...styles.icon,
    color: iconFlash === key ? "#0b5fff" : styles.icon.color,
    transform: iconFlash === key ? "scale(1.08)" : "scale(1)",
    transition: "transform 120ms ease, color 120ms ease",
  });

  // --------------------------
  // ✅ Empty-state workspace router
  // --------------------------
  const focusComposer = useCallback(() => {
    const run = () => {
      try {
        inputRef?.current?.focus?.();
      } catch {}
      try {
        window.dispatchEvent(new Event("asklawly_focus_chat_input"));
      } catch {}
    };

    try {
      requestAnimationFrame(run);
    } catch {
      run();
    }
    setTimeout(run, 60);
    setTimeout(run, 220);
  }, [inputRef]);

  const clearPendingFollowUp = useCallback(() => {
    setPendingFollowUp(null);
    try {
      window.dispatchEvent(new CustomEvent("asklawly_followup_context_clear"));
    } catch {}
  }, []);

  const handleFollowUpCompose = useCallback(
    (followUp, originMode = "auto", parentMessageId = "") => {
      if (loading) return;
      const normalized = normalizeFollowUpMetaItem(followUp);
      if (!normalized) return;

      const context = {
        ...normalized,
        parent_message_id: normalized.parent_message_id || String(parentMessageId || ""),
        origin_mode: normalizeChatMode(originMode),
      };

      setPendingFollowUp(context);

      // ChatInput will consume pendingFollowUp as a prop. This event is also emitted as
      // a compatibility bridge for the exact ChatInput version we update next.
      try {
        window.dispatchEvent(
          new CustomEvent("asklawly_followup_context", {
            detail: { followUp: context },
          })
        );
      } catch {}

      focusComposer();
    },
    [focusComposer, loading]
  );

  const handleFollowUpOption = useCallback(
    async (followUp, option, originMode = "auto", parentMessageId = "") => {
      if (loading) return;
      const answer = String(option || "").trim();
      const normalized = normalizeFollowUpMetaItem(followUp);
      if (!answer || !normalized) return;

      const context = {
        ...normalized,
        parent_message_id: normalized.parent_message_id || String(parentMessageId || ""),
        origin_mode: normalizeChatMode(originMode),
      };

      // Direct choice answers respect the user's CURRENT composer mode. originMode is
      // context only; it never silently overrides a manual Auto/Research selection.
      await sendMessage({
        text: answer,
        attachment: null,
        mode: normalizeChatMode(composerMode || originMode),
        message_type: "follow_up_answer",
        follow_up: context,
      });
    },
    [composerMode, loading, sendMessage]
  );

  // --------------------------
  // ✅ Document Intelligence actions
  // --------------------------
  const resolveDocumentForChat = useCallback(
    (document) => {
      const source =
        document && typeof document === "object"
          ? document
          : {};

      const documentId = String(
        source.id ||
          source.documentId ||
          source.document_id ||
          ""
      ).trim();

      if (!documentId) return null;

      const authoritativeDocument =
        documentIntelligenceDocuments.find(
          (item) =>
            String(
              item?.id ||
                item?.documentId ||
                ""
            ) === documentId
        ) || null;

      const eligibilitySource =
        authoritativeDocument || source;

      if (!isDocumentChatEligible(eligibilitySource)) {
        return null;
      }

      const fallback = messageDocumentFallbacks.find(
        (item) => String(item?.id || "") === documentId
      );

      const fileName = String(
        source.name ||
          source.fileName ||
          fallback?.name ||
          fallback?.fileName ||
          fallback?.attachment?.fileName ||
          "Document"
      );

      const sourceAttachment =
        source.attachment &&
        typeof source.attachment === "object"
          ? source.attachment
          : {};

      const explicitOriginalFileUrl = String(
        sourceAttachment.fileUrl ||
          sourceAttachment.file_url ||
          source.originalFileUrl ||
          source.original_file_url ||
          source.fileUrl ||
          source.file_url ||
          ""
      ).trim();

      const explicitOriginalFileUrls = Array.isArray(
        sourceAttachment.fileUrls
      )
        ? sourceAttachment.fileUrls
        : Array.isArray(
            sourceAttachment.file_urls
          )
        ? sourceAttachment.file_urls
        : Array.isArray(source.fileUrls)
        ? source.fileUrls
        : Array.isArray(source.file_urls)
        ? source.file_urls
        : [];

      const attachment = {
        ...(fallback?.attachment || {}),
        ...sourceAttachment,
        ...(explicitOriginalFileUrl
          ? {
              fileUrl: explicitOriginalFileUrl,
            }
          : {}),
        ...(explicitOriginalFileUrls.length
          ? {
              fileUrls:
                explicitOriginalFileUrls,
            }
          : {}),
        documentId,
        fileName,
        documentStatus:
          source.status ||
          source.documentStatus ||
          source.document_status ||
          fallback?.attachment?.documentStatus ||
          null,
        documentType:
          source.type ||
          source.documentType ||
          source.document_type ||
          fallback?.attachment?.documentType ||
          null,
        pageCount:
          source.pageCount ??
          source.page_count ??
          fallback?.pageCount ??
          fallback?.attachment?.pageCount ??
          null,
        attachmentMode:
          source.attachmentMode ||
          source.attachment_mode ||
          fallback?.attachment?.attachmentMode ||
          "chat_document_intelligence",
      };

      return {
        ...(fallback || {}),
        ...source,
        id: documentId,
        documentId,
        name: fileName,
        fileName,
        attachment,
      };
    },
    [
      documentIntelligenceDocuments,
      messageDocumentFallbacks,
    ]
  );

  const clearPendingDocumentContext = useCallback(() => {
    setPendingDocumentContext(null);
    try {
      window.dispatchEvent(new CustomEvent("asklawly_document_context_clear"));
    } catch {}
  }, []);

  const handleAskDocument = useCallback(
    ({ document } = {}) => {
      if (normalizeDocumentChatMode(documentChatMode) === "off") return;

      const resolvedDocument = resolveDocumentForChat(document);
      if (!resolvedDocument?.id) return;

      // Broad document scope, page focus, and structured follow-up scope are
      // mutually exclusive. ChatInterface owns the state; ChatInput only presents it.
      clearPendingFollowUp();
      setDocumentPageScope(null);
      selectDocumentForCurrentThread(
        resolvedDocument.id
      );
      setPendingDocumentContext({ document: resolvedDocument });

      try {
        window.dispatchEvent(
          new CustomEvent("asklawly_document_context", {
            detail: {
              documentId: resolvedDocument.id,
              fileName:
                resolvedDocument.name ||
                resolvedDocument.fileName ||
                "Document",
            },
          })
        );
      } catch {}

      focusComposer();
    },
    [
      clearPendingDocumentContext,
      clearPendingFollowUp,
      documentChatMode,
      focusComposer,
      resolveDocumentForChat,
      selectDocumentForCurrentThread,
    ]
  );

  const handleDocumentAction = useCallback(
    async ({ action, actionId: suppliedActionId, document, scope } = {}) => {
      if (
        loading ||
        !action ||
        normalizeDocumentChatMode(documentChatMode) === "off"
      ) {
        return;
      }

      // Cross-document retrieval needs the new page-aware pipeline. Keep the UI
      // architecture ready without pretending today's attachment path can preserve
      // document boundaries safely.
      if (scope === "all_documents") {
        showToast(
          {
            title: "Cross-document analysis",
            message: "This control will activate with the new document retrieval pipeline.",
            hint: "",
            showSpinner: false,
          },
          { autoCloseMs: 3200 }
        );
        return;
      }

      const resolvedDocument = resolveDocumentForChat(document);
      if (!resolvedDocument?.id) return;

      const actionId =
        normalizeDocumentActionId(
          suppliedActionId ||
            action.actionId ||
            action.action_id ||
            action.id
        ) || "analysis";
      const stateKey = `${resolvedDocument.id}:${actionId}`;
      selectDocumentForCurrentThread(
        resolvedDocument.id
      );
      setDocumentActionState((prev) => ({
        ...(prev || {}),
        [stateKey]: { loading: true, completed: false },
      }));

      try {
        const actionPageScope =
          activeDocumentPageScope &&
          (!activeDocumentPageScope.documentId ||
            String(activeDocumentPageScope.documentId) ===
              String(resolvedDocument.id))
            ? activeDocumentPageScope
            : null;

        await sendMessage({
          text: buildDocumentActionPrompt(action, resolvedDocument),
          display_text: `Document Intelligence · ${action.label || actionId} · ${resolvedDocument.name || "Document"}`,
          attachment: {
            ...(resolvedDocument.attachment || {}),
            documentId: resolvedDocument.id,
            reusedDocument: true,
            displayHidden: true,
          },
          document_id: resolvedDocument.id,
          document_action_id: actionId,
          ...(actionPageScope
            ? { document_page_scope: actionPageScope }
            : {}),
          mode: composerMode,
          message_type: "document_action",
        });

        setDocumentActionState((prev) => ({
          ...(prev || {}),
          [stateKey]: { loading: false, completed: true },
        }));
      } catch (error) {
        setDocumentActionState((prev) => ({
          ...(prev || {}),
          [stateKey]: { loading: false, completed: false, error: true },
        }));

        showToast(
          {
            title: "Document analysis failed",
            message: toUserFacingChatError(
              error,
              "Unable to analyze this document right now. Please try again."
            ),
            hint: "",
            showSpinner: false,
          },
          { autoCloseMs: 4500 }
        );
      }
    },
    [
      activeDocumentPageScope,
      composerMode,
      documentChatMode,
      loading,
      resolveDocumentForChat,
      selectDocumentForCurrentThread,
      sendMessage,
      showToast,
    ]
  );

  const handleDocumentPageScopeChange = useCallback(
    (nextScope, document, meta = {}) => {
      const resolvedDocument =
        resolveDocumentForChat(document);

      if (!resolvedDocument?.id) {
        setDocumentPageScope(null);
        return;
      }

      if (
        normalizeDocumentChatMode(documentChatMode) ===
        "off"
      ) {
        setDocumentPageScope(null);
        return;
      }

      const normalizedScope =
        normalizeDocumentPageScope(
          nextScope,
          resolvedDocument.pageCount
        );

      if (!normalizedScope) {
        setDocumentPageScope((current) =>
          String(current?.documentId || "") ===
          String(resolvedDocument.id)
            ? null
            : current
        );
        return;
      }

      // Selecting a Page Map section/manual range scopes the NEXT document
      // question. It does not itself send a second AI request. A page/section
      // focus is more specific than a broad Ask This Document context.
      clearPendingFollowUp();
      clearPendingDocumentContext();
      selectDocumentForCurrentThread(
        resolvedDocument.id
      );
      setDocumentPageScope({
        ...normalizedScope,
        documentId: resolvedDocument.id,
        source: String(
          meta?.source ||
            normalizedScope.source ||
            "page_scope"
        ),
      });
      focusComposer();
    },
    [
      clearPendingFollowUp,
      documentChatMode,
      focusComposer,
      resolveDocumentForChat,
      selectDocumentForCurrentThread,
    ]
  );

  const handleOpenDocument = useCallback(
    async (document, options = {}) => {
      const resolvedDocument =
        resolveDocumentForChat(document);

      const identity =
        documentFileIdentity(resolvedDocument);

      if (!resolvedDocument || !identity.documentId) {
        showToast(
          {
            title: "File unavailable",
            message:
              "This file is not available to preview right now.",
            hint: "",
            showSpinner: false,
          },
          { autoCloseMs: 3500 }
        );
        return;
      }

      const fileAccessBase =
        getFileAccessBase();

      if (!fileAccessBase) {
        showToast(
          {
            title: "File unavailable",
            message:
              "File access is not configured right now.",
            hint: "",
            showSpinner: false,
          },
          { autoCloseMs: 3500 }
        );
        return;
      }

      try {
        if (!currentUser) {
          throw new Error(
            "Authentication unavailable."
          );
        }

        const token =
          await currentUser.getIdToken();

        if (!token) {
          throw new Error(
            "Authentication unavailable."
          );
        }

        const authorizationHeader =
          `Bearer ${token}`;

        const encodedDocumentId =
          encodeURIComponent(identity.documentId);

        const fileAccessUrl =
          `${fileAccessBase}/files/document/${encodedDocumentId}`;

        const browserTicketUrl =
          `${fileAccessUrl}/browser-ticket`;

        let browserViewUrl = "";

        // PDF.js can use Authorization headers for its in-app Range requests.
        // Office/generic iframe viewers cannot. For those file types, obtain one
        // short-lived opaque AskLawly ticket before opening the viewer.
        if (!identity.isPdf) {
          const ticketResponse = await fetch(
            browserTicketUrl,
            {
              method: "POST",
              headers: {
                Authorization:
                  authorizationHeader,
              },
              credentials: "omit",
              mode: "cors",
            }
          );

          const ticketPayload =
            await ticketResponse
              .json()
              .catch(() => null);

          if (!ticketResponse.ok) {
            throw new Error(
              ticketPayload?.error ||
                `Browser file access request failed (${ticketResponse.status}).`
            );
          }

          const ticketPath = String(
            ticketPayload?.ticketPath ||
              ticketPayload?.path ||
              ""
          ).trim();

          if (!ticketPath) {
            throw new Error(
              "Browser file access did not return an AskLawly ticket."
            );
          }

          browserViewUrl =
            new URL(
              ticketPath,
              browserTicketUrl
            ).toString();
        }

        const viewerRecord =
          buildDocumentIntelligenceGatewayViewerRecord({
            document: resolvedDocument,
            fileAccessUrl,
            browserTicketUrl,
            authorizationHeader,
            browserViewUrl,
            openSource:
              options?.source ||
              "document_intelligence_original_gateway",
          });

        if (!viewerRecord) {
          throw new Error(
            "Unable to prepare the file viewer."
          );
        }

        setAttachmentViewerRecord(
          viewerRecord
        );
      } catch (error) {
        console.warn(
          "[ChatInterface] File Access Gateway open failed:",
          error?.message || error
        );

        showToast(
          {
            title: "File unavailable",
            message:
              "This file is not available to preview right now.",
            hint: "",
            showSpinner: false,
          },
          { autoCloseMs: 3500 }
        );
      }
    },
    [
      currentUser,
      resolveDocumentForChat,
      showToast,
    ]
  );

  const dismissRouterForTyping = useCallback(() => {
    if (session_id) return;
    if (!isEmpty) return;
    setEmptyRouterDismissed(true);
  }, [session_id, isEmpty]);

  const handleAskOrResearch = useCallback(() => {
    try {
      localStorage.removeItem("chat_input");
    } catch {}
    setInput("");
    setMessages([]);
    setPendingFollowUp(null);
    setStreamingText("");
    setLoading(false);
    setHistoryLoading(false);
    setEmptyRouterDismissed(true);
    focusComposer();
  }, [focusComposer]);

  const handleWorkspaceRoute = useCallback(
    (route) => {
      setEmptyRouterDismissed(true);
      navigate(route);
    },
    [navigate]
  );

  const emptyTaskCards = [
    {
      key: "ask",
      title: "Ask or research",
      icon: <FiMessageCircle />,
      accent: "blue",
      stay: true,
      onClick: handleAskOrResearch,
    },
    {
      key: "doc",
      title: "Create document",
      icon: <FiFileText />,
      accent: "cyan",
      onClick: () => handleWorkspaceRoute("/docwriter/start"),
    },
    {
      key: "contract",
      title: "Review contract",
      icon: <FiShield />,
      accent: "indigo",
      onClick: () => handleWorkspaceRoute("/contract-insight"),
    },
    {
      key: "vision",
      title: "Visual Help",
      icon: <FiEye />,
      accent: "teal",
      onClick: () => handleWorkspaceRoute("/vision-assist"),
    },
  ];

  const renderEmptyState = () => {
    if (session_id) return null;

    if (!isEmpty) return null;
    if (historyLoading) return null;
    if (emptyRouterDismissed || String(input || "").trim()) return null;
    if (Date.now() < emptySuppressUntilRef.current) return null;

    return (
      <div
        className="asklawly-empty-router"
        style={{ ...styles.emptyTopWrap, paddingTop: Math.max(18, topSpacerHeight ? 10 : 18) }}
      >
        <div style={{ ...styles.emptyCenterInner, maxWidth: `${CONTENT_MAX}px` }}>
          <div style={styles.emptyTitle}>What are you working on?</div>
          <div style={styles.emptySubtitle}>Choose a workspace, or ask Lexa directly below.</div>

          <div className="asklawly-task-grid" style={styles.taskGrid}>
            {emptyTaskCards.map((card) => {
              const accent = styles.taskAccent?.[card.accent] || styles.taskAccent.blue;

              return (
                <button
                  key={card.key}
                  type="button"
                  className={`asklawly-task-card asklawly-task-card-${card.accent} ${card.stay ? "is-primary" : ""}`}
                  onClick={card.onClick}
                  style={{
                    ...styles.taskCard,
                    ...(card.stay ? styles.taskCardPrimary : null),
                  }}
                  aria-label={card.title}
                >
                  <span style={{ ...styles.taskIconWrap, background: accent.bg, color: accent.color }} aria-hidden="true">
                    {card.icon}
                  </span>

                  <span style={styles.taskTextWrap}>
                    <span style={styles.taskTitle}>{card.title}</span>
                  </span>

                  <FiChevronRight style={styles.taskChevron} aria-hidden="true" />
                </button>
              );
            })}
          </div>

          {!currentUser && (
            <div style={styles.loginBlock}>
              <div style={styles.loginText}>Please log in to start chatting.</div>
              <button onClick={() => navigate("/login")} style={styles.loginBtn} type="button">
                Log in
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderTypingBubble = () => {
    const now = Date.now();
    const requestLockActive = gotAnyOutputForRequestRef.current && now < (thinkingLockedUntilRef.current || 0);

    const lastMsg = messages[messages.length - 1];
    const lastIsUser = !!lastMsg && lastMsg.user === t("you");

    if (!loading || streamingText) return null;
    if (!lastIsUser) return null;
    if (Date.now() < thinkingSuppressUntilRef.current) return null;
    if (requestLockActive) return null;

    return (
      <div style={{ ...styles.message, ...styles.lexaBubble, marginBottom: "12px" }}>
        <div style={styles.messageHeaderFixed}>
          <strong>Lexa</strong>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={styles.dots}>
            <span style={{ ...styles.dot, animationDelay: "0ms" }} />
            <span style={{ ...styles.dot, animationDelay: "160ms" }} />
            <span style={{ ...styles.dot, animationDelay: "320ms" }} />
          </div>
          <em style={{ opacity: 0.8 }}>{t("thinking")}…</em>
        </div>
      </div>
    );
  };

  const renderDocumentProcessingIndicator = () => {
    const state = documentProcessingState;
    if (!state) return null;

    const hasProgressPercent =
      state.progressPercent !== null &&
      state.progressPercent !== undefined &&
      String(state.progressPercent).trim() !== "";

    const percentRaw = hasProgressPercent
      ? Number(state.progressPercent)
      : Number.NaN;

    const percent =
      Number.isFinite(percentRaw) &&
      percentRaw > 0
        ? Math.max(
            0,
            Math.min(100, percentRaw)
          )
        : null;

    const messageText =
      customerDocumentProgressMessage({
        stage:
          state.progressStage ||
          "processing",
        status:
          state.documentStatus ||
          state.status ||
          "",
        percent:
          percent !== null
            ? percent
            : state.progressPercent,
      });

    return (
      <div
        className="asklawly-di-processing-row no-print"
        aria-live="polite"
        aria-atomic="true"
      >
        <div className="asklawly-di-processing-card">
          <span
            className="asklawly-di-processing-icon"
            aria-hidden="true"
          >
            <FiFileText />
          </span>

          <span className="asklawly-di-processing-copy">
            <span className="asklawly-di-processing-eyebrow">
              Document Intelligence
            </span>
            <strong title={state.fileName || "Document"}>
              {state.fileName || "Document"}
            </strong>
            <small>{messageText}</small>
          </span>

          {percent !== null ? (
            <span className="asklawly-di-processing-percent">
              {Math.round(percent)}%
            </span>
          ) : null}

          <span
            className="asklawly-di-processing-track"
            aria-hidden="true"
          >
            <span
              style={{
                width:
                  percent !== null
                    ? `${percent}%`
                    : "18%",
              }}
              className={
                percent === null
                  ? "is-indeterminate"
                  : ""
              }
            />
          </span>
        </div>
      </div>
    );
  };

  const renderFollowUpQuestions = (followUps, requestedMode = "auto", messageKey = "", parentMessageId = "") => {
    if (!Array.isArray(followUps) || followUps.length === 0) return null;

    const mode = normalizeChatMode(requestedMode);
    const label = followUps.length === 1 ? "Follow-up" : "Follow-up questions";

    return (
      <div className="no-print" style={styles.followUpWrap}>
        <div style={styles.followUpDivider} />
        <div style={styles.followUpLabel}>{label}</div>
        <div style={styles.followUpList}>
          {followUps.map((rawFollowUp, idx) => {
            const followUp = normalizeFollowUpMetaItem(rawFollowUp, "", `${messageKey || "follow-up"}-${idx}`);
            if (!followUp) return null;

            const itemParentId = followUp.parent_message_id || parentMessageId || "";
            const isDirectChoice = followUp.type === "yes_no" || followUp.type === "choice";

            if (!isDirectChoice) {
              return (
                <button
                  key={followUp.id || `${messageKey || "follow-up"}-${idx}`}
                  type="button"
                  onClick={() => handleFollowUpCompose(followUp, mode, itemParentId)}
                  disabled={loading}
                  style={{
                    ...styles.followUpButton,
                    ...(loading ? styles.followUpButtonDisabled : null),
                  }}
                  title="Answer this follow-up in the message box"
                >
                  <span style={styles.followUpText}>{followUp.question}</span>
                  <FiChevronRight style={styles.followUpChevron} aria-hidden="true" />
                </button>
              );
            }

            return (
              <div key={followUp.id || `${messageKey || "follow-up"}-${idx}`} style={styles.followUpChoiceCard}>
                <div style={styles.followUpChoiceQuestion}>{followUp.question}</div>
                <div style={styles.followUpOptionRow}>
                  {followUp.options.map((option) => (
                    <button
                      key={`${followUp.id}-${option}`}
                      type="button"
                      onClick={() => handleFollowUpOption(followUp, option, mode, itemParentId)}
                      disabled={loading}
                      style={{
                        ...styles.followUpOptionButton,
                        ...(loading ? styles.followUpButtonDisabled : null),
                      }}
                      title={`Answer: ${option}`}
                    >
                      {option}
                    </button>
                  ))}
                </div>

                {followUp.allow_custom && (
                  <button
                    type="button"
                    onClick={() => handleFollowUpCompose(followUp, mode, itemParentId)}
                    disabled={loading}
                    style={{
                      ...styles.followUpCustomButton,
                      ...(loading ? styles.followUpButtonDisabled : null),
                    }}
                    title="Type a different or more detailed answer"
                  >
                    <span>Type another answer</span>
                    <FiChevronRight style={styles.followUpChevron} aria-hidden="true" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const bottomReserveStyle = isNarrow
    ? `calc(${bottomSpacerHeight}px + env(safe-area-inset-bottom, 0px) + ${vvInset}px + ${SAFE_BREATHING_PX}px)`
    : 0;

  const historyNavRight = isNarrow
    ? "14px"
    : `max(18px, calc((100% - ${CONTENT_MAX}px) / 2 + 14px))`;

  const historyLoadPreviousTop = `${Math.max(14, topSpacerHeight + 12)}px`;

  const jumpToLatestBottom = isNarrow
    ? `calc(${bottomSpacerHeight}px + env(safe-area-inset-bottom, 0px) + ${vvInset}px + ${SAFE_BREATHING_PX + 10}px)`
    : `${Math.max(76, bottomSpacerHeight + 14)}px`;

  const historyPreviousDisabled = Boolean(
    historyLoading ||
      historyLoadingPrevious ||
      loading ||
      (streamingText && streamingText.trim())
  );

  return (
    <div className="chat-interface" style={styles.chatWrapper}>
      {/* ✅ In-app toast (non-blocking) */}
      <ExportToast toast={toast} onClose={closeToast} />

      {/* ✅ Billing/entitlement dialog: centered inside the AskLawly app.
          Never uses browser alert()/confirm()/popup UI. */}
      <BillingNoticeDialog
        notice={billingNotice}
        onClose={closeBillingNotice}
      />

      {/* ✅ Chat attachment viewer. The shared FileViewerModal owns the dimmed
          backdrop and only closes on a deliberate stationary backdrop click. */}
      <FileViewerModal
        open={!!attachmentViewerRecord}
        onClose={closeAttachmentViewer}
        record={attachmentViewerRecord}
      />

      {/* ✅ In-app PDF transcript viewer. Updated FileViewerModal dims the app
          background consistently on desktop, tablet, and mobile. */}
      <FileViewerModal
        open={!!transcriptRecord}
        onClose={closeTranscriptViewer}
        record={transcriptRecord}
      />

      {documentCatalogResolved &&
      hydratedDocumentSelectionScope === documentSelectionScope &&
      documentIntelligenceDocuments.length > 0 ? (
        <DocumentIntelligence
          presentation="dock"
          documents={documentIntelligenceDocuments}
          defaultOpen={!isPhone}
          open={
            documentIntelligenceDockOpen === null
              ? undefined
              : documentIntelligenceDockOpen
          }
          onOpenChange={(nextOpen) =>
            setDocumentIntelligenceDockOpen(
              Boolean(nextOpen)
            )
          }
          activeDocumentId={activeDocumentId}
          onActiveDocumentChange={(documentId) =>
            selectDocumentForCurrentThread(
              documentId
            )
          }
          documentChatMode={documentChatMode}
          defaultDocumentChatMode="auto"
          onDocumentChatModeChange={handleDocumentChatModeChange}
          documentPageScope={activeDocumentPageScope}
          onDocumentPageScopeChange={handleDocumentPageScopeChange}
          allowAllDocuments={false}
          onAction={handleDocumentAction}
          onAskDocument={handleAskDocument}
          onOpenDocument={handleOpenDocument}
          onDeleteDocument={handleDocumentSoftDeleted}
          actionState={documentActionState}
          persistKey={`asklawly:document-intelligence:${currentUser?.uid || "owner"}`}
          consumer="chat"
          autoLoadCatalog={true}
          catalogLimit={10}
          catalogRefreshKey={documentCatalogRefreshKey}
          onCatalogChange={handleDocumentCatalogChange}
          showProgress={true}
          brandColor="#1E2A78"
        />
      ) : null}

      <div style={styles.chatContainer}>
        <div
          ref={chatWindowRef}
          className="asklawly-chat-window"
          style={{
            ...styles.chatWindow,
            paddingTop: `${topSpacerHeight}px`,
            paddingBottom: bottomReserveStyle,
            scrollbarGutter: "stable both-edges",
            marginRight: isNarrow ? 0 : `${desktopChatScrollbarInsetPx}px`,
            scrollbarWidth: isNarrow ? "none" : "auto",
            msOverflowStyle: isNarrow ? "none" : "auto",
          }}
        >
          <div style={{ ...styles.content, maxWidth: `${CONTENT_MAX}px` }}>
            <div id="chat-log">
              <div
                className="print-only"
                style={{ textAlign: "center", marginBottom: "20px", fontWeight: "bold", fontSize: "18px" }}
              >
                AskLawly
              </div>

              {renderEmptyState()}

              {messages.map((msg, i) => {
                const isUser = msg.user === t("you");
                const isLexa = !isUser && msg.user === "Lexa";
                const bubbleStyle = isUser ? styles.userBubble : styles.lexaBubble;
                const transport = isLexa ? extractStructuredFollowUpEnvelope(msg.text) : null;
                const parsedLexa = isLexa ? splitFollowUpSection(transport?.visibleText ?? msg.text) : null;
                const displayText = parsedLexa?.answerText ?? transport?.visibleText ?? msg.text;
                const parentMessageId = String(msg?.messageId || msg?.message_id || `msg_${i}`);
                const responseMode = normalizeChatMode(msg?.followUpOriginMode || transport?.originMode || msg?.mode || "auto");
                const followUps = isLexa
                  ? buildRenderableFollowUps(
                      parsedLexa?.followUps || [],
                      (Array.isArray(msg?.followUpMeta) && msg.followUpMeta.length > 0
                        ? msg.followUpMeta
                        : transport?.followUps || []),
                      parentMessageId
                    )
                  : [];
                const messageDocument = isUser
                  ? buildDocumentFromMessage(msg, i)
                  : null;
                const messageDocumentId = String(
                  msg.attachment?.documentId ||
                    msg.attachment?.document_id ||
                    ""
                ).trim();
                const documentHiddenByUser =
                  !!messageDocumentId &&
                  hiddenDocumentIds.has(messageDocumentId);
                const attachmentPreviewHidden = !!(
                  documentHiddenByUser ||
                  msg.attachment?.displayHidden ||
                  msg.attachment?.display_hidden ||
                  msg.attachment?.reusedDocument ||
                  msg.attachment?.reused_document
                );
                const documentIntelligenceAttachment =
                  isDocumentIntelligenceChatAttachment(
                    msg.attachment
                  );
                const printableAttachmentName = String(
                  msg.attachment?.fileName ||
                    msg.attachment?.filename ||
                    ""
                ).trim();
                const printableAttachmentDescriptor =
                  printableAttachmentName
                    ? attachmentDescriptor(msg.attachment)
                    : "";

                const userMessageType = isUser
                  ? String(
                      msg?.messageType ||
                        msg?.message_type ||
                        ""
                    )
                      .trim()
                      .toLowerCase()
                  : "";

                const userFollowUpContext = isUser
                  ? normalizeFollowUpContextForSend(
                      msg?.followUp ||
                        msg?.follow_up
                    )
                  : null;

                const userDocumentPageScope = isUser
                  ? normalizeDocumentPageScope(
                      msg?.documentPageScope ||
                        msg?.document_page_scope ||
                        msg?.documentContext?.pageScope ||
                        msg?.documentContext?.page_scope
                    )
                  : null;

                const parsedDocumentAction =
                  isUser
                    ? parseDocumentActionDisplayText(
                        msg?.text
                      )
                    : null;

                const isDocumentActionMessage = !!(
                  isUser &&
                  (userMessageType === "document_action" ||
                    parsedDocumentAction)
                );

                const userDocumentActionId =
                  isUser
                    ? normalizeDocumentActionId(
                        msg?.documentActionId ||
                          msg?.document_action_id ||
                          msg?.documentContext?.actionId ||
                          msg?.documentContext?.action_id
                      )
                    : "";

                const referencedDocumentId = String(
                  msg?.documentReference?.documentId ||
                    msg?.documentReference?.document_id ||
                    msg?.documentScope?.documentId ||
                    msg?.documentScope?.document_id ||
                    msg?.documentContext?.documentId ||
                    msg?.documentContext?.document_id ||
                    msg?.attachment?.documentId ||
                    msg?.attachment?.document_id ||
                    ""
                ).trim();

                const referencedCatalogDocument =
                  referencedDocumentId
                    ? documentIntelligenceDocuments.find(
                        (doc) =>
                          String(
                            doc?.id ||
                              doc?.documentId ||
                              ""
                          ) === referencedDocumentId
                      ) || null
                    : null;

                const referencedDocumentName = String(
                  msg?.documentReference?.fileName ||
                    msg?.documentReference?.file_name ||
                    msg?.documentScope?.fileName ||
                    msg?.documentScope?.file_name ||
                    msg?.attachment?.fileName ||
                    msg?.attachment?.filename ||
                    parsedDocumentAction?.documentName ||
                    referencedCatalogDocument?.fileName ||
                    referencedCatalogDocument?.name ||
                    ""
                )
                  .replace(/\s+/g, " ")
                  .trim();

                const pageRangeLabel =
                  formatDocumentPageScopeRange(
                    userDocumentPageScope
                  );
                const pageScopeDetail =
                  documentPageScopeDetail(
                    userDocumentPageScope
                  );

                const documentActionLabel = String(
                  parsedDocumentAction?.actionLabel ||
                    prettifyDocumentActionId(
                      userDocumentActionId
                    )
                )
                  .replace(/\s+/g, " ")
                  .trim();

                const showAboutDocumentReference = !!(
                  isUser &&
                  !isDocumentActionMessage &&
                  !userDocumentPageScope &&
                  attachmentPreviewHidden &&
                  !!(
                    msg.attachment?.reusedDocument ||
                    msg.attachment?.reused_document ||
                    msg.attachment?.displayHidden ||
                    msg.attachment?.display_hidden
                  ) &&
                  !!(
                    referencedDocumentName ||
                    printableAttachmentName
                  )
                );

                const hasTranscriptContext = !!(
                  isUser &&
                  (userFollowUpContext?.question ||
                    userDocumentPageScope ||
                    isDocumentActionMessage ||
                    showAboutDocumentReference)
                );

                const suppressRawDocumentActionText = !!(
                  isDocumentActionMessage &&
                  parsedDocumentAction
                );

                return (
                  <div
                    key={parentMessageId}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: isUser ? "flex-end" : "flex-start",
                      width: "100%",
                      marginBottom: "16px",
                    }}
                  >
                    <div style={{ ...styles.message, ...bubbleStyle }}>
                      <div style={styles.messageHeaderFixed}>
                        <strong>{msg.user}</strong>
                        <span className="no-print" style={styles.iconGroup}>
                          {!isUser && msg.user === "Lexa" && (
                            <>
                              <FaVolumeUp
                                onClick={() => {
                                  flashIcon(`speak-${i}`);
                                  handleSpeak(
                                    displayText,
                                    msg?.language ||
                                      msg?.languageCode ||
                                      msg?.language_code ||
                                      msg?.responseLanguage ||
                                      msg?.response_language ||
                                      ""
                                  );
                                }}
                                style={iconStyle(`speak-${i}`)}
                                title="Read aloud"
                                aria-label="Read Lexa answer aloud"
                              />
                              <FaCopy
                                onClick={() => {
                                  flashIcon(`copy-lexa-${i}`);
                                  injectDraft(msg.text);
                                }}
                                style={iconStyle(`copy-lexa-${i}`)}
                              />
                            </>
                          )}
                          {isUser && (
                            <>
                              <FaEdit
                                onClick={() => {
                                  flashIcon(`edit-${i}`);
                                  injectDraft(msg.text);
                                }}
                                style={iconStyle(`edit-${i}`)}
                              />
                              <FaCopy
                                onClick={() => {
                                  flashIcon(`copy-user-${i}`);
                                  injectDraft(msg.text);
                                }}
                                style={iconStyle(`copy-user-${i}`)}
                              />
                            </>
                          )}
                        </span>
                      </div>

                      <div style={{ wordBreak: "break-word", whiteSpace: "pre-wrap", userSelect: "text" }}>
                        {hasTranscriptContext ? (
                          <div className="asklawly-turn-context-stack">
                            {userFollowUpContext?.question ? (
                              <div className="asklawly-turn-context asklawly-turn-context-reply">
                                <div className="asklawly-turn-context-head">
                                  <FiMessageCircle aria-hidden="true" />
                                  <span>In reply to Lexa</span>
                                </div>
                                <div className="asklawly-turn-context-main">
                                  {userFollowUpContext.question}
                                </div>
                              </div>
                            ) : null}

                            {isDocumentActionMessage ? (
                              <div className="asklawly-turn-context asklawly-turn-context-document">
                                <div className="asklawly-turn-context-head">
                                  <FiFileText aria-hidden="true" />
                                  <span>Document Intelligence</span>
                                </div>
                                <div className="asklawly-turn-context-main">
                                  {documentActionLabel}
                                </div>
                                {(referencedDocumentName ||
                                  pageRangeLabel) ? (
                                  <div className="asklawly-turn-context-meta">
                                    {[
                                      referencedDocumentName,
                                      pageRangeLabel &&
                                      pageScopeDetail
                                        ? `${pageRangeLabel} · ${pageScopeDetail}`
                                        : pageRangeLabel,
                                    ]
                                      .filter(Boolean)
                                      .join(" · ")}
                                  </div>
                                ) : null}
                              </div>
                            ) : userDocumentPageScope ? (
                              <div className="asklawly-turn-context asklawly-turn-context-document">
                                <div className="asklawly-turn-context-head">
                                  <FiFileText aria-hidden="true" />
                                  <span>Document pages</span>
                                </div>
                                <div className="asklawly-turn-context-main">
                                  {pageRangeLabel}
                                  {pageScopeDetail
                                    ? ` · ${pageScopeDetail}`
                                    : ""}
                                </div>
                                {referencedDocumentName ? (
                                  <div className="asklawly-turn-context-meta">
                                    {referencedDocumentName}
                                  </div>
                                ) : null}
                              </div>
                            ) : showAboutDocumentReference ? (
                              <div className="asklawly-turn-context asklawly-turn-context-document">
                                <div className="asklawly-turn-context-head">
                                  <FiFileText aria-hidden="true" />
                                  <span>About this document</span>
                                </div>
                                <div className="asklawly-turn-context-main">
                                  {referencedDocumentName ||
                                    printableAttachmentName ||
                                    "Selected document"}
                                </div>
                              </div>
                            ) : null}
                          </div>
                        ) : null}

                        {msg.text && isLexa ? (
                          <>
                            <AnswerRenderer text={displayText} />
                            {renderFollowUpQuestions(followUps, responseMode, `msg-${i}`, parentMessageId)}
                          </>
                        ) : (
                          msg.text &&
                          !suppressRawDocumentActionText && (
                            <div className="asklawly-user-message-text">
                              {msg.text}
                            </div>
                          )
                        )}

                        {isUser &&
                        !documentHiddenByUser &&
                        printableAttachmentName ? (
                          <div className="asklawly-print-attachment print-only">
                            <strong>
                              {messageDocument
                                ? "Attached document:"
                                : "Attached file:"}
                            </strong>{" "}
                            <span>{printableAttachmentName}</span>
                            {printableAttachmentDescriptor ? (
                              <small>
                                {" "}
                                · {printableAttachmentDescriptor}
                              </small>
                            ) : null}
                          </div>
                        ) : null}

                        {!attachmentPreviewHidden &&
                          (msg.attachment?.previewUrl ||
                            msg.attachment?.fileUrl ||
                            (msg.attachment?.fileUrls?.length > 0)) && (
                            <div className="asklawly-attachment-area no-print">
                              {msg.attachment?.previewUrl &&
                              !documentIntelligenceAttachment ? (
                                <img
                                  src={msg.attachment.previewUrl}
                                  alt="Preview"
                                  className="asklawly-attachment-preview"
                                />
                              ) : null}

                              {(() => {
                                const urlsToShow = normalizeAttachmentUrls(msg.attachment);
                                if (!urlsToShow.length) return null;

                                const totalPages = urlsToShow.length;
                                const shouldCollapse = totalPages > 1;
                                const key = `msg-${i}`;
                                const isExpanded = !!expandedAttachments[key];
                                const firstUrl = urlsToShow[0];
                                const remainingUrls = urlsToShow.slice(1);
                                const renderAttachmentUrl = (u, idx, pageNumber) => {
                                  if (!u) return null;
                                  const isImage = /\.(png|jpg|jpeg|webp|gif)(\?.*)?$/i.test(u);
                                  return (
                                    <div key={`${u}-${idx}`} style={{ marginTop: idx ? 8 : 0 }}>
                                      {isImage &&
                                      !documentIntelligenceAttachment ? (
                                        <a href={u} target="_blank" rel="noopener noreferrer" style={{ display: "block" }}>
                                          <img
                                            src={u}
                                            alt={`attachment-${pageNumber}`}
                                            className="asklawly-attachment-page"
                                            loading="lazy"
                                          />
                                        </a>
                                      ) : documentIntelligenceAttachment ? (
                                        <div
                                          className="asklawly-file-link asklawly-file-link-static"
                                        >
                                          <span
                                            className="asklawly-file-link-icon"
                                            aria-hidden="true"
                                          >
                                            <FiFileText />
                                          </span>
                                          <span className="asklawly-file-link-copy">
                                            <strong>
                                              {msg.attachment?.fileName ||
                                                `Attached file${
                                                  pageNumber > 1
                                                    ? ` · page ${pageNumber}`
                                                    : ""
                                                }`}
                                            </strong>
                                            <small>
                                              {attachmentDescriptor(
                                                msg.attachment
                                              ) ||
                                                attachmentFileTypeLabel(
                                                  msg.attachment
                                                )}
                                            </small>
                                          </span>
                                        </div>
                                      ) : (
                                        <button
                                          type="button"
                                          className="asklawly-file-link"
                                          onClick={() =>
                                            openChatAttachmentViewer(
                                              msg.attachment,
                                              u,
                                              pageNumber
                                            )
                                          }
                                          title={`View ${
                                            msg.attachment?.fileName ||
                                            "attached file"
                                          } in the app`}
                                        >
                                          <span
                                            className="asklawly-file-link-icon"
                                            aria-hidden="true"
                                          >
                                            <FiFileText />
                                          </span>
                                          <span className="asklawly-file-link-copy">
                                            <strong>
                                              {msg.attachment?.fileName ||
                                                `Attached file${
                                                  pageNumber > 1
                                                    ? ` · page ${pageNumber}`
                                                    : ""
                                                }`}
                                            </strong>
                                            <small>
                                              {attachmentDescriptor(
                                                msg.attachment
                                              ) ||
                                                attachmentFileTypeLabel(
                                                  msg.attachment
                                                )}
                                            </small>
                                          </span>
                                          <span className="asklawly-file-link-action">
                                            {pageNumber > 1
                                              ? `View page ${pageNumber}`
                                              : "View file"}
                                          </span>
                                        </button>
                                      )}
                                    </div>
                                  );
                                };

                                return (
                                  <>
                                    {renderAttachmentUrl(firstUrl, 0, 1)}

                                    {messageDocument &&
                                    !documentHiddenByUser ? (
                                      <div className="asklawly-inline-document-intelligence no-print">
                                        <DocumentIntelligence
                                          presentation="inline"
                                          documents={[messageDocument]}
                                          autoLoadCatalog={false}
                                          allowAllDocuments={false}
                                          documentChatMode={documentChatMode}
                                          defaultDocumentChatMode="auto"
                                          onDocumentChatModeChange={(nextMode) => {
                                            selectDocumentForCurrentThread(
                                              messageDocument.id
                                            );
                                            handleDocumentChatModeChange(nextMode);
                                          }}
                                          documentPageScope={
                                            String(activeDocumentId || "") ===
                                            String(messageDocument.id || "")
                                              ? activeDocumentPageScope
                                              : null
                                          }
                                          onDocumentPageScopeChange={handleDocumentPageScopeChange}
                                          onAction={handleDocumentAction}
                                          onAskDocument={handleAskDocument}
                                          onOpenDocument={handleOpenDocument}
                                          actionState={documentActionState}
                                          brandColor="#1E2A78"
                                        />
                                      </div>
                                    ) : null}

                                    {shouldCollapse && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setExpandedAttachments((prev) => ({
                                            ...prev,
                                            [key]: !prev[key],
                                          }))
                                        }
                                        className="asklawly-pages-toggle no-print"
                                      >
                                        {isExpanded ? "Hide additional pages" : `View all pages (${totalPages})`}
                                      </button>
                                    )}

                                    {isExpanded
                                      ? remainingUrls.map((u, idx) => renderAttachmentUrl(u, idx + 1, idx + 2))
                                      : null}
                                  </>
                                );
                              })()}
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {renderDocumentProcessingIndicator()}

              {streamingText && (
                <div style={{ ...styles.message, ...styles.lexaBubble, marginBottom: "12px" }}>
                  <div style={styles.messageHeaderFixed}>
                    <strong>Lexa</strong>
                  </div>
                  <div style={{ opacity: 0.95 }}>
                    {(() => {
                      const transport = extractStructuredFollowUpEnvelope(streamingText);
                      const parsedStreaming = splitFollowUpSection(transport.visibleText || streamingText);
                      return <AnswerRenderer text={parsedStreaming.answerText || transport.visibleText || streamingText} isStreaming />;
                    })()}
                  </div>
                </div>
              )}

              {renderTypingBubble()}

              <div
                className="print-only"
                style={{ textAlign: "center", marginTop: "20px", fontSize: "12px", color: "#888" }}
              >
                https://asklawly.com — Printed on {new Date().toLocaleDateString()}
              </div>

              <div style={{ height: 20 }} />

              {showUpgrade && (
                <div style={{ ...styles.message, ...styles.warnBubble, marginBottom: "12px" }}>
                  <strong>Notice</strong>: <em>{upgradeMessage}</em>
                </div>
              )}
            </div>

            <div ref={chatScrollRef} />
          </div>
        </div>

        {/* ↑ talks to /chat/history and prepends the next older 40. */}
        {session_id && historyHasMore ? (
          <button
            type="button"
            className="no-print asklawly-history-nav-button"
            aria-label="Load previous messages"
            title="Load previous messages"
            disabled={historyPreviousDisabled}
            onClick={loadPreviousChatHistory}
            style={{
              ...styles.historyNavButton,
              top: historyLoadPreviousTop,
              right: historyNavRight,
              ...(historyPreviousDisabled
                ? styles.historyNavButtonDisabled
                : null),
            }}
          >
            {historyLoadingPrevious ? (
              <span
                aria-hidden="true"
                style={styles.historyNavSpinner}
              />
            ) : (
              <FiChevronUp aria-hidden="true" />
            )}
          </button>
        ) : null}

        {/* ↓ is frontend-only: it simply returns to the newest message. */}
        {showJumpToLatest && messages.length > 0 ? (
          <button
            type="button"
            className="no-print asklawly-history-nav-button"
            aria-label="Jump to latest message"
            title="Jump to latest message"
            onClick={jumpToLatest}
            style={{
              ...styles.historyNavButton,
              bottom: jumpToLatestBottom,
              right: historyNavRight,
            }}
          >
            <FiChevronDown aria-hidden="true" />
          </button>
        ) : null}

        <div
          ref={inputContainerRef}
          onInputCapture={dismissRouterForTyping}
          onPasteCapture={dismissRouterForTyping}
          onKeyDownCapture={(e) => {
            const key = e?.key || "";
            if (key.length === 1 || key === "Backspace" || key === "Delete" || key === "Enter") {
              dismissRouterForTyping();
            }
          }}
          style={{
            ...styles.stickyBar,
            paddingBottom: isNarrow
              ? `calc(env(safe-area-inset-bottom, 0px) + ${vvInset}px + ${SAFE_BREATHING_PX}px)`
              : 0,
          }}
        >
          <div style={{ ...styles.content, maxWidth: `${CONTENT_MAX}px` }}>
            <ChatInput
              onSend={sendMessage}
              inputRef={inputRef}
              activeChat={activeChat}
              sessionId={session_id}
              currentUser={currentUser}
              planType={planType}
              embedded
              maxWidth={CONTENT_MAX}
              disabled={false}
              disabledReason={null}
              mode={composerMode}
              onModeChange={(nextMode) => setComposerMode(normalizeChatMode(nextMode))}
              pendingFollowUp={pendingFollowUp}
              onCancelFollowUp={clearPendingFollowUp}
              documentPageScope={activeDocumentPageScope}
              onClearDocumentPageScope={() =>
                setDocumentPageScope(null)
              }
              pendingDocumentContext={pendingDocumentContext}
              onClearDocumentContext={clearPendingDocumentContext}
              onOpenChatTranscript={handleOpenChatTranscript}
            />
          </div>
        </div>

        <style>
          {`
            /* ================================================================
               ChatInterface.css — merged into ChatInterface.jsx
               Safe-area rules remain scoped to .chat-interface.
               The standalone ChatInterface.css file can now be removed.
               ================================================================ */
            .chat-interface {
              --chat-safe-base: 112px;
              --chat-safe-inset-env: env(safe-area-inset-bottom, 0px);
              --chat-safe-inset-legacy: 0px;
              --chat-safe: max(
                var(--chat-safe-base),
                calc(max(var(--chat-safe-inset-env), var(--chat-safe-inset-legacy)) + 16px)
              );
            }

            @supports (padding: constant(safe-area-inset-bottom)) {
              .chat-interface {
                --chat-safe-inset-legacy: constant(safe-area-inset-bottom);
              }
            }

            @media (max-width: 480px) {
              .chat-interface {
                --chat-safe-base: 128px;
              }
            }

            .chat-interface #chat-log {
              padding-bottom: var(--chat-safe);
              box-sizing: border-box;
              scrollbar-gutter: stable both-edges;
              overscroll-behavior: contain;
            }

            .chat-interface .safe-banner {
              margin: 8px 0;
              line-height: 1.4;
              font-size: 14px;
              display: -webkit-box;
              -webkit-box-orient: vertical;
              -webkit-line-clamp: 2;
              overflow: hidden;
            }

            .chat-interface img {
              max-width: 100%;
              height: auto;
              display: inline-block;
            }

            .asklawly-history-nav-button {
              transition:
                transform 120ms ease,
                box-shadow 120ms ease,
                background 120ms ease,
                opacity 120ms ease;
            }

            .asklawly-history-nav-button:not(:disabled):hover {
              transform: translateY(-1px);
              box-shadow: 0 9px 22px rgba(15, 23, 42, 0.22);
            }

            .asklawly-history-nav-button:focus-visible {
              outline: 3px solid rgba(96, 165, 250, 0.42);
              outline-offset: 2px;
            }

            /* Document Intelligence attachment bridge */
            .asklawly-attachment-area {
              margin-top: 10px;
              width: 100%;
            }

            .asklawly-file-link {
              width: min(100%, 520px);
              min-height: 52px;
              margin-top: 8px;
              padding: 8px 10px;
              display: inline-flex;
              align-items: center;
              gap: 10px;
              box-sizing: border-box;
              border: 1px solid rgba(30, 42, 120, 0.13);
              border-radius: 12px;
              background: rgba(248, 250, 252, 0.96);
              color: #172033;
              text-decoration: none;
              font: inherit;
              text-align: left;
              cursor: pointer;
              transition:
                background 120ms ease,
                border-color 120ms ease,
                box-shadow 120ms ease;
            }

            .asklawly-file-link:hover,
            .asklawly-file-link:focus-visible {
              background: #F8FAFF;
              border-color: rgba(30, 42, 120, 0.28);
              box-shadow: 0 5px 16px rgba(15, 23, 42, 0.07);
              outline: none;
            }

            .asklawly-file-link-static {
              cursor: default;
            }

            .asklawly-file-link-static:hover {
              background: rgba(248, 250, 252, 0.96);
              border-color: rgba(30, 42, 120, 0.13);
              box-shadow: none;
            }

            .asklawly-file-link-icon {
              width: 34px;
              height: 34px;
              flex: 0 0 34px;
              display: inline-grid;
              place-items: center;
              border-radius: 9px;
              background: rgba(30, 42, 120, 0.08);
              color: #1E2A78;
              font-size: 17px;
            }

            .asklawly-file-link-copy {
              min-width: 0;
              flex: 1 1 auto;
              display: flex;
              flex-direction: column;
              gap: 2px;
            }

            .asklawly-file-link-copy strong {
              min-width: 0;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              font-size: 12.5px;
              line-height: 1.2;
              font-weight: 800;
              color: #172033;
            }

            .asklawly-file-link-copy small {
              font-size: 10.5px;
              line-height: 1.2;
              color: #64748B;
              font-weight: 650;
            }

            .asklawly-file-link-action {
              flex: 0 0 auto;
              color: #1E2A78;
              font-size: 11.5px;
              font-weight: 800;
              white-space: nowrap;
            }

            .asklawly-print-attachment {
              margin-top: 7px;
              font-size: 12px;
              line-height: 1.4;
              color: #334155;
            }

            .asklawly-print-attachment small {
              color: #64748B;
              font-size: 11px;
            }

            .asklawly-di-processing-row {
              width: 100%;
              display: flex;
              justify-content: flex-end;
              margin: 0 0 16px;
            }

            .asklawly-di-processing-card {
              width: min(85%, 480px);
              position: relative;
              display: grid;
              grid-template-columns: 40px minmax(0, 1fr) auto;
              grid-template-rows: auto 4px;
              gap: 9px 11px;
              align-items: center;
              padding: 11px 12px 12px;
              border: 1px solid rgba(30, 42, 120, 0.12);
              border-radius: 16px;
              background: linear-gradient(180deg, #FBFCFF 0%, #F6F8FF 100%);
              box-shadow: 0 8px 24px rgba(15, 23, 42, 0.065);
              overflow: hidden;
              box-sizing: border-box;
            }

            .asklawly-di-processing-icon {
              width: 40px;
              height: 40px;
              grid-row: 1;
              display: inline-grid;
              place-items: center;
              border-radius: 12px;
              background: rgba(30, 42, 120, 0.085);
              color: #1E2A78;
              font-size: 18px;
            }

            .asklawly-di-processing-copy {
              min-width: 0;
              grid-column: 2;
              display: flex;
              flex-direction: column;
              gap: 2px;
            }

            .asklawly-di-processing-eyebrow {
              color: #1E2A78;
              font-size: 9.5px;
              line-height: 1.1;
              font-weight: 800;
              letter-spacing: 0.055em;
              text-transform: uppercase;
            }

            .asklawly-di-processing-copy strong {
              min-width: 0;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              color: #172033;
              font-size: 12.5px;
              line-height: 1.2;
              font-weight: 800;
            }

            .asklawly-di-processing-copy small {
              min-width: 0;
              color: #64748B;
              font-size: 10.5px;
              line-height: 1.25;
              font-weight: 650;
              overflow-wrap: anywhere;
            }

            .asklawly-di-processing-percent {
              grid-column: 3;
              min-width: 38px;
              padding: 5px 7px;
              border-radius: 999px;
              background: rgba(30, 42, 120, 0.075);
              color: #1E2A78;
              font-size: 11.5px;
              line-height: 1;
              font-weight: 850;
              text-align: center;
            }

            .asklawly-di-processing-track {
              grid-column: 1 / -1;
              grid-row: 2;
              width: 100%;
              height: 5px;
              overflow: hidden;
              border-radius: 999px;
              background: rgba(148, 163, 184, 0.22);
            }

            .asklawly-di-processing-track > span {
              display: block;
              height: 100%;
              min-width: 2%;
              border-radius: inherit;
              background: #1E2A78;
              transition: width 180ms ease;
            }

            .asklawly-di-processing-track > span.is-indeterminate {
              animation: asklawlyDiProgressSlide 1.15s ease-in-out infinite;
            }

            .asklawly-attachment-preview,
            .asklawly-attachment-page {
              max-width: 100%;
              border-radius: 10px;
              display: block;
            }

            .asklawly-attachment-preview {
              margin-bottom: 8px;
            }

            .asklawly-inline-document-intelligence {
              width: min(100%, 640px);
              margin: 12px 0 2px;
            }

            .asklawly-pages-toggle {
              appearance: none;
              -webkit-appearance: none;
              margin: 10px 0 2px;
              border: 1px solid rgba(30, 42, 120, 0.13);
              background: rgba(248, 250, 252, 0.94);
              color: #1E2A78;
              border-radius: 999px;
              min-height: 34px;
              padding: 6px 12px;
              font: inherit;
              font-size: 12.5px;
              line-height: 1.2;
              font-weight: 750;
              cursor: pointer;
              transition: background 120ms ease, border-color 120ms ease, transform 120ms ease;
            }

            .asklawly-pages-toggle:hover {
              background: rgba(238, 242, 255, 0.96);
              border-color: rgba(30, 42, 120, 0.25);
            }

            .asklawly-pages-toggle:active {
              transform: translateY(1px);
            }

            .asklawly-turn-context-stack {
              width: 100%;
              display: grid;
              gap: 6px;
              margin-bottom: 8px;
            }

            .asklawly-turn-context {
              width: 100%;
              min-width: 0;
              padding: 7px 9px;
              border: 1px solid rgba(71, 85, 105, 0.14);
              border-radius: 9px;
              background: rgba(255, 255, 255, 0.62);
              box-sizing: border-box;
              break-inside: avoid;
            }

            .asklawly-turn-context-document {
              border-color: rgba(30, 42, 120, 0.14);
              background: rgba(255, 255, 255, 0.66);
            }

            .asklawly-turn-context-head {
              display: flex;
              align-items: center;
              gap: 5px;
              margin-bottom: 3px;
              color: #64748B;
              font-size: 9.8px;
              line-height: 1.2;
              font-weight: 800;
              letter-spacing: 0.045em;
              text-transform: uppercase;
            }

            .asklawly-turn-context-head svg {
              flex: 0 0 auto;
              font-size: 12px;
              color: #64748B;
            }

            .asklawly-turn-context-document .asklawly-turn-context-head svg {
              color: #1E2A78;
            }

            .asklawly-turn-context-main {
              min-width: 0;
              color: #1F2937;
              font-size: 12.5px;
              line-height: 1.34;
              font-weight: 650;
              white-space: normal;
              overflow-wrap: anywhere;
            }

            .asklawly-turn-context-meta {
              margin-top: 2px;
              min-width: 0;
              color: #64748B;
              font-size: 10.8px;
              line-height: 1.3;
              white-space: normal;
              overflow-wrap: anywhere;
            }

            .asklawly-user-message-text {
              white-space: pre-wrap;
              overflow-wrap: anywhere;
            }

            .asklawly-active-document-context {
              width: min(100%, 800px);
              margin: 0 auto 6px;
              min-height: 42px;
              display: flex;
              align-items: center;
              gap: 9px;
              padding: 7px 8px 7px 10px;
              border: 1px solid rgba(30, 42, 120, 0.13);
              border-radius: 14px;
              background: rgba(255, 255, 255, 0.96);
              box-shadow: 0 7px 22px rgba(15, 23, 42, 0.07);
              backdrop-filter: blur(10px);
            }

            .asklawly-active-document-context-icon {
              width: 28px;
              height: 28px;
              flex: 0 0 auto;
              display: inline-grid;
              place-items: center;
              border-radius: 9px;
              background: rgba(30, 42, 120, 0.08);
              color: #1E2A78;
            }

            .asklawly-active-document-context-copy {
              min-width: 0;
              flex: 1 1 auto;
              display: flex;
              flex-direction: column;
              gap: 1px;
            }

            .asklawly-active-document-context-copy small {
              color: #64748B;
              font-size: 10px;
              line-height: 1.1;
              font-weight: 700;
              text-transform: uppercase;
              letter-spacing: 0.045em;
            }

            .asklawly-active-document-context-copy strong {
              min-width: 0;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              color: #172033;
              font-size: 12.5px;
              line-height: 1.25;
              font-weight: 800;
            }

            .asklawly-active-document-context > button {
              width: 30px;
              height: 30px;
              flex: 0 0 auto;
              display: inline-grid;
              place-items: center;
              border-radius: 9px;
              border: 1px solid transparent;
              background: transparent;
              color: #64748B;
              cursor: pointer;
            }

            .asklawly-active-document-context > button:hover {
              background: #F1F5F9;
              color: #1E2A78;
            }

            @media (max-width: 640px) {
              .asklawly-inline-document-intelligence {
                margin-top: 10px;
              }

              .asklawly-turn-context {
                padding: 7px 8px;
              }

              .asklawly-turn-context-main {
                font-size: 12.2px;
              }

              .asklawly-active-document-context {
                border-radius: 12px;
                margin-bottom: 5px;
              }

              .asklawly-di-processing-card {
                width: min(92%, 480px);
              }

              .asklawly-file-link {
                width: 100%;
              }
            }

            @keyframes asklawlyDiProgressSlide {
              0% { transform: translateX(-120%); }
              50% { transform: translateX(250%); }
              100% { transform: translateX(620%); }
            }

            @keyframes asklawlyDotPulse {
              0% { transform: translateY(0); opacity: 0.35; }
              50% { transform: translateY(-2px); opacity: 0.9; }
              100% { transform: translateY(0); opacity: 0.35; }
            }

            @media (min-width: 1025px) {
              .chat-interface .asklawly-chat-window {
                overflow-y: auto !important;
              }
              .chat-interface .asklawly-chat-window::-webkit-scrollbar {
                width: 10px;
              }
              .chat-interface .asklawly-chat-window::-webkit-scrollbar-track {
                background: transparent;
              }
              .chat-interface .asklawly-chat-window::-webkit-scrollbar-thumb {
                background: rgba(107, 114, 128, 0.45);
                border-radius: 999px;
              }
            }

            @media (max-width: 1024px) {
              .chat-interface .asklawly-chat-window::-webkit-scrollbar {
                width: 0 !important;
                height: 0 !important;
                display: none !important;
              }
            }


            .asklawly-task-card:hover {
              transform: translateY(-1px);
              border-color: rgba(30, 42, 120, 0.18) !important;
              box-shadow: 0 16px 34px rgba(2, 6, 23, 0.12) !important;
            }

            .asklawly-task-card:active {
              transform: translateY(0px) scale(0.995);
            }

            .asklawly-task-card:focus-visible {
              outline: 3px solid rgba(37, 99, 235, 0.24);
              outline-offset: 3px;
            }

            @media (max-width: 640px) {
              .asklawly-empty-router {
                padding-left: 10px !important;
                padding-right: 10px !important;
              }

              .asklawly-task-grid {
                grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
                gap: 10px !important;
              }

              .asklawly-task-card {
                min-height: 112px !important;
                padding: 12px 11px !important;
                flex-direction: column !important;
                align-items: flex-start !important;
                justify-content: center !important;
                gap: 9px !important;
              }

              .asklawly-task-card span[aria-hidden="true"] {
                width: 44px !important;
                height: 44px !important;
                font-size: 21px !important;
              }

              .asklawly-task-card .task-mobile-hidden {
                display: none !important;
              }
            }

            @media (max-width: 390px) {
              .asklawly-task-grid {
                gap: 8px !important;
              }

              .asklawly-task-card {
                min-height: 104px !important;
                padding: 11px 9px !important;
              }
            }

            .chat-interface .print-only {
              display: none !important;
            }

            @media print {
              .no-print {
                display: none !important;
              }

              .chat-interface .print-only {
                display: block !important;
              }

              .asklawly-turn-context {
                background: #F8FAFC !important;
                border-color: #D8DEE8 !important;
                box-shadow: none !important;
                break-inside: avoid;
              }

              .asklawly-turn-context-stack {
                break-inside: avoid;
              }

              .asklawly-print-attachment {
                margin-top: 6px !important;
                padding-top: 4px;
                border-top: 1px solid #e5e7eb;
                break-inside: avoid;
              }
            }

            @keyframes chatSpin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    </div>
  );
};

const styles = {
  chatWrapper: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxWidth: "100%",
    flexGrow: 1,
    overflow: "hidden",
    height: "100dvh",
    minHeight: "100vh",
  },
  chatContainer: {
    position: "relative",
    width: "100%",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    minHeight: 0,
  },
  chatWindow: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
  },
  content: {
    width: "100%",
    margin: "0 auto",
    padding: "0",
    boxSizing: "border-box",
  },
  historyNavButton: {
    position: "absolute",
    zIndex: 7,
    width: 38,
    height: 38,
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.34)",
    background: "#1E2A78",
    color: "#FFFFFF",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    fontSize: 22,
    lineHeight: 1,
    cursor: "pointer",
    boxShadow: "0 7px 18px rgba(15, 23, 42, 0.18)",
    WebkitTapHighlightColor: "transparent",
  },
  historyNavButtonDisabled: {
    cursor: "default",
    opacity: 0.62,
  },
  historyNavSpinner: {
    width: 16,
    height: 16,
    borderRadius: 999,
    border: "2px solid rgba(255,255,255,0.42)",
    borderTopColor: "#FFFFFF",
    animation: "chatSpin 0.8s linear infinite",
    boxSizing: "border-box",
  },
  message: {
    padding: "10px 14px",
    borderRadius: "12px",
    maxWidth: "85%",
    fontSize: "16px",
    lineHeight: "1.4",
  },

  userBubble: {
    backgroundColor: "#EAF2FF",
  },
  lexaBubble: {
    backgroundColor: "#F3F4F6",
  },
  warnBubble: {
    backgroundColor: "#FFF1F2",
    maxWidth: "100%",
  },

  iconGroup: { display: "flex", gap: "8px" },
  icon: { cursor: "pointer", fontSize: "16px", color: "#555" },
  messageHeaderFixed: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "6px",
    flexWrap: "wrap",
  },

  followUpWrap: {
    marginTop: 14,
    width: "100%",
  },
  followUpDivider: {
    height: 1,
    width: "100%",
    background: "rgba(148, 163, 184, 0.30)",
    marginBottom: 10,
  },
  followUpLabel: {
    fontSize: 12,
    lineHeight: 1.2,
    fontWeight: 800,
    color: "#64748B",
    letterSpacing: "0.03em",
    textTransform: "uppercase",
    marginBottom: 8,
  },
  followUpList: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  followUpButton: {
    appearance: "none",
    WebkitAppearance: "none",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(148, 163, 184, 0.38)",
    background: "rgba(255, 255, 255, 0.88)",
    color: "#0F172A",
    cursor: "pointer",
    textAlign: "left",
    font: "inherit",
    lineHeight: 1.35,
    transition: "border-color 120ms ease, background 120ms ease, transform 120ms ease",
  },
  followUpButtonDisabled: {
    opacity: 0.55,
    cursor: "not-allowed",
  },
  followUpText: {
    minWidth: 0,
    flex: "1 1 auto",
    fontSize: 14,
    fontWeight: 650,
  },
  followUpChevron: {
    flex: "0 0 auto",
    fontSize: 17,
    color: "#64748B",
    strokeWidth: 2,
  },

  followUpChoiceCard: {
    width: "100%",
    boxSizing: "border-box",
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(148, 163, 184, 0.38)",
    background: "rgba(255, 255, 255, 0.88)",
  },
  followUpChoiceQuestion: {
    fontSize: 14,
    fontWeight: 650,
    lineHeight: 1.35,
    color: "#0F172A",
    marginBottom: 9,
  },
  followUpOptionRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
  },
  followUpOptionButton: {
    appearance: "none",
    WebkitAppearance: "none",
    minHeight: 34,
    padding: "7px 12px",
    borderRadius: 999,
    border: "1px solid rgba(30, 42, 120, 0.28)",
    background: "rgba(238, 242, 255, 0.92)",
    color: "#1E2A78",
    cursor: "pointer",
    font: "inherit",
    fontSize: 13,
    fontWeight: 750,
    lineHeight: 1.2,
  },
  followUpCustomButton: {
    appearance: "none",
    WebkitAppearance: "none",
    width: "100%",
    marginTop: 9,
    padding: "7px 0 0",
    border: "none",
    borderTop: "1px solid rgba(148, 163, 184, 0.22)",
    background: "transparent",
    color: "#475569",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    textAlign: "left",
    font: "inherit",
    fontSize: 12.5,
    fontWeight: 650,
  },

  stickyBar: {
    position: "sticky",
    bottom: 0,
    width: "100%",
    zIndex: 3,
    background: "transparent",
    padding: 0,
    borderTop: "none",
  },

  emptyTopWrap: {
    width: "100%",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    padding: "16px 0 0",
    boxSizing: "border-box",
  },
  emptyCenterInner: {
    width: "100%",
    margin: "0 auto",
    padding: "0 8px",
    textAlign: "center",
    boxSizing: "border-box",
  },
  emptyTitle: {
    fontSize: 21,
    fontWeight: 850,
    color: "#0F172A",
    letterSpacing: "-0.25px",
    marginBottom: 6,
  },
  emptySubtitle: {
    fontSize: 14,
    color: "#64748B",
    lineHeight: 1.45,
    marginBottom: 16,
  },
  taskGrid: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 14,
    textAlign: "left",
  },
  taskCard: {
    appearance: "none",
    WebkitAppearance: "none",
    width: "100%",
    minHeight: 92,
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: 13,
    padding: "14px 16px",
    borderRadius: 18,
    border: "1px solid rgba(15, 23, 42, 0.09)",
    background: "rgba(255, 255, 255, 0.94)",
    boxShadow: "0 12px 28px rgba(2, 6, 23, 0.08)",
    cursor: "pointer",
    transition: "transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease",
    overflow: "hidden",
    textAlign: "left",
  },
  taskCardPrimary: {
    border: "1px solid rgba(37, 99, 235, 0.72)",
    boxShadow: "0 14px 34px rgba(37, 99, 235, 0.14), 0 10px 28px rgba(2, 6, 23, 0.08)",
  },
  taskIconWrap: {
    flex: "0 0 auto",
    width: 52,
    height: 52,
    borderRadius: 18,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 25,
    boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.72)",
  },
  taskTextWrap: {
    minWidth: 0,
    flex: "1 1 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },
  taskTitle: {
    display: "block",
    width: "100%",
    color: "#0F172A",
    fontSize: 16,
    fontWeight: 900,
    lineHeight: 1.2,
  },
  taskChevron: {
    flex: "0 0 auto",
    color: "#64748B",
    fontSize: 20,
    strokeWidth: 2.1,
  },

  taskAccent: {
    blue: { bg: "linear-gradient(135deg, rgba(37, 99, 235, 0.13), rgba(14, 165, 233, 0.10))", color: "#0B5FFF" },
    cyan: { bg: "linear-gradient(135deg, rgba(14, 165, 233, 0.13), rgba(59, 130, 246, 0.09))", color: "#0369A1" },
    indigo: { bg: "linear-gradient(135deg, rgba(99, 102, 241, 0.14), rgba(30, 42, 120, 0.08))", color: "#4F46E5" },
    teal: { bg: "linear-gradient(135deg, rgba(20, 184, 166, 0.14), rgba(14, 165, 233, 0.08))", color: "#0F766E" },
  },
  loginBlock: {
    marginTop: 14,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 10,
  },
  loginText: {
    fontSize: 14,
    fontWeight: 700,
    color: "#111827",
  },
  loginBtn: {
    background: "#0b5fff",
    color: "#fff",
    border: "none",
    borderRadius: 12,
    padding: "10px 14px",
    fontWeight: 800,
    cursor: "pointer",
  },

  dots: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 999,
    background: "#6B7280",
    opacity: 0.6,
    animation: "asklawlyDotPulse 1.2s infinite ease-in-out",
  },

  billingDialogOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 100000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    boxSizing: "border-box",
    background: "rgba(15, 23, 42, 0.48)",
    backdropFilter: "blur(3px)",
  },
  billingDialogCard: {
    width: "min(460px, 100%)",
    maxHeight: "min(82dvh, 680px)",
    overflowY: "auto",
    borderRadius: 20,
    border: "1px solid rgba(148, 163, 184, 0.34)",
    background: "#FFFFFF",
    boxShadow: "0 26px 80px rgba(15, 23, 42, 0.30)",
    padding: "28px 26px 24px",
    color: "#0F172A",
    textAlign: "center",
  },
  billingDialogIcon: {
    width: 48,
    height: 48,
    margin: "0 auto 14px",
    borderRadius: 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 23,
    color: "#1E2A78",
    background: "rgba(37, 99, 235, 0.10)",
  },
  billingDialogTitle: {
    fontSize: 20,
    lineHeight: 1.25,
    fontWeight: 850,
    letterSpacing: "-0.2px",
    color: "#0F172A",
  },
  billingDialogMessage: {
    marginTop: 10,
    fontSize: 15,
    lineHeight: 1.6,
    color: "#475569",
    whiteSpace: "pre-wrap",
  },
  billingDialogActions: {
    display: "flex",
    justifyContent: "center",
    marginTop: 22,
  },
  billingDialogPrimaryButton: {
    appearance: "none",
    WebkitAppearance: "none",
    minWidth: 108,
    minHeight: 42,
    padding: "10px 20px",
    border: 0,
    borderRadius: 12,
    background: "#1E2A78",
    color: "#FFFFFF",
    cursor: "pointer",
    font: "inherit",
    fontSize: 14,
    fontWeight: 800,
  },

  toastWrap: {
    position: "fixed",
    right: 14,
    bottom: 14,
    zIndex: 99999,
    width: "min(420px, calc(100vw - 28px))",
    pointerEvents: "none", // ✅ non-blocking for the app
  },
  toastCard: {
    pointerEvents: "auto",
    borderRadius: 14,
    border: "1px solid rgba(148,163,184,0.45)",
    background: "rgba(255,255,255,0.94)",
    boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
    padding: "12px 12px 10px",
    color: "#0f172a",
    backdropFilter: "blur(10px)",
  },
  toastTopRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },
  toastTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "#0f172a",
  },
  toastCloseBtn: {
    width: 30,
    height: 30,
    borderRadius: 10,
    border: "1px solid rgba(148,163,184,0.45)",
    background: "rgba(255,255,255,0.9)",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#334155",
  },
  toastMessage: {
    marginTop: 8,
    fontSize: 13,
    lineHeight: 1.35,
    color: "#111827",
    whiteSpace: "pre-wrap",
  },
  toastBottomRow: {
    marginTop: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },
  toastSpinnerRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  toastHint: {
    fontSize: 12,
    color: "#64748b",
  },
  spinner: {
    width: 16,
    height: 16,
    borderRadius: 999,
    border: "2px solid rgba(100,116,139,0.35)",
    borderTopColor: "rgba(100,116,139,0.95)",
    animation: "chatSpin 0.9s linear infinite",
  },
};

export default ChatInterface;
